import React, { useState, useEffect, useRef, startTransition } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useSpring, useTransform, useMotionTemplate, AnimatePresence, useScroll, useMotionValueEvent, useInView, type MotionValue } from 'motion/react';
import { ArrowRight, ArrowDown, ShieldCheck, MessageSquare, Sliders, Sparkles, Mic2, Zap, Brain, Volume2, Music, Heart, Languages, Smile, Play, Pause, X, Check, FileText, Speaker, Database, Users, Layers, Tag, ChevronDown, ChevronLeft, ChevronRight, Filter, Menu } from 'lucide-react';
const DiagnosticModal = React.lazy(() => import('../components/DiagnosticModal'));
import QuickConnectForm from '../components/QuickConnectForm';
import ContactScoping from '../components/ContactScoping';
import Navbar from '../components/Navbar';
import { ProjectBriefOverlay, TalentFormOverlay, StudioFormOverlay, DatasetEnquiryOverlay, CastingRequirementsOverlay, LiveSessionOverlay } from '../components/ProjectBriefDrawer';
import { usePageMeta } from '../hooks/usePageMeta';

// Map of section IDs that differ between mobile and desktop
const responsiveIds: Record<string, string> = {
  'datasets': 'datasets-dt',
  'why-india': 'why-india-mobile',
};

// Scroll helper — manually scrolls #main-scroll-container to a target element by ID
const scrollToId = (id: string) => {
  const isMobile = window.innerWidth < 768;
  // Resolve responsive IDs: mobile uses default, desktop uses mapped alt (and vice versa)
  let resolvedId = id;
  if (isMobile && id === 'why-india') resolvedId = 'why-india-mobile';
  if (!isMobile && id === 'datasets') resolvedId = 'datasets-dt';
  if (!isMobile && id === 'why-india-mobile') resolvedId = 'why-india';
  if (isMobile && id === 'datasets-dt') resolvedId = 'datasets';
  const container = document.getElementById('main-scroll-container');
  let target = document.getElementById(resolvedId);
  // Fall back to the original id if the remapped target doesn't exist on this
  // page — e.g. /about has only one #why-india section, no #why-india-mobile.
  if (!target && resolvedId !== id) {
    target = document.getElementById(id);
  }
  if (!container || !target) return;
  let top = 0;
  let el: HTMLElement | null = target;
  while (el && el !== container) {
    top += el.offsetTop;
    el = el.offsetParent as HTMLElement | null;
  }
  container.scrollTo({ top, behavior: 'smooth' });
};

const usps = [
  {
    id: 0,
    title: "Real-World Code-Switching",
    subtitle: "Complex & Natural",
    description: "Standard datasets teach your model textbook Hindi. We teach it how Indians actually talk \u2014 capturing natural, complex code-switched speech across 2\u20133 languages in a single breath.",
    icon: <MessageSquare className="w-6 h-6 text-white" />,
    image: "/code-switching.webp",
    bgPosition: "85% center",
    cta: "Know More",
    tagline: "How India Actually Talks.",
    targetId: "usp1"
  },
  {
    id: 1,
    title: "Voqals\nQuality\nStandard",
    subtitle: "Studio Certified",
    description: "Your model shouldn\u2019t learn from electrical hum. Our proprietary studio certification guarantees \u221260dB noise floor, under 200ms RT60, and zero electrical interference.",
    icon: <Sliders className="w-6 h-6 text-white" />,
    image: "/voqals-quality.webp",
    bgPosition: "80% center",
    cta: "Know More",
    tagline: "Train On Speech. Not Noise.",
    targetId: "usp2"
  },
  {
    id: 2,
    title: "AI That Sounds Human",
    subtitle: "Directed Performance",
    description: "Flat recordings produce flat AI. Every Voqals session is directed by experienced audio professionals who extract real emotion, genuine intent, and naturalistic delivery from every talent.",
    icon: <Sparkles className="w-6 h-6 text-white" />,
    image: "/directed-performance.webp",
    cta: "Know More",
    tagline: "Every emotion. Directed. Delivered.",
    targetId: "usp3"
  }
];
const RotatingUSPCards = () => {
  const [active, setActive] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    if (isHovered) return;
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % usps.length);
    }, 5000); // Sped up for more dynamism
    return () => clearInterval(interval);
  }, [isHovered]);
  return (
    <div 
      className="relative w-full max-w-[var(--card-max-w)] h-[var(--card-height)] mx-auto perspective-1000"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <AnimatePresence mode="popLayout">
        {usps.map((usp, index) => {
          const offset = (index - active + usps.length) % usps.length;
          const isTop = offset === 0;
          
          return (
            <motion.div
              key={usp.id}
              className="absolute inset-x-0 top-0 h-[var(--card-inner-h)] rounded-[clamp(16px,1.4vw,24px)] p-[var(--card-padding)] flex flex-col border border-white/[0.08] bg-[#0a0a0a] cursor-pointer overflow-hidden group shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
              animate={{
                y: offset * 16,
                scale: 1 - offset * 0.04,
                opacity: isTop ? 1 : Math.max(0, 1 - offset * 0.3),
                zIndex: usps.length - offset,
                rotateX: offset * 2,
              }}
              whileHover={isTop ? { scale: 1.015, y: -4, rotateX: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => {
                if (isTop) {
                  scrollToId(usp.targetId);
                } else {
                  setActive(index);
                }
              }}
            >
              {/* Background image — pushed to the right */}
              <motion.div
                className="absolute inset-0 bg-cover"
                style={{ backgroundImage: `url(${usp.image})`, backgroundPosition: usp.bgPosition || 'center' }}
                animate={{ scale: isTop && isHovered ? 1.06 : 1 }}
                transition={{ duration: 1.5, ease: "easeOut" }}
              />
              {/* Left glass overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#0a0e1a]/95 via-[#0a0e1a]/60 to-transparent" />
              <div className="absolute inset-y-0 left-0 w-[60%] backdrop-blur-md" style={{ maskImage: 'linear-gradient(to right, black 40%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to right, black 40%, transparent 100%)' }} />
              <div className="absolute inset-0 bg-gradient-to-r from-[#56CFFC]/[0.04] via-transparent to-transparent" />

              {/* Content — left aligned, spread top to bottom */}
              <div className="relative z-10 w-[55%] flex flex-col justify-between h-full">
                <p className="text-[clamp(10px,0.7vw,14px)] font-semibold uppercase tracking-[0.2em] text-[#56CFFC]">{usp.subtitle}</p>
                <div>
                  <p className="font-display font-black text-[clamp(24px,2.2vw,48px)] text-white leading-[1.1] tracking-tight mb-[clamp(4px,0.3vw,8px)] whitespace-pre-line">{usp.title}</p>
                  <p className="text-[clamp(12px,0.85vw,17px)] text-white/70 font-normal">{usp.tagline}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); scrollToId(usp.targetId); }}
                  className="flex items-center gap-[clamp(6px,0.5vw,12px)] px-[clamp(14px,1.1vw,24px)] py-[clamp(8px,0.5vw,14px)] rounded-full border border-white/20 bg-white/5 backdrop-blur-sm text-[clamp(9px,0.65vw,14px)] font-bold uppercase tracking-[0.1em] text-white/90 hover:bg-white/10 hover:border-white/30 transition-all group/btn w-fit"
                >
                  {usp.cta} <ArrowRight className="w-[clamp(12px,0.8vw,18px)] h-[clamp(12px,0.8vw,18px)] transform group-hover/btn:translate-x-1 transition-transform" />
                </button>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
      
      {/* Navigation Arrows + Pagination Dots */}
      <div className="absolute -bottom-2 left-0 right-0 flex justify-center items-center gap-[clamp(8px,0.8vw,16px)] z-50">
        <button
          onClick={() => setActive((prev) => (prev - 1 + usps.length) % usps.length)}
          className="w-[clamp(24px,1.6vw,32px)] h-[clamp(24px,1.6vw,32px)] rounded-full border border-white/20 bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-all"
          aria-label="Previous card"
        >
          <ChevronLeft className="w-[clamp(12px,0.8vw,16px)] h-[clamp(12px,0.8vw,16px)]" />
        </button>
        <div className="flex items-center gap-1">
          {usps.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setActive(idx)}
              className="p-[9px] group"
              aria-label={`Go to slide ${idx + 1}`}
            >
              <span className={`block h-1.5 rounded-full transition-all duration-300 ${active === idx ? 'w-8 bg-[#56CFFC] shadow-[0_0_10px_rgba(86,207,252,0.5)]' : 'w-2 bg-[#EEEEEE]/30 group-hover:bg-[#EEEEEE]/50'}`} />
            </button>
          ))}
        </div>
        <button
          onClick={() => setActive((prev) => (prev + 1) % usps.length)}
          className="w-[clamp(24px,1.6vw,32px)] h-[clamp(24px,1.6vw,32px)] rounded-full border border-white/20 bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-all"
          aria-label="Next card"
        >
          <ChevronRight className="w-[clamp(12px,0.8vw,16px)] h-[clamp(12px,0.8vw,16px)]" />
        </button>
      </div>
    </div>
  );
};
/* ── USP1: Full Expanded Widget ── */
interface Usp1WidgetProps {
  datasetType: 'standard' | 'voqals';
  setDatasetType: (v: 'standard' | 'voqals') => void;
  isPlaying: boolean;
  setIsPlaying: (v: boolean) => void;
  playbackProgress: number;
  setPlaybackProgress: (v: number) => void;
  detailsOpacity?: any; // MotionValue<number> from useTransform — controls transcript/metadata visibility on mobile
  detailsBlur?: any; // MotionValue<string> — CSS filter blur for transcript teaser
  isExpanded?: boolean; // true when widget is in fullscreen state on mobile
  visible?: boolean; // true once section scrolls into view — gates audio loading
}
// Downsample a channel's Float32Array into `barCount` peak values (0–100)
function downsampleChannel(channelData: Float32Array, barCount: number): number[] {
  const blockSize = Math.floor(channelData.length / barCount);
  const peaks: number[] = [];
  for (let i = 0; i < barCount; i++) {
    let max = 0;
    const start = i * blockSize;
    for (let j = start; j < start + blockSize && j < channelData.length; j++) {
      const abs = Math.abs(channelData[j]);
      if (abs > max) max = abs;
    }
    peaks.push(max * 100);
  }
  // Normalize so the loudest bar is 95%
  const maxPeak = Math.max(...peaks, 0.01);
  return peaks.map(p => Math.max(3, (p / maxPeak) * 95));
}

const Usp1ExpandedWidget = ({ datasetType, setDatasetType, isPlaying, setIsPlaying, playbackProgress, setPlaybackProgress, detailsOpacity, detailsBlur, isExpanded, visible = false }: Usp1WidgetProps) => {
  const voqalsAudioRef = useRef<HTMLAudioElement | null>(null);
  const standardAudioRef = useRef<HTMLAudioElement | null>(null);
  const rafRef = useRef<number>(0);
  const [voqalsDuration, setVoqalsDuration] = useState(0);
  const [standardDuration, setStandardDuration] = useState(0);
  const toggleContainerRef = useRef<HTMLDivElement>(null);
  const [toggleIndicator, setToggleIndicator] = useState({ left: 0, width: 0 });
  const [voqalsLeftWaveform, setVoqalsLeftWaveform] = useState<number[]>([]);
  const [voqalsRightWaveform, setVoqalsRightWaveform] = useState<number[]>([]);
  const [standardLeftWaveform, setStandardLeftWaveform] = useState<number[]>([]);
  const [standardRightWaveform, setStandardRightWaveform] = useState<number[]>([]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const barCount = isMobile ? 100 : 200;

  const duration = datasetType === 'voqals' ? voqalsDuration : standardDuration;

  // Load Voqals audio + decode stereo waveform (deferred until section visible)
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    const audioFile = '/Audio/USP1.wav';
    const audio = new Audio(audioFile);
    voqalsAudioRef.current = audio;
    audio.addEventListener('loadedmetadata', () => setVoqalsDuration(audio.duration));
    audio.addEventListener('ended', () => { setIsPlaying(false); setPlaybackProgress(0); });
    fetch(audioFile)
      .then(r => r.arrayBuffer())
      .then(buf => new AudioContext().decodeAudioData(buf))
      .then(decoded => {
        if (cancelled) return;
        const left = decoded.getChannelData(0);
        const right = decoded.numberOfChannels > 1 ? decoded.getChannelData(1) : left;
        setVoqalsLeftWaveform(downsampleChannel(left, barCount));
        setVoqalsRightWaveform(downsampleChannel(right, barCount));
      })
      .catch(() => {});
    return () => { cancelled = true; audio.pause(); audio.src = ''; };
  }, [barCount, visible]);

  // Load Standard audio + decode waveform (deferred until section visible)
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    const audioFile = '/Audio/UPS1standard.mp3';
    const audio = new Audio(audioFile);
    standardAudioRef.current = audio;
    audio.addEventListener('loadedmetadata', () => setStandardDuration(audio.duration));
    audio.addEventListener('ended', () => { setIsPlaying(false); setPlaybackProgress(0); });
    fetch(audioFile)
      .then(r => r.arrayBuffer())
      .then(buf => new AudioContext().decodeAudioData(buf))
      .then(decoded => {
        if (cancelled) return;
        const left = decoded.getChannelData(0);
        const right = decoded.numberOfChannels > 1 ? decoded.getChannelData(1) : left;
        setStandardLeftWaveform(downsampleChannel(left, barCount));
        setStandardRightWaveform(downsampleChannel(right, barCount));
      })
      .catch(() => {});
    return () => { cancelled = true; audio.pause(); audio.src = ''; };
  }, [barCount, visible]);

  useEffect(() => {
    const container = toggleContainerRef.current;
    if (!container) return;
    const measure = () => {
      const buttons = container.querySelectorAll('button');
      const idx = datasetType === 'standard' ? 0 : 1;
      const btn = buttons[idx];
      if (btn) {
        setToggleIndicator({ left: (btn as HTMLElement).offsetLeft, width: (btn as HTMLElement).offsetWidth });
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(measure));
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [datasetType]);

  // Real audio playback — plays the correct audio per tab
  useEffect(() => {
    const voqalsAudio = voqalsAudioRef.current;
    const standardAudio = standardAudioRef.current;
    if (isPlaying) {
      const activeAudio = datasetType === 'voqals' ? voqalsAudio : standardAudio;
      const inactiveAudio = datasetType === 'voqals' ? standardAudio : voqalsAudio;
      if (inactiveAudio) { inactiveAudio.pause(); }
      if (activeAudio) {
        activeAudio.play().catch(() => {});
        const tick = () => {
          if (activeAudio.duration) setPlaybackProgress(activeAudio.currentTime / activeAudio.duration);
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      }
    } else {
      voqalsAudio?.pause();
      standardAudio?.pause();
      cancelAnimationFrame(rafRef.current);
    }
    return () => cancelAnimationFrame(rafRef.current);
  }, [isPlaying, datasetType]);

  const togglePlay = () => {
    const audio = datasetType === 'voqals' ? voqalsAudioRef.current : standardAudioRef.current;
    if (audio && playbackProgress >= 1) { audio.currentTime = 0; setPlaybackProgress(0); }
    setIsPlaying(!isPlaying);
  };

  // Use real waveform data per tab
  const userWaveform = datasetType === 'voqals'
    ? (voqalsLeftWaveform.length > 0 ? voqalsLeftWaveform : Array.from({ length: barCount }, () => 15))
    : (standardLeftWaveform.length > 0 ? standardLeftWaveform : Array.from({ length: barCount }, () => 15));
  const agentWaveform = datasetType === 'voqals'
    ? (voqalsRightWaveform.length > 0 ? voqalsRightWaveform : Array.from({ length: barCount }, () => 15))
    : (standardRightWaveform.length > 0 ? standardRightWaveform : Array.from({ length: barCount }, () => 15));
  return (
    <div className={`gradient-border rounded-2xl md:rounded-[clamp(20px,1.85vw,32px)] backdrop-blur-xl shadow-2xl relative h-full md:h-full`}
      style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' }}
    >
      <div className="px-6 py-[3dvh] md:p-[clamp(20px,1.85vw,32px)] flex flex-col h-full overflow-hidden rounded-[inherit]">
      {/* Top highlight */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      {/* Toggle Switch */}
      <div className={`flex justify-center mb-5 md:mb-[clamp(16px,1.39vw,24px)]`}>
        <div ref={toggleContainerRef} className="bg-white/5 p-1 rounded-full flex items-center relative">
          {/* Sliding indicator */}
          <motion.div
            className="absolute top-1 bottom-1 rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] shadow-[0_0_20px_rgba(27,54,255,0.3)]"
            animate={{ left: toggleIndicator.left, width: toggleIndicator.width }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          />
          <button
            onClick={() => { setDatasetType('standard'); setIsPlaying(false); setPlaybackProgress(0); }}
            className={`relative z-10 px-4 md:px-[clamp(16px,1.39vw,24px)] py-2.5 md:py-[clamp(7px,0.58vw,10px)] rounded-full text-[11px] md:text-[clamp(8px,0.64vw,11px)] font-bold tracking-[0.15em] md:tracking-[0.2em] uppercase transition-colors duration-300 whitespace-nowrap ${
              datasetType === 'standard' ? 'text-white' : 'text-white/40 hover:text-white/60'
            }`}
          >Standard Datasets</button>
          <button
            onClick={() => { setDatasetType('voqals'); setIsPlaying(false); setPlaybackProgress(0); }}
            className={`relative z-10 px-4 md:px-[clamp(16px,1.39vw,24px)] py-2.5 md:py-[clamp(7px,0.58vw,10px)] rounded-full text-[11px] md:text-[clamp(8px,0.64vw,11px)] font-bold tracking-[0.15em] md:tracking-[0.2em] uppercase transition-colors duration-300 whitespace-nowrap ${
              datasetType === 'voqals' ? 'text-white' : 'text-white/40 hover:text-white/60'
            }`}
          >Voqals</button>
        </div>
      </div>
      {/* Audio Section */}
      <div className={`mb-5 md:mb-[clamp(8px,0.93vw,16px)] flex flex-col gap-2 md:gap-[clamp(8px,0.93vw,16px)]`}>
        <div className="flex items-center justify-between mb-0.5 md:mb-[clamp(2px,0.23vw,4px)]">
          <span className={`text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-bold tracking-widest uppercase text-white/40 truncate mr-2`}>
            {datasetType === 'standard' ? 'Customer Support · Mono · Hindi' : 'Voq_CS_Dual_CodeSwitch_Intent_Empathy'}
          </span>
          <div className={`text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-mono text-white/30 shrink-0`}>
            {(() => { const cur = Math.floor(playbackProgress * duration); const tot = Math.floor(duration); const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`; return `${fmt(cur)} / ${fmt(tot)}`; })()}
          </div>
        </div>
        <div className={`flex items-stretch gap-4 p-3 md:gap-[clamp(16px,1.39vw,24px)] md:p-[clamp(8px,0.69vw,12px)] rounded-xl bg-white/[0.03] border border-white/[0.06] shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]`}>
          <div className="flex-1 flex flex-col gap-1.5 md:gap-[clamp(4px,0.35vw,6px)] justify-center w-full min-w-0">
            {datasetType === 'standard' ? (
              /* Standard: single mono waveform */
              <div
                className="flex-1 h-10 md:h-[clamp(20px,1.62vw,28px)] flex items-center gap-0 overflow-hidden relative min-w-0 cursor-pointer"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const pos = (e.clientX - rect.left) / rect.width;
                  const audio = standardAudioRef.current;
                  if (audio && audio.duration) { audio.currentTime = pos * audio.duration; setPlaybackProgress(pos); }
                }}
              >
                {userWaveform.map((height, i) => {
                  const barPos = i / userWaveform.length;
                  const isPassed = playbackProgress > barPos;
                  // Standard Hindi + English segments (from annotation JSON, duration 5.232s)
                  const stdSegments = [
                    { start: 0, end: 0.3270, color: '#A78BFA' },     // Mujhe khed hai. Aapka — Hindi
                    { start: 0.3270, end: 0.4539, color: '#56CFFC' }, // transaction fail — English
                    { start: 0.4539, end: 1.0, color: '#A78BFA' },   // ho gaya hai. Kripya... — Hindi
                  ];
                  const seg = stdSegments.find(s => barPos >= s.start && barPos < s.end);
                  const color = seg ? seg.color : '#888888';
                  return <div key={i} className="rounded-full transition-colors duration-75" style={{ flex: '1 1 0', minWidth: 0, height: `${height}%`, backgroundColor: isPassed ? color : `${color}33`, opacity: isPassed ? 0.8 : 0.25 }} />;
                })}
              </div>
            ) : (
              /* Voqals: dual stereo waveform */
              <>
                <div className={`flex items-center gap-3 md:gap-[clamp(8px,0.93vw,16px)]`}>
                  <span className={`text-[10px] w-10 md:text-[clamp(6px,0.52vw,9px)] font-bold tracking-widest uppercase text-[#56CFFC]/50 md:w-[clamp(28px,2.31vw,40px)] text-right shrink-0`}>User</span>
                  <div
                    className={`flex-1 h-8 md:h-[clamp(14px,1.16vw,20px)] flex items-center gap-0 overflow-hidden relative min-w-0 cursor-pointer`}
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const pos = (e.clientX - rect.left) / rect.width;
                      const audio = voqalsAudioRef.current;
                      if (audio && audio.duration) { audio.currentTime = pos * audio.duration; setPlaybackProgress(pos); }
                    }}
                  >
                    {userWaveform.map((height, i) => {
                      const barPos = i / userWaveform.length;
                      const isPassed = playbackProgress > barPos;
                      const userSegments = [
                        { start: 0.0207, end: 0.0432, color: '#F59E0B' }, // Oh dada — Marathi
                        { start: 0.0432, end: 0.0575, color: '#A78BFA' }, // mera — Hindi
                        { start: 0.0575, end: 0.0877, color: '#56CFFC' }, // payment decline — English
                        { start: 0.0877, end: 0.1176, color: '#A78BFA' }, // ho gaya hai — Hindi
                        { start: 0.1176, end: 0.1755, color: '#F59E0B' }, // Ata mi kay karun? — Marathi
                        { start: 0.1755, end: 0.1823, color: '#A78BFA' }, // Mere — Hindi
                        { start: 0.1823, end: 0.1979, color: '#56CFFC' }, // account — English
                        { start: 0.1979, end: 0.2227, color: '#A78BFA' }, // se bhi paise — Hindi
                        { start: 0.2227, end: 0.2366, color: '#56CFFC' }, // deduct — English
                        { start: 0.2366, end: 0.2729, color: '#A78BFA' }, // ho gaye hain — Hindi
                        { start: 0.2729, end: 0.3117, color: '#56CFFC' }, // Please urgently check — English
                        { start: 0.3117, end: 0.3438, color: '#A78BFA' }, // karo na — Hindi
                        { start: 0.3438, end: 0.3790, color: '#56CFFC' }, // Madam — English
                        { start: 0.3790, end: 0.5072, color: '#F59E0B' }, // tumi kahi kalji karu naka. Mala dista — Marathi
                        { start: 0.5072, end: 0.5928, color: '#56CFFC' }, // that the payment has been decline — English
                        { start: 0.5928, end: 0.6814, color: '#F59E0B' }, // ani paise pan deduct zhalet — Marathi
                        { start: 0.6814, end: 0.7210, color: '#56CFFC' }, // But don't worry — English
                        { start: 0.7210, end: 0.7449, color: '#A78BFA' }, // yeh — Hindi
                        { start: 0.7449, end: 0.7706, color: '#56CFFC' }, // 24 hours — English
                        { start: 0.7706, end: 0.7772, color: '#A78BFA' }, // mein — Hindi
                        { start: 0.7772, end: 0.7916, color: '#56CFFC' }, // reverse — English
                        { start: 0.7916, end: 0.8951, color: '#A78BFA' }, // ho jaega. Main aapke liye ek — Hindi
                        { start: 0.8951, end: 0.9257, color: '#56CFFC' }, // urgent ticket raise — English
                        { start: 0.9257, end: 0.9545, color: '#A78BFA' }, // kar deta hoon — Hindi
                        { start: 0.9545, end: 0.9953, color: '#56CFFC' }, // Is that okay? — English
                      ];
                      const seg = userSegments.find(s => barPos >= s.start && barPos < s.end);
                      const color = seg ? seg.color : '#888888';
                      const isSilence = !seg;
                      return <div key={i} className="rounded-full transition-colors duration-75" style={{ flex: '1 1 0', minWidth: 0, height: isSilence ? '15%' : `${height}%`, backgroundColor: isPassed ? color : `${color}33`, opacity: isSilence ? 0.1 : (isPassed ? 1 : 0.3) }} />;
                    })}
                  </div>
                </div>
                <div className={`flex items-center gap-3 md:gap-[clamp(8px,0.93vw,16px)]`}>
                  <span className={`text-[10px] w-10 md:text-[clamp(6px,0.52vw,9px)] font-bold tracking-widest uppercase text-white/50 md:w-[clamp(28px,2.31vw,40px)] text-right shrink-0`}>Agent</span>
                  <div
                    className={`flex-1 h-8 md:h-[clamp(14px,1.16vw,20px)] flex items-center gap-0 overflow-hidden relative min-w-0 cursor-pointer`}
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const pos = (e.clientX - rect.left) / rect.width;
                      const audio = voqalsAudioRef.current;
                      if (audio && audio.duration) { audio.currentTime = pos * audio.duration; setPlaybackProgress(pos); }
                    }}
                  >
                    {agentWaveform.map((height, i) => {
                      const barPos = i / agentWaveform.length;
                      const isPassed = playbackProgress > barPos;
                      const agentSegments = [
                        { start: 0.0207, end: 0.0432, color: '#F59E0B' }, // Oh dada — Marathi
                        { start: 0.0432, end: 0.0575, color: '#A78BFA' }, // mera — Hindi
                        { start: 0.0575, end: 0.0877, color: '#56CFFC' }, // payment decline — English
                        { start: 0.0877, end: 0.1176, color: '#A78BFA' }, // ho gaya hai — Hindi
                        { start: 0.1176, end: 0.1755, color: '#F59E0B' }, // Ata mi kay karun? — Marathi
                        { start: 0.1755, end: 0.1823, color: '#A78BFA' }, // Mere — Hindi
                        { start: 0.1823, end: 0.1979, color: '#56CFFC' }, // account — English
                        { start: 0.1979, end: 0.2227, color: '#A78BFA' }, // se bhi paise — Hindi
                        { start: 0.2227, end: 0.2366, color: '#56CFFC' }, // deduct — English
                        { start: 0.2366, end: 0.2729, color: '#A78BFA' }, // ho gaye hain — Hindi
                        { start: 0.2729, end: 0.3117, color: '#56CFFC' }, // Please urgently check — English
                        { start: 0.3117, end: 0.3438, color: '#A78BFA' }, // karo na — Hindi
                        { start: 0.3438, end: 0.3790, color: '#56CFFC' }, // Madam — English
                        { start: 0.3790, end: 0.5072, color: '#F59E0B' }, // tumi kahi kalji karu naka. Mala dista — Marathi
                        { start: 0.5072, end: 0.5928, color: '#56CFFC' }, // that the payment has been decline — English
                        { start: 0.5928, end: 0.6814, color: '#F59E0B' }, // ani paise pan deduct zhalet — Marathi
                        { start: 0.6814, end: 0.7210, color: '#56CFFC' }, // But don't worry — English
                        { start: 0.7210, end: 0.7449, color: '#A78BFA' }, // yeh — Hindi
                        { start: 0.7449, end: 0.7706, color: '#56CFFC' }, // 24 hours — English
                        { start: 0.7706, end: 0.7772, color: '#A78BFA' }, // mein — Hindi
                        { start: 0.7772, end: 0.7916, color: '#56CFFC' }, // reverse — English
                        { start: 0.7916, end: 0.8951, color: '#A78BFA' }, // ho jaega. Main aapke liye ek — Hindi
                        { start: 0.8951, end: 0.9257, color: '#56CFFC' }, // urgent ticket raise — English
                        { start: 0.9257, end: 0.9545, color: '#A78BFA' }, // kar deta hoon — Hindi
                        { start: 0.9545, end: 0.9953, color: '#56CFFC' }, // Is that okay? — English
                      ];
                      const seg = agentSegments.find(s => barPos >= s.start && barPos < s.end);
                      const color = seg ? seg.color : '#888888';
                      const isSilence = !seg;
                      return <div key={i} className="rounded-full transition-colors duration-75" style={{ flex: '1 1 0', minWidth: 0, height: isSilence ? '15%' : `${height}%`, backgroundColor: isPassed ? color : `${color}33`, opacity: isSilence ? 0.1 : (isPassed ? 1 : 0.3) }} />;
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
          <button aria-label={isPlaying ? "Pause audio" : "Play audio"} onClick={togglePlay} className={`w-14 aspect-square md:w-[clamp(32px,2.78vw,48px)] shrink-0 rounded-full flex items-center justify-center transition-all ${datasetType === 'voqals' ? 'bg-gradient-to-br from-[#56CFFC] to-[#1B36FF] hover:shadow-[0_0_16px_rgba(86,207,252,0.3)]' : 'bg-white/5 border border-white/10 hover:bg-white/10'}`}>
            {isPlaying ? <Pause className={`w-5 h-5 md:w-[clamp(14px,1.16vw,20px)] md:h-[clamp(14px,1.16vw,20px)] text-white fill-white`} /> : <Play className={`w-5 h-5 md:w-[clamp(14px,1.16vw,20px)] md:h-[clamp(14px,1.16vw,20px)] text-white fill-white ml-0.5 md:ml-1`} />}
          </button>
        </div>
      </div>
      <div className={`h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent mb-5 md:mb-[clamp(8px,0.93vw,16px)]`} />
      {/* Transcript + Metadata — on mobile, opacity driven by scroll progress */}
      <motion.div style={detailsOpacity != null ? { opacity: detailsOpacity, filter: detailsBlur } : undefined} className="flex flex-col">
      {/* Transcript */}
      <div className={`mb-4 md:mb-[clamp(8px,0.93vw,16px)] md:min-h-[clamp(80px,6.94vw,120px)]`}>
        <div className={`flex items-center justify-center gap-4 mb-3 text-[9px] md:gap-[clamp(8px,0.93vw,16px)] md:mb-[clamp(8px,0.93vw,16px)] md:text-[clamp(6px,0.52vw,9px)] font-bold uppercase tracking-widest`}>
          {datasetType === 'standard' ? (
            <>
              <span className="text-[#A78BFA] flex items-center gap-1.5 md:gap-[clamp(4px,0.35vw,8px)]"><div className={`w-2 h-2 md:w-[clamp(5px,0.46vw,8px)] md:h-[clamp(5px,0.46vw,8px)] rounded-full bg-[#A78BFA]`} /> Hindi</span>
              <span className="text-[#56CFFC] flex items-center gap-1.5 md:gap-[clamp(4px,0.35vw,8px)]"><div className={`w-2 h-2 md:w-[clamp(5px,0.46vw,8px)] md:h-[clamp(5px,0.46vw,8px)] rounded-full bg-[#56CFFC]`} /> English</span>
            </>
          ) : (
            <>
              <span className="text-[#F59E0B] flex items-center gap-1.5 md:gap-[clamp(4px,0.35vw,8px)]"><div className={`w-2 h-2 md:w-[clamp(5px,0.46vw,8px)] md:h-[clamp(5px,0.46vw,8px)] rounded-full bg-[#F59E0B]`} /> Marathi</span>
              <span className="text-[#A78BFA] flex items-center gap-1.5 md:gap-[clamp(4px,0.35vw,8px)]"><div className={`w-2 h-2 md:w-[clamp(5px,0.46vw,8px)] md:h-[clamp(5px,0.46vw,8px)] rounded-full bg-[#A78BFA]`} /> Hindi</span>
              <span className="text-[#56CFFC] flex items-center gap-1.5 md:gap-[clamp(4px,0.35vw,8px)]"><div className={`w-2 h-2 md:w-[clamp(5px,0.46vw,8px)] md:h-[clamp(5px,0.46vw,8px)] rounded-full bg-[#56CFFC]`} /> English</span>
            </>
          )}
        </div>
        <div className={`flex flex-col gap-3 md:gap-[clamp(8px,0.93vw,16px)]`}>
          <div className={`flex gap-3 md:gap-[clamp(16px,1.39vw,24px)]`}>
            <div className={`w-10 md:w-[clamp(28px,2.31vw,40px)] shrink-0 flex flex-col items-end`}>
              <span className={`text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-bold tracking-widest uppercase text-white/30 mt-0.5 md:mt-[clamp(2px,0.23vw,4px)]`}>User</span>
            </div>
            <div className={`flex-1 text-[14px] leading-relaxed md:text-[clamp(11px,0.93vw,16px)] md:leading-relaxed font-medium`}>
              {datasetType === 'standard' ? (
                <span className="text-white/40">—</span>
              ) : (
                <span className="opacity-90">
                  <span className="text-[#F59E0B]">Oh dada</span>, <span className="text-[#A78BFA]">mera</span> <span className="text-[#56CFFC]">payment decline</span> <span className="text-[#A78BFA]">ho gaya hai.</span> <span className="text-[#F59E0B]">Ata mi kay karun?</span> <span className="text-[#A78BFA]">Mere</span> <span className="text-[#56CFFC]">account</span> <span className="text-[#A78BFA]">se bhi paise</span> <span className="text-[#56CFFC]">deduct</span> <span className="text-[#A78BFA]">ho gaye hain.</span> <span className="text-[#56CFFC]">Please urgently check</span> <span className="text-[#A78BFA]">karo na.</span>
                </span>
              )}
            </div>
          </div>
          <div className={`flex gap-3 md:gap-[clamp(16px,1.39vw,24px)]`}>
            <div className={`w-10 md:w-[clamp(28px,2.31vw,40px)] shrink-0 flex flex-col items-end`}>
              <span className={`text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-bold tracking-widest uppercase text-white/30 mt-0.5 md:mt-[clamp(2px,0.23vw,4px)]`}>Agent</span>
            </div>
            <div className={`flex-1 text-[14px] leading-relaxed md:text-[clamp(11px,0.93vw,16px)] md:leading-relaxed font-medium`}>
              {datasetType === 'standard' ? (
                <span className="text-white/60"><span className="text-[#A78BFA]/60">Mujhe khed hai. Aapka</span> <span className="text-[#56CFFC]/60">transaction fail</span> <span className="text-[#A78BFA]/60">ho gaya hai. Kripya chaubis ghante pratiksha karein.</span></span>
              ) : (
                <span className="opacity-90">
                  <span className="text-[#56CFFC]">Madam,</span> <span className="text-[#F59E0B]">tumi kahi kalji karu naka. Mala distay</span> <span className="text-[#56CFFC]">that the payment has been declined</span> <span className="text-[#F59E0B]">ani paise pan</span> <span className="text-[#56CFFC]">deduct</span> <span className="text-[#F59E0B]">zhalet.</span> <span className="text-[#56CFFC]">But don't worry,</span> <span className="text-[#A78BFA]">yeh</span> <span className="text-[#56CFFC]">24 hours</span> <span className="text-[#A78BFA]">mein</span> <span className="text-[#56CFFC]">reverse</span> <span className="text-[#A78BFA]">ho jaega. Main aapke liye ek</span> <span className="text-[#56CFFC]">urgent ticket raise</span> <span className="text-[#A78BFA]">kar deta hoon.</span> <span className="text-[#56CFFC]">Is that okay?</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Metadata */}
      <div className={`pt-4 md:pt-[clamp(8px,0.93vw,16px)] border-t border-white/5`}>
        {datasetType === 'standard' ? (
          <div className={`flex flex-wrap justify-center gap-3 md:gap-[clamp(8px,0.93vw,16px)]`}>
            <div className={`px-4 py-1.5 text-[10px] md:px-[clamp(8px,0.93vw,16px)] md:py-[clamp(4px,0.35vw,6px)] rounded-full bg-white/5 md:text-[clamp(7px,0.58vw,10px)] font-medium tracking-wide text-white/40 uppercase`}>Monolingual</div>
            <div className={`px-4 py-1.5 text-[10px] md:px-[clamp(8px,0.93vw,16px)] md:py-[clamp(4px,0.35vw,6px)] rounded-full bg-white/5 md:text-[clamp(7px,0.58vw,10px)] font-medium tracking-wide text-white/40 uppercase`}>Rigid Syntax</div>
            <div className={`px-4 py-1.5 text-[10px] md:px-[clamp(8px,0.93vw,16px)] md:py-[clamp(4px,0.35vw,6px)] rounded-full bg-white/5 md:text-[clamp(7px,0.58vw,10px)] font-medium tracking-wide text-white/40 uppercase`}>Zero Empathy</div>
          </div>
        ) : (
          <div className={`flex flex-col gap-2 md:gap-[clamp(6px,0.52vw,12px)]`}>
            <div className={`flex items-start md:items-center gap-3 md:gap-[clamp(8px,0.93vw,16px)] text-[11px] md:text-[clamp(8px,0.64vw,11px)]`}>
              <span className={`font-bold text-white/30 uppercase tracking-widest w-10 md:w-[clamp(28px,2.31vw,40px)] text-right shrink-0`}>User</span>
              <div className={`flex flex-wrap gap-x-3 md:gap-x-[clamp(6px,0.52vw,12px)] gap-y-1`}>
                <span className="text-white/60"><span className="text-white/30 mr-1">Persona:</span>GenPop, 34, Pune</span>
                <span className={`inline md:inline text-white/10`}>•</span>
                <span className="text-white/60"><span className="text-white/30 mr-1">Intent:</span>Complain & resolution</span>
                <span className={`inline md:inline text-white/10`}>•</span>
                <span className="text-[#FF56A5]/80"><span className="text-white/30 mr-1">Emotion:</span>Frustrated & angry</span>
              </div>
            </div>
            <div className={`flex items-start md:items-center gap-3 md:gap-[clamp(8px,0.93vw,16px)] text-[11px] md:text-[clamp(8px,0.64vw,11px)]`}>
              <span className={`font-bold text-white/30 uppercase tracking-widest w-10 md:w-[clamp(28px,2.31vw,40px)] text-right shrink-0`}>Agent</span>
              <div className={`flex flex-wrap gap-x-3 md:gap-x-[clamp(6px,0.52vw,12px)] gap-y-1`}>
                <span className="text-white/60"><span className="text-white/30 mr-1">Persona:</span>Senior Support Rep</span>
                <span className={`inline md:inline text-white/10`}>•</span>
                <span className="text-white/60"><span className="text-white/30 mr-1">Intent:</span>De-escalation</span>
                <span className={`inline md:inline text-white/10`}>•</span>
                <span className="text-[#56CFFC]/80"><span className="text-white/30 mr-1">Emotion:</span>Empathetic & reassuring</span>
                <span className={`inline md:inline text-white/10`}>•</span>
                <span className="text-[#50E3C2]/80"><span className="text-white/30 mr-1">Action:</span>Language Mirroring</span>
              </div>
            </div>
          </div>
        )}
      </div>
      </motion.div>
      </div>
    </div>
  );
};
/* ── USP1: Desktop layout (unchanged — heading + full widget side by side) ── */
const Usp1Complexity = () => {
  const [datasetType, setDatasetType] = useState<'standard' | 'voqals'>('voqals');
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={sectionRef} className="max-w-[calc(var(--max-content)-2*var(--px-container))] w-full mx-auto grid grid-cols-1 md:grid-cols-12 gap-[var(--gap-medium)] items-center pointer-events-auto">
      {/* Left Column */}
      <div className="md:col-span-5 flex flex-col justify-center">
        <h2 className="font-display text-[length:var(--heading-usp)] md:text-[clamp(36px,3.13vw,54px)] font-black mb-[clamp(8px,0.93vw,16px)] text-white leading-[1.05] tracking-tighter uppercase overflow-visible">
          REAL&#8209;WORLD<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic whitespace-nowrap pr-[0.15em]">CODE&#8209;SWITCHING.</span>
        </h2>
        <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed mb-[clamp(16px,1.39vw,24px)] max-w-lg">
          In real Indian conversations, people move fluidly between 2 or 3 languages within a single breath. Every Voqals dataset is built around this reality, capturing natural, complex code-switched speech so your model understands how India actually communicates.
        </p>
      </div>
      {/* Right Column */}
      <div className="md:col-span-7 relative">
        <Usp1ExpandedWidget datasetType={datasetType} setDatasetType={setDatasetType} isPlaying={isPlaying} setIsPlaying={setIsPlaying} playbackProgress={playbackProgress} setPlaybackProgress={setPlaybackProgress} visible={visible} />
      </div>
    </div>
  );
};
/* ── USP1: Mobile wrapper — single widget, scroll-driven peek → fullscreen ── */
const Usp1MobileWrapper = () => {
  const [datasetType, setDatasetType] = useState<'standard' | 'voqals'>('voqals');
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const widgetProps = { datasetType, setDatasetType, isPlaying, setIsPlaying, playbackProgress, setPlaybackProgress, visible };
  const sectionRef = useRef<HTMLDivElement>(null);
  const scrollYProgress = useMotionValue(0);
  useEffect(() => {
    const container = document.getElementById('main-scroll-container');
    const section = sectionRef.current;
    if (!container || !section) return;
    const handleScroll = () => {
      // Walk up offsetParent chain to get absolute position within scroll container
      let sectionTop = 0;
      let el: HTMLElement | null = section;
      while (el && el !== container) {
        sectionTop += el.offsetTop;
        el = el.offsetParent as HTMLElement | null;
      }
      const sectionHeight = section.offsetHeight;
      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;
      // progress 0 = section top at viewport top, progress 1 = section bottom at viewport bottom
      const start = sectionTop;
      const end = sectionTop + sectionHeight - containerHeight;
      const progress = Math.max(0, Math.min(1, (scrollTop - start) / (end - start)));
      scrollYProgress.set(progress);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  // Lazy-load audio when section enters viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  // ── Scroll-driven animation timeline ──
  // 0.0–0.3: Heading fades out, widget starts growing
  // 0.3–0.35: isExpanded triggers (masked by blur)
  // 0.35–0.65: Margins/radius shrink, blur clears, content revealed
  // 0.65–0.8: Widget finishes growing to full height
  const headingOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const widgetBorderRadius = useTransform(scrollYProgress, [0.1, 0.6], [24, 16]);
  const widgetMarginLR = useTransform(scrollYProgress, [0, 0.35], [24, 0]);
  const widgetMarginB = useTransform(scrollYProgress, [0.1, 0.6], [16, 0]);
  const widgetHeight = useTransform(scrollYProgress, (v) => {
    const p = Math.min(1, Math.max(0, v / 0.8)); // normalize to 0-1 over 0–0.8 range
    const vh = window.innerHeight;
    const startH = vh * 0.38;
    const endH = vh - 90;
    return startH + (endH - startH) * p;
  });
  const detailsOpacity = useTransform(scrollYProgress, [0.2, 0.65], [0.7, 1]);
  const detailsBlurAmount = useTransform(scrollYProgress, [0.2, 0.65], [4, 0]);
  const detailsBlurFilter = useMotionTemplate`blur(${detailsBlurAmount}px)`;
  // isExpanded fires at 0.35 while blur is still ~4px, masking the class size jump
  const [isExpanded, setIsExpanded] = useState(false);
  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    const expanded = v > 0.35;
    if (expanded !== isExpanded) setIsExpanded(expanded);
  });
  return (
    <div ref={sectionRef} id="usp1-mobile" className="md:hidden h-[200dvh] w-full relative shrink-0">
      <div className="sticky top-0 h-[100dvh] w-full flex flex-col overflow-hidden">
        {/* Heading — vertically centered in the viewport, fades out on scroll */}
        <motion.div
          style={{ opacity: headingOpacity }}
          className="flex-1 flex flex-col items-center justify-center text-center px-[var(--px-container)] overflow-hidden pointer-events-auto pt-[70px]"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)] mb-6">
            Real-World Code-Switching
          </div>
          <h2 className="font-display text-[length:var(--heading-usp)] font-black mb-6 text-[#EEEEEE] leading-[1.05] tracking-tighter uppercase overflow-visible">
            REAL&#8209;WORLD<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic whitespace-nowrap pr-[0.15em]">CODE&#8209;SWITCHING.</span>
          </h2>
          <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed max-w-sm mx-auto">
            In real Indian conversations, people move fluidly between 2 or 3 languages within a single breath. We capture natural, complex code-switched speech so your model understands how India actually communicates.
          </p>
        </motion.div>
        {/* Widget — fixed to bottom, explicit height grows from peek to fullscreen */}
        <motion.div
          style={{ borderRadius: widgetBorderRadius, marginLeft: widgetMarginLR, marginRight: widgetMarginLR, marginBottom: widgetMarginB, height: widgetHeight }}
          className="gradient-border shrink-0 pointer-events-auto"
        >
          <div className="w-full h-full overflow-hidden" style={{ borderRadius: 'inherit' }}>
            <Usp1ExpandedWidget {...widgetProps} detailsOpacity={detailsOpacity} detailsBlur={detailsBlurFilter} isExpanded={isExpanded} />
          </div>
        </motion.div>
      </div>
    </div>
  );
};
const Usp2Technicality = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sliderPos, setSliderPos] = useState(50);
  const [hasInteracted, setHasInteracted] = useState(false);
  const autoAnimRef = useRef<number>(0);
  const autoTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Auto-slide animation: sweeps once, then repeats every 5s until user interacts
  useEffect(() => {
    if (hasInteracted) {
      cancelAnimationFrame(autoAnimRef.current);
      clearTimeout(autoTimerRef.current);
      return;
    }
    const runSweep = () => {
      let start: number | null = null;
      const duration = 2400; // ms for one full sweep
      const animate = (ts: number) => {
        if (!start) start = ts;
        const elapsed = ts - start;
        const t = Math.min(elapsed / duration, 1);
        // Ease in-out: go from 50 → 5 → 95 → 50
        let pos: number;
        if (t < 0.33) {
          const p = t / 0.33;
          pos = 50 - 45 * (1 - Math.pow(1 - p, 3)); // 50 → 5
        } else if (t < 0.66) {
          const p = (t - 0.33) / 0.33;
          pos = 5 + 90 * (1 - Math.pow(1 - p, 3)); // 5 → 95
        } else {
          const p = (t - 0.66) / 0.34;
          pos = 95 - 45 * (1 - Math.pow(1 - p, 3)); // 95 → 50
        }
        setSliderPos(pos);
        if (t < 1) {
          autoAnimRef.current = requestAnimationFrame(animate);
        } else {
          // Schedule next sweep
          autoTimerRef.current = setTimeout(runSweep, 5000);
        }
      };
      autoAnimRef.current = requestAnimationFrame(animate);
    };
    // Initial delay before first sweep
    autoTimerRef.current = setTimeout(runSweep, 1500);
    return () => {
      cancelAnimationFrame(autoAnimRef.current);
      clearTimeout(autoTimerRef.current);
    };
  }, [hasInteracted]);
  return (
    <>
      <div className="max-w-[calc(var(--max-content)-2*var(--px-container))] w-full mx-auto grid grid-cols-1 md:grid-cols-12 gap-[var(--gap-medium)] items-center pointer-events-auto">
        {/* Left Column: Copy */}
        <div className="md:col-span-5 flex flex-col justify-center order-1">
          <h3 className="font-display text-[length:var(--heading-usp)] md:text-[clamp(36px,3.13vw,54px)] font-black mb-[clamp(8px,0.93vw,16px)] text-white leading-[1.1] tracking-tighter uppercase relative z-10 overflow-visible">
            <span className="whitespace-nowrap">TRAIN ON SPEECH.</span> <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic pr-[0.15em]">NOT NOISE.</span>
          </h3>
          <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed mb-[clamp(12px,0.93vw,16px)] max-w-lg relative z-10">
            The <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-semibold">Voqals Quality Standard</span> is our proprietary studio certification process refined over years of AI speech data production. Every studio is audited, modified, and certified to our specifications so the only thing your model learns from is human voice.
          </p>
          {/* Studio Specifications */}
          <div className="flex flex-col gap-[clamp(8px,0.69vw,12px)] mb-[clamp(16px,1.39vw,24px)] relative z-10">
            <div className="flex items-center gap-[clamp(10px,0.81vw,14px)]">
              <div className="w-[clamp(6px,0.52vw,9px)] h-[clamp(6px,0.52vw,9px)] rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] shrink-0 shadow-[0_0_8px_rgba(86,207,252,0.4)]" />
              <span className="text-white text-[length:var(--text-body)] font-semibold tracking-wide">Below {'\u2212'}60dB noise floor</span>
            </div>
            <div className="flex items-center gap-[clamp(10px,0.81vw,14px)]">
              <div className="w-[clamp(6px,0.52vw,9px)] h-[clamp(6px,0.52vw,9px)] rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] shrink-0 shadow-[0_0_8px_rgba(86,207,252,0.4)]" />
              <span className="text-white text-[length:var(--text-body)] font-semibold tracking-wide">Under 200ms RT60</span>
            </div>
            <div className="flex items-center gap-[clamp(10px,0.81vw,14px)]">
              <div className="w-[clamp(6px,0.52vw,9px)] h-[clamp(6px,0.52vw,9px)] rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] shrink-0 shadow-[0_0_8px_rgba(86,207,252,0.4)]" />
              <span className="text-white text-[length:var(--text-body)] font-semibold tracking-wide">Zero electrical interference</span>
            </div>
          </div>
        </div>
        {/* Right Column: Interactive Slider */}
        <div className="md:col-span-7 relative order-2 flex flex-col">
          <div className="gradient-border rounded-[clamp(20px,1.85vw,32px)] backdrop-blur-xl p-[clamp(16px,1.4vw,24px)] flex flex-col shadow-2xl relative"
            style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' }}
          >
            {/* Widget Title */}
            <div className="flex flex-col items-center text-center mb-[clamp(6px,0.52vw,12px)]">
              <h4 className="text-[clamp(7px,0.58vw,10px)] font-bold uppercase tracking-[0.2em] text-white/60 mb-[clamp(2px,0.23vw,4px)]">The Voqals Certified Studio Difference</h4>
              <div className="flex items-center gap-[clamp(4px,0.46vw,8px)] text-[clamp(6px,0.52vw,9px)] uppercase tracking-widest text-white/30">
                <span>Slide to compare</span>
                <motion.div animate={{ x: [0, 4, 0] }} transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}>
                  <ArrowRight className="w-[clamp(8px,0.69vw,12px)] h-[clamp(8px,0.69vw,12px)]" />
                </motion.div>
              </div>
            </div>
          <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden select-none group border border-white/10 shadow-[inset_0_0_40px_rgba(86,207,252,0.04)] bg-[#0A0A0A]">

            {/* After Image (Background) - Voqals */}
            <div className="absolute inset-0 bg-black flex items-center justify-center">
              <img
                src="/clean-audio.webp"
                alt="Clean Spectrogram"
                loading="lazy"
                width="1920"
                height="1080"
                className="absolute inset-0 w-full h-full object-contain opacity-90"
              />
              <div
                className="absolute inset-0 z-10"
                style={{ clipPath: `polygon(${sliderPos}% 0, 100% 0, 100% 100%, ${sliderPos}% 100%)` }}
              >
                <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-md px-[clamp(8px,0.93vw,16px)] py-[clamp(4px,0.46vw,8px)] rounded-full text-[clamp(7px,0.58vw,10px)] font-bold border border-[#56CFFC]/30 uppercase tracking-widest shadow-lg">
                  Voqals Dataset
                </div>

              </div>
            </div>
            {/* Before Image (Foreground) - Standard */}
            <div
              className="absolute inset-0 bg-black flex items-center justify-center border-r border-white/20"
              style={{ clipPath: `polygon(0 0, ${sliderPos}% 0, ${sliderPos}% 100%, 0 100%)` }}
            >
              <img
                src="/bad-audio.webp"
                alt="Corrupted Spectrogram"
                loading="lazy"
                width="1920"
                height="1080"
                className="absolute inset-0 w-full h-full object-contain opacity-90"
              />
              <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-md px-[clamp(8px,0.93vw,16px)] py-[clamp(4px,0.46vw,8px)] rounded-full text-[clamp(7px,0.58vw,10px)] font-bold text-yellow-500 border border-yellow-500/30 uppercase tracking-widest z-10 shadow-lg">
                Standard Dataset
              </div>
              
            </div>
            {/* Slider Handle — minimal line with small diamond grip */}
            <div
              className="absolute top-0 bottom-0 w-px bg-white/70 cursor-ew-resize flex items-center justify-center pointer-events-none z-20"
              style={{ left: `${sliderPos}%` }}
            >
              <div className="w-[clamp(14px,1.16vw,20px)] h-[clamp(14px,1.16vw,20px)] bg-[#0a0a0a] border border-white/50 rotate-45 flex items-center justify-center shadow-[0_0_12px_rgba(255,255,255,0.15)]" />
            </div>
            {/* Invisible Range Input */}
            <input
              type="range"
              min="0"
              max="100"
              value={sliderPos}
              aria-label="Quality comparison slider"
              onChange={(e) => {
                setSliderPos(Number(e.target.value));
                if (!hasInteracted) setHasInteracted(true);
              }}
              className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-30"
            />
          </div>
            {/* Deep Dive CTA — below spectrogram */}
            <div className="flex justify-center mt-[clamp(8px,0.93vw,16px)]">
              <button
                onClick={() => setIsModalOpen(true)}
                className="group relative px-[clamp(20px,1.85vw,32px)] py-[clamp(8px,0.81vw,14px)] rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all shadow-[0_0_20px_rgba(27,54,255,0.3)] hover:shadow-[0_0_30px_rgba(86,207,252,0.4)] hover:scale-105 flex items-center justify-center"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
                <span className="relative z-10 flex items-center gap-3">
                  <Play className="w-4 h-4 text-white fill-white group-hover:scale-110 transition-transform" />
                  Deep Dive
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Lightbox Modal */}
      {isModalOpen && (
        <React.Suspense fallback={null}>
          <DiagnosticModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </React.Suspense>
      )}
    </>
  );
};
/* ── USP2: Mobile wrapper — scroll-driven peek → fullscreen ── */
const Usp2MobileWrapper = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sliderPos, setSliderPos] = useState(50);
  const [hasInteracted, setHasInteracted] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const autoAnimRef = useRef<number>(0);
  const autoTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (hasInteracted) {
      cancelAnimationFrame(autoAnimRef.current);
      clearTimeout(autoTimerRef.current);
      return;
    }
    const runSweep = () => {
      let start: number | null = null;
      const duration = 2400;
      const animate = (ts: number) => {
        if (!start) start = ts;
        const elapsed = ts - start;
        const t = Math.min(elapsed / duration, 1);
        let pos: number;
        if (t < 0.33) {
          const p = t / 0.33;
          pos = 50 - 45 * (1 - Math.pow(1 - p, 3));
        } else if (t < 0.66) {
          const p = (t - 0.33) / 0.33;
          pos = 5 + 90 * (1 - Math.pow(1 - p, 3));
        } else {
          const p = (t - 0.66) / 0.34;
          pos = 95 - 45 * (1 - Math.pow(1 - p, 3));
        }
        setSliderPos(pos);
        if (t < 1) {
          autoAnimRef.current = requestAnimationFrame(animate);
        } else {
          autoTimerRef.current = setTimeout(runSweep, 5000);
        }
      };
      autoAnimRef.current = requestAnimationFrame(animate);
    };
    autoTimerRef.current = setTimeout(runSweep, 1500);
    return () => {
      cancelAnimationFrame(autoAnimRef.current);
      clearTimeout(autoTimerRef.current);
    };
  }, [hasInteracted]);
  const scrollYProgress = useMotionValue(0);
  useEffect(() => {
    const container = document.getElementById('main-scroll-container');
    const section = sectionRef.current;
    if (!container || !section) return;
    const handleScroll = () => {
      let sectionTop = 0;
      let el: HTMLElement | null = section;
      while (el && el !== container) {
        sectionTop += el.offsetTop;
        el = el.offsetParent as HTMLElement | null;
      }
      const sectionHeight = section.offsetHeight;
      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;
      const start = sectionTop;
      const end = sectionTop + sectionHeight - containerHeight;
      const progress = Math.max(0, Math.min(1, (scrollTop - start) / (end - start)));
      scrollYProgress.set(progress);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  // ── Scroll-driven animation timeline (same as USP1) ──
  const headingOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const widgetBorderRadius = useTransform(scrollYProgress, [0.1, 0.6], [24, 16]);
  const widgetMarginLR = useTransform(scrollYProgress, [0, 0.35], [24, 0]);
  const widgetMarginB = useTransform(scrollYProgress, [0, 0.35], [16, 0]);
  const widgetHeight = useTransform(scrollYProgress, (v) => {
    const p = Math.min(1, Math.max(0, v / 0.8));
    const vh = window.innerHeight;
    const startH = vh * 0.38;
    const endH = vh - 90;
    return startH + (endH - startH) * p;
  });
  const spectrogramPadding = useTransform(scrollYProgress, [0.3, 0.7], [0, 10]);
  const deepDiveOpacity = useTransform(scrollYProgress, [0.5, 0.75], [0, 1]);
  const deepDiveHeight = useTransform(scrollYProgress, [0.5, 0.8], [0, 64]);
  return (
    <>
    <div ref={sectionRef} id="usp2-mobile" className="md:hidden h-[200dvh] w-full relative shrink-0">
      <div className="sticky top-0 h-[100dvh] w-full flex flex-col overflow-hidden">
        {/* Heading — vertically centered, fades out on scroll */}
        <motion.div
          style={{ opacity: headingOpacity }}
          className="flex-1 flex flex-col items-center justify-center text-center px-[var(--px-container)] overflow-hidden pointer-events-auto pt-[70px]"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)] mb-6">
            The Voqals Quality Standard
          </div>
          <h3 className="font-display text-[length:var(--heading-usp)] font-black mb-6 text-[#EEEEEE] leading-[1.05] tracking-tighter uppercase overflow-visible">
            <span className="whitespace-nowrap">TRAIN ON SPEECH.</span><br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic pr-[0.15em]">NOT NOISE.</span>
          </h3>
          <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed max-w-sm mx-auto">
            The <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-semibold">Voqals Quality Standard</span> is our proprietary studio certification process refined over years of AI speech data production. Every studio is audited, modified, and certified to our specifications so the only thing your model learns from is human voice.
          </p>
        </motion.div>
        {/* Widget — peek to fullscreen */}
        <motion.div
          style={{ borderRadius: widgetBorderRadius, marginLeft: widgetMarginLR, marginRight: widgetMarginLR, marginBottom: widgetMarginB, height: widgetHeight }}
          className="gradient-border shrink-0 pointer-events-auto shadow-2xl backdrop-blur-xl overflow-hidden"
        >
          <div className="w-full h-full flex flex-col relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' }}
          >
            {/* Top highlight */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            {/* Title */}
            <div className="flex flex-col items-center gap-2 mb-2 shrink-0 px-5 pt-[2dvh]">
              <p className="text-[10px] font-bold text-white/60 uppercase tracking-[0.2em] text-center">The Voqals Certified Studio Difference</p>
              <div className="flex items-center gap-3 w-full">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent to-white/8" />
                <p className="text-[8px] text-white/30 uppercase tracking-[0.25em] font-medium whitespace-nowrap">Slide to compare</p>
                <div className="h-px flex-1 bg-gradient-to-l from-transparent to-white/8" />
              </div>
            </div>
            {/* Spectrogram slider — fills remaining space */}
            <motion.div style={{ marginLeft: spectrogramPadding, marginRight: spectrogramPadding, marginBottom: spectrogramPadding, borderRadius: spectrogramPadding }} className="flex-1 relative overflow-hidden shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)] border border-white/[0.06] bg-white/[0.03]">
              {/* After Image (Background) - Voqals */}
              <div className="absolute inset-0 bg-black">
                <img
                  src="/clean-audio.webp"
                  alt="Clean Spectrogram"
                  loading="lazy"
                  width="1920"
                  height="1080"
                  className="absolute inset-0 w-full h-full object-contain opacity-90"
                />
                <div
                  className="absolute inset-0 z-10"
                  style={{ clipPath: `polygon(${sliderPos}% 0, 100% 0, 100% 100%, ${sliderPos}% 100%)` }}
                >
                  <div className="absolute top-2.5 right-2.5 z-10 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm text-[8px] font-bold text-[#56CFFC]/80 uppercase tracking-widest border border-[#56CFFC]/15">
                    Voqals Studio
                  </div>
                </div>
              </div>
              {/* Before Image (Foreground) - Standard */}
              <div
                className="absolute inset-0 bg-black border-r border-white/10"
                style={{ clipPath: `polygon(0 0, ${sliderPos}% 0, ${sliderPos}% 100%, 0 100%)` }}
              >
                <img
                  src="/bad-audio.webp"
                  alt="Corrupted Spectrogram"
                  loading="lazy"
                  width="1920"
                  height="1080"
                  className="absolute inset-0 w-full h-full object-contain opacity-90"
                />
                <div className="absolute top-2.5 left-2.5 z-10 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm text-[8px] font-bold text-yellow-500/80 uppercase tracking-widest border border-yellow-500/15">
                  Standard Studio
                </div>
              </div>
              {/* Slider Handle — minimal line with small diamond grip */}
              <div
                className="absolute top-0 bottom-0 w-px bg-white/70 cursor-ew-resize flex items-center justify-center pointer-events-none z-20"
                style={{ left: `${sliderPos}%` }}
              >
                <div className="w-3 h-3 bg-[#0a0a0a] border border-white/50 rotate-45 flex items-center justify-center shadow-[0_0_12px_rgba(255,255,255,0.15)]" />
              </div>
              {/* Invisible Range Input */}
              <input
                type="range"
                min="0"
                max="100"
                value={sliderPos}
                aria-label="Quality comparison slider"
                onChange={(e) => {
                  setSliderPos(Number(e.target.value));
                  if (!hasInteracted) setHasInteracted(true);
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-30"
              />
            </motion.div>
            {/* Deep Dive CTA — grows into view when fullscreen */}
            <motion.div style={{ opacity: deepDiveOpacity, height: deepDiveHeight }} className="shrink-0 flex items-center justify-center overflow-hidden">
              <button
                onClick={() => setIsModalOpen(true)}
                className="group relative px-7 py-2.5 rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white text-[10px] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all shadow-[0_0_20px_rgba(27,54,255,0.3)] hover:shadow-[0_0_30px_rgba(86,207,252,0.4)] hover:scale-105 flex items-center justify-center"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <Play className="w-3.5 h-3.5 text-white fill-white" />
                  Deep Dive
                </span>
              </button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
    {/* Lightbox Modal */}
    {isModalOpen && (
      <React.Suspense fallback={null}>
        <DiagnosticModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      </React.Suspense>
    )}
    </>
  );
};
/* ── USP3: Expanded Widget (shared by mobile wrapper + desktop layout) ── */
interface Usp3WidgetProps {
  activeTab: 'neutral' | 'empathetic' | 'urgent';
  handleTabChange: (tab: 'neutral' | 'empathetic' | 'urgent') => void;
  isPlaying: boolean;
  togglePlay: () => void;
  playbackProgress: number;
  duration: number;
  waveform: number[];
  detailsOpacity?: any;
  detailsBlur?: any;
  isExpanded?: boolean;
}
const Usp3ExpandedWidget = ({ activeTab, handleTabChange, isPlaying, togglePlay, playbackProgress, duration, waveform, detailsOpacity, detailsBlur, isExpanded }: Usp3WidgetProps) => {
  const tabs = ['neutral', 'empathetic', 'urgent'] as const;
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  useEffect(() => {
    const container = tabContainerRef.current;
    if (!container) return;
    const measure = () => {
      const buttons = container.querySelectorAll('button');
      const tabIndex = tabs.indexOf(activeTab);
      const btn = buttons[tabIndex];
      if (btn) {
        setIndicatorStyle({ left: (btn as HTMLElement).offsetLeft, width: (btn as HTMLElement).offsetWidth });
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(measure));
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [activeTab]);
  return (
    <div className="gradient-border rounded-2xl md:rounded-[clamp(20px,1.85vw,32px)] backdrop-blur-xl shadow-2xl relative h-full"
      style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' }}
    >
      <div className="px-6 py-[3dvh] md:p-[clamp(20px,1.85vw,32px)] flex flex-col h-full overflow-hidden rounded-[inherit]">
      {/* Top highlight */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      {/* Widget Heading */}
      <div className="flex flex-col items-center gap-2 md:gap-[clamp(5px,0.46vw,8px)] mb-2 md:mb-[clamp(5px,0.46vw,8px)] shrink-0">
        <p className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-bold text-white/60 uppercase tracking-[0.2em] text-center">The Director Difference</p>
      </div>
      {/* Toggle Switch */}
      <div className="flex justify-center mb-5 md:mb-[clamp(16px,1.39vw,24px)]">
        <div ref={tabContainerRef} className="bg-white/5 p-1 md:p-[clamp(3px,0.23vw,4px)] rounded-full flex items-center relative">
          {/* Sliding gradient indicator */}
          <motion.div
            className="absolute top-1 bottom-1 rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] shadow-[0_0_20px_rgba(27,54,255,0.3)]"
            animate={{ left: indicatorStyle.left, width: indicatorStyle.width }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          />
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`relative z-10 px-4 md:px-[clamp(16px,1.39vw,24px)] py-2.5 md:py-[clamp(7px,0.58vw,10px)] rounded-full text-[11px] md:text-[clamp(7px,0.64vw,11px)] font-bold tracking-[0.15em] md:tracking-[0.2em] uppercase transition-colors duration-300 whitespace-nowrap ${
                activeTab === tab ? 'text-white' : 'text-white/40 hover:text-white/60'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {/* Audio Player */}
      <div className="mb-5 md:mb-[clamp(14px,1.16vw,20px)] flex items-center gap-3 md:gap-[clamp(11px,0.93vw,16px)] bg-white/5 rounded-2xl md:rounded-[clamp(11px,0.93vw,16px)] p-3 md:p-[clamp(8px,0.69vw,12px)] border border-white/10">
        <div className="flex-1 flex flex-col gap-2 md:gap-[clamp(5px,0.46vw,8px)] min-w-0">
          <div className="flex justify-between items-center">
            <span className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-bold tracking-widest uppercase text-white/50">
              {activeTab === 'neutral' ? 'Directed: Neutral' :
               activeTab === 'empathetic' ? 'Directed: Empathy' :
               'Directed: Urgency'}
            </span>
            <span className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-mono text-white/30">
              {(() => { const cur = Math.floor(playbackProgress * duration / 1000); const tot = Math.floor(duration / 1000); const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; return `${fmt(cur)} / ${fmt(tot)}`; })()}
            </span>
          </div>
          <div className="h-8 md:h-[clamp(20px,2.3vw,40px)] flex items-end gap-[1px] overflow-hidden relative w-full">
            {waveform.map((height, i) => {
              const barProgress = i / waveform.length;
              const isPassed = playbackProgress > barProgress;
              const distBehindHead = playbackProgress - barProgress;
              const isGlow = isPassed && distBehindHead < 0.04 && playbackProgress > 0;
              return (
                <div
                  key={i}
                  className={`flex-1 min-w-0 rounded-full transition-colors duration-100 ${isPassed ? 'bg-[#56CFFC]' : 'bg-[#56CFFC]/20'}`}
                  style={{
                    height: `${height}%`,
                    boxShadow: isGlow ? `0 0 ${8 - distBehindHead * 150}px rgba(86,207,252,0.9)` : undefined,
                  }}
                />
              );
            })}
          </div>
        </div>
        <button
          aria-label={isPlaying ? "Pause audio" : "Play audio"}
          onClick={togglePlay}
          className="w-12 h-12 md:w-[clamp(32px,2.78vw,48px)] md:h-[clamp(32px,2.78vw,48px)] shrink-0 rounded-full flex items-center justify-center bg-gradient-to-br from-[#56CFFC] to-[#1B36FF] text-white hover:shadow-[0_0_16px_rgba(86,207,252,0.3)] transition-shadow"
        >
          {isPlaying ? (
            <Pause className="w-5 h-5 md:w-[clamp(14px,1.16vw,20px)] md:h-[clamp(14px,1.16vw,20px)] fill-current" />
          ) : (
            <Play className="w-5 h-5 md:w-[clamp(14px,1.16vw,20px)] md:h-[clamp(14px,1.16vw,20px)] fill-current ml-0.5" />
          )}
        </button>
      </div>
      {/* Transcript + Metadata — on mobile, opacity driven by scroll progress */}
      <motion.div style={detailsOpacity != null ? { opacity: detailsOpacity, filter: detailsBlur } : undefined} className="flex flex-col flex-1 min-h-0 overflow-hidden">
      {/* Transcript Section */}
      <div className="min-h-[100px] md:min-h-[clamp(68px,5.79vw,100px)] bg-black/40 rounded-3xl md:rounded-[clamp(16px,1.39vw,24px)] p-5 md:p-[clamp(16px,1.39vw,24px)] border border-white/5 relative mb-4 md:mb-[clamp(11px,0.93vw,16px)]">
        <AnimatePresence mode="wait">
          {activeTab === 'neutral' && (
            <motion.div
              key="neutral"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col gap-5 md:gap-[clamp(14px,1.16vw,20px)]"
            >
              <div className="flex items-center justify-between mb-2 md:mb-[clamp(5px,0.46vw,8px)]">
                <div className="flex items-center gap-3 md:gap-[clamp(8px,0.69vw,12px)]">
                  <div className="w-2 h-2 md:w-[clamp(5px,0.46vw,8px)] md:h-[clamp(5px,0.46vw,8px)] rounded-full bg-white" />
                  <span className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-bold tracking-widest uppercase text-white/40">Voqals Dataset</span>
                </div>
                <div className="flex gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[9px] md:text-[clamp(6px,0.52vw,9px)] font-bold uppercase tracking-widest">
                  <span className="text-white/60">Pause</span>
                  <span className="text-white/80">Filler</span>
                </div>
              </div>
              <p className="text-[length:var(--text-body)] leading-relaxed text-white/90 font-medium">
                "Ma'am, I completely understand that you're upset about the delay and we're actively working on resolving the issue. <span className="text-white/80">Um,</span> can I please, <span className="text-white/80">uh,</span> place you on hold for a moment <span className="text-white/60 italic">[pause]</span> while I check the status?"
              </p>
            </motion.div>
          )}
          {activeTab === 'empathetic' && (
            <motion.div
              key="empathetic"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col gap-5 md:gap-[clamp(14px,1.16vw,20px)]"
            >
              <div className="flex items-center justify-between mb-2 md:mb-[clamp(5px,0.46vw,8px)]">
                <div className="flex items-center gap-3 md:gap-[clamp(8px,0.69vw,12px)]">
                  <div className="w-2 h-2 md:w-[clamp(5px,0.46vw,8px)] md:h-[clamp(5px,0.46vw,8px)] rounded-full bg-[#56CFFC]" />
                  <span className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-bold tracking-widest uppercase text-white/40">Voqals Dataset</span>
                </div>
                <div className="flex gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[9px] md:text-[clamp(6px,0.52vw,9px)] font-bold uppercase tracking-widest">
                  <span className="text-[#FF56A5]">Breath</span>
                  <span className="text-[#50E3C2]">Pause</span>
                  <span className="text-[#56CFFC]">Emphasis</span>
                  <span className="text-yellow-400">Tone Shift</span>
                </div>
              </div>
              <p className="text-[length:var(--text-body)] leading-relaxed text-white/90 font-medium">
                "Ma'am, I <span className="text-[#56CFFC] font-bold">completely</span> understand that you're upset about the delay and <span className="text-[#50E3C2] italic">[short pause]</span> we're <span className="text-[#56CFFC] font-bold">actively</span> working on resolving the issue. <span className="text-yellow-400 italic">[tone shift]</span> <span className="text-[#FF56A5] italic">[short intake]</span> Um, <span className="text-[#56CFFC] font-bold">can I please</span> place you on hold for a moment <span className="text-[#50E3C2] italic">[pause]</span> while I check the status?"
              </p>
            </motion.div>
          )}
          {activeTab === 'urgent' && (
            <motion.div
              key="urgent"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col gap-5 md:gap-[clamp(14px,1.16vw,20px)]"
            >
              <div className="flex items-center justify-between mb-2 md:mb-[clamp(5px,0.46vw,8px)]">
                <div className="flex items-center gap-3 md:gap-[clamp(8px,0.69vw,12px)]">
                  <div className="w-2 h-2 md:w-[clamp(5px,0.46vw,8px)] md:h-[clamp(5px,0.46vw,8px)] rounded-full bg-[#FF56A5]" />
                  <span className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-bold tracking-widest uppercase text-white/40">Voqals Dataset</span>
                </div>
                <div className="flex gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[9px] md:text-[clamp(6px,0.52vw,9px)] font-bold uppercase tracking-widest">
                  <span className="text-yellow-400">Fast Pace</span>
                  <span className="text-[#FF56A5]">Rushed</span>
                  <span className="text-[#56CFFC]">Emphasis</span>
                  <span className="text-[#50E3C2]">Louder</span>
                </div>
              </div>
              <p className="text-[length:var(--text-body)] leading-relaxed text-white/90 font-medium">
                "<span className="text-yellow-400 italic">[fast]</span> Ma'am I completely understand that you're upset about the delay and we're <span className="text-[#56CFFC] font-bold">actively</span> working on resolving the issue. <span className="text-[#FF56A5] italic">[rushed]</span> Um, <span className="text-[#50E3C2]">[slightly louder]</span> can I please place you on hold for a moment while I check the status?"
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* Metadata Chips */}
      <div className="pt-4 md:pt-[clamp(11px,0.93vw,16px)] border-t border-white/5 shrink-0">
        {/* Specs row — same for all tabs */}
        <div className="flex items-center gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[11px] md:text-[clamp(7px,0.64vw,11px)] mb-3 md:mb-[clamp(8px,0.69vw,12px)]">
          <span className="font-bold text-white/30 uppercase tracking-widest w-16 md:w-[clamp(43px,3.7vw,64px)] text-right shrink-0">Specs</span>
          <div className="flex flex-wrap gap-x-3 md:gap-x-[clamp(8px,0.69vw,12px)] gap-y-1">
            <span className="text-white/50 font-mono">48 kHz</span>
            <span className="text-white/10">•</span>
            <span className="text-white/50 font-mono">24-bit</span>
            <span className="text-white/10">•</span>
            <span className="text-white/50 font-mono">Mono</span>
            <span className="text-white/10">•</span>
            <span className="text-white/50 font-mono">{(duration / 1000).toFixed(1)}s</span>
          </div>
        </div>
        {activeTab === 'neutral' && (
          <div className="flex flex-col gap-3 md:gap-[clamp(8px,0.69vw,12px)]">
            <div className="flex items-center gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[11px] md:text-[clamp(7px,0.64vw,11px)]">
              <span className="font-bold text-white/30 uppercase tracking-widest w-16 md:w-[clamp(43px,3.7vw,64px)] text-right shrink-0">Prosody</span>
              <div className="flex flex-wrap gap-x-3 md:gap-x-[clamp(8px,0.69vw,12px)] gap-y-1">
                <span className="text-white/80">Even Pitch Contours</span>
                <span className="text-white/10">•</span>
                <span className="text-white/80">Measured Speech Rate</span>
              </div>
            </div>
            <div className="flex items-center gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[11px] md:text-[clamp(7px,0.64vw,11px)]">
              <span className="font-bold text-white/30 uppercase tracking-widest w-16 md:w-[clamp(43px,3.7vw,64px)] text-right shrink-0">Texture</span>
              <div className="flex flex-wrap gap-x-3 md:gap-x-[clamp(8px,0.69vw,12px)] gap-y-1">
                <span className="text-white/80">Natural Pauses</span>
                <span className="text-white/10">•</span>
                <span className="text-white/80">Subtle Fillers</span>
                <span className="text-white/10">•</span>
                <span className="text-white/80">Clear Articulation</span>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'empathetic' && (
          <div className="flex flex-col gap-3 md:gap-[clamp(8px,0.69vw,12px)]">
            <div className="flex items-center gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[11px] md:text-[clamp(7px,0.64vw,11px)]">
              <span className="font-bold text-white/30 uppercase tracking-widest w-16 md:w-[clamp(43px,3.7vw,64px)] text-right shrink-0">Prosody</span>
              <div className="flex flex-wrap gap-x-3 md:gap-x-[clamp(8px,0.69vw,12px)] gap-y-1">
                <span className="text-white/80">Soft Pitch Contours</span>
                <span className="text-white/10">•</span>
                <span className="text-white/80">Slowed Speech Rate</span>
                <span className="text-white/10">•</span>
                <span className="text-white/80">Empathetic Tone Shift</span>
              </div>
            </div>
            <div className="flex items-center gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[11px] md:text-[clamp(7px,0.64vw,11px)]">
              <span className="font-bold text-white/30 uppercase tracking-widest w-16 md:w-[clamp(43px,3.7vw,64px)] text-right shrink-0">Texture</span>
              <div className="flex flex-wrap gap-x-3 md:gap-x-[clamp(8px,0.69vw,12px)] gap-y-1">
                <span className="text-[#FF56A5]/80">Short Intakes</span>
                <span className="text-white/10">•</span>
                <span className="text-[#50E3C2]/80">Deliberate Pauses</span>
                <span className="text-white/10">•</span>
                <span className="text-[#56CFFC]/80">Warm Emphasis</span>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'urgent' && (
          <div className="flex flex-col gap-3 md:gap-[clamp(8px,0.69vw,12px)]">
            <div className="flex items-center gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[11px] md:text-[clamp(7px,0.64vw,11px)]">
              <span className="font-bold text-white/30 uppercase tracking-widest w-16 md:w-[clamp(43px,3.7vw,64px)] text-right shrink-0">Prosody</span>
              <div className="flex flex-wrap gap-x-3 md:gap-x-[clamp(8px,0.69vw,12px)] gap-y-1">
                <span className="text-white/80">Accelerated Rate</span>
                <span className="text-white/10">•</span>
                <span className="text-white/80">Raised Volume</span>
              </div>
            </div>
            <div className="flex items-center gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[11px] md:text-[clamp(7px,0.64vw,11px)]">
              <span className="font-bold text-white/30 uppercase tracking-widest w-16 md:w-[clamp(43px,3.7vw,64px)] text-right shrink-0">Texture</span>
              <div className="flex flex-wrap gap-x-3 md:gap-x-[clamp(8px,0.69vw,12px)] gap-y-1">
                <span className="text-yellow-400/80">Rushed Delivery</span>
                <span className="text-white/10">•</span>
                <span className="text-[#56CFFC]/80">Stressed Emphasis</span>
                <span className="text-white/10">•</span>
                <span className="text-[#50E3C2]/80">Heightened Energy</span>
              </div>
            </div>
          </div>
        )}
      </div>
      </motion.div>
      </div>
    </div>
  );
};
/* ── USP3: Audio sources ── */
const USP3_AUDIO: Record<string, string> = {
  neutral: '/Audio/Neutral.wav',
  empathetic: '/Audio/Empathetic.wav',
  urgent: '/Audio/Urgent.wav',
};
/* ── USP3: Extract real waveform from audio file ── */
const waveformCache: Record<string, number[]> = {};
function useWaveform(url: string, bars: number = 100, enabled: boolean = true) {
  const [data, setData] = useState<number[]>(() => waveformCache[url] || Array(bars).fill(5));
  useEffect(() => {
    if (!enabled) return;
    if (waveformCache[url]) { setData(waveformCache[url]); return; }
    let cancelled = false;
    fetch(url)
      .then(r => r.arrayBuffer())
      .then(buf => new (window.AudioContext || (window as any).webkitAudioContext)().decodeAudioData(buf))
      .then(decoded => {
        if (cancelled) return;
        const raw = decoded.getChannelData(0);
        const step = Math.floor(raw.length / bars);
        const peaks: number[] = [];
        for (let i = 0; i < bars; i++) {
          let max = 0;
          for (let j = i * step; j < (i + 1) * step && j < raw.length; j++) {
            const abs = Math.abs(raw[j]);
            if (abs > max) max = abs;
          }
          peaks.push(max);
        }
        const maxPeak = Math.max(...peaks, 0.01);
        const normalized = peaks.map(p => Math.max(4, (p / maxPeak) * 100));
        waveformCache[url] = normalized;
        setData(normalized);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [url, bars, enabled]);
  return data;
}
/* ── USP3: Desktop layout (heading + widget side by side) ── */
const Usp3Creativity = ({ onOpenTalentForm }: { onOpenTalentForm: () => void }) => {
  const [activeTab, setActiveTab] = useState<'neutral' | 'empathetic' | 'urgent'>('empathetic');
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [duration, setDuration] = useState(3000);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const usp3Ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = usp3Ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!visible) return;
    const audio = new Audio(USP3_AUDIO[activeTab]);
    audioRef.current = audio;
    audio.addEventListener('loadedmetadata', () => setDuration(audio.duration * 1000));
    audio.addEventListener('ended', () => { setIsPlaying(false); setPlaybackProgress(0); });
    return () => { audio.pause(); audio.src = ''; };
  }, [activeTab, visible]);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    let raf: number;
    const tick = () => {
      if (audio.duration) setPlaybackProgress(audio.currentTime / audio.duration);
      raf = requestAnimationFrame(tick);
    };
    if (isPlaying) { audio.play().catch(() => {}); raf = requestAnimationFrame(tick); }
    else { audio.pause(); }
    return () => cancelAnimationFrame(raf);
  }, [isPlaying]);
  const togglePlay = () => {
    if (playbackProgress >= 1) { const a = audioRef.current; if (a) a.currentTime = 0; setPlaybackProgress(0); }
    setIsPlaying(!isPlaying);
  };
  const handleTabChange = (tab: 'neutral' | 'empathetic' | 'urgent') => {
    setActiveTab(tab);
    setIsPlaying(false);
    setPlaybackProgress(0);
  };
  const waveform = useWaveform(USP3_AUDIO[activeTab], 120, visible);
  return (
    <div ref={usp3Ref} className="max-w-[calc(var(--max-content)-2*var(--px-container))] w-full mx-auto grid grid-cols-1 md:grid-cols-12 gap-[var(--gap-medium)] items-center pointer-events-auto">
      {/* Left Column */}
      <div className="md:col-span-5 flex flex-col justify-center">
        <h3 className="font-display text-[length:var(--heading-usp)] md:text-[clamp(36px,3.13vw,54px)] font-black mb-[clamp(8px,0.93vw,16px)] text-white leading-[1.1] tracking-tighter uppercase overflow-visible">
          DIRECTED FOR<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic pr-[0.15em]">REAL EMOTIONS.</span>
        </h3>
        <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed mb-[clamp(16px,1.39vw,24px)] max-w-lg">
          Flat recordings produce flat AI. Every Voqals session is directed by experienced voice UI directors who understand both the craft of performance and the technical requirements of AI training data. Every utterance is tagged with intent, emotion, persona, and action metadata.
        </p>
      </div>
      {/* Right Column */}
      <div className="md:col-span-7 relative">
        <Usp3ExpandedWidget activeTab={activeTab} handleTabChange={handleTabChange} isPlaying={isPlaying} togglePlay={togglePlay} playbackProgress={playbackProgress} duration={duration} waveform={waveform} />
      </div>
      {/* Swipe-in Voice Talent CTA */}
      <motion.div 
        initial={{ opacity: 0, x: 100, rotate: 5 }}
        whileInView={{ opacity: 1, x: 0, rotate: 0 }}
        transition={{ delay: 0.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        viewport={{ once: false, amount: 0.5 }}
        className="hidden md:block absolute bottom-8 right-8 z-50"
      >
        <button onClick={onOpenTalentForm} className="flex items-center gap-4 p-4 rounded-2xl bg-black/60 border border-[#56CFFC]/30 backdrop-blur-xl group hover:bg-black/80 hover:border-[#56CFFC] transition-all duration-300 shadow-[0_10px_40px_rgba(0,0,0,0.5)] text-left">
          <div className="w-12 h-12 rounded-full bg-[#56CFFC]/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-[0_0_15px_rgba(86,207,252,0.3)]">
            <Sparkles className="w-6 h-6 text-[#56CFFC]" />
          </div>
          <div className="pr-2">
            <p className="text-[10px] uppercase tracking-widest font-bold mb-1">Voice talent?</p>
            <p className="text-sm text-white font-medium flex items-center gap-2 group-hover:text-[#56CFFC] transition-colors">
              Join our roster
              <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
            </p>
          </div>
        </button>
      </motion.div>
    </div>
  );
};
/* ── USP3: Mobile wrapper — scroll-driven peek → fullscreen (like USP1) ── */
const Usp3MobileWrapper = () => {
  const [activeTab, setActiveTab] = useState<'neutral' | 'empathetic' | 'urgent'>('empathetic');
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [duration, setDuration] = useState(3000);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!visible) return;
    const audio = new Audio(USP3_AUDIO[activeTab]);
    audioRef.current = audio;
    audio.addEventListener('loadedmetadata', () => setDuration(audio.duration * 1000));
    audio.addEventListener('ended', () => { setIsPlaying(false); setPlaybackProgress(0); });
    return () => { audio.pause(); audio.src = ''; };
  }, [activeTab, visible]);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    let raf: number;
    const tick = () => {
      if (audio.duration) setPlaybackProgress(audio.currentTime / audio.duration);
      raf = requestAnimationFrame(tick);
    };
    if (isPlaying) { audio.play().catch(() => {}); raf = requestAnimationFrame(tick); }
    else { audio.pause(); }
    return () => cancelAnimationFrame(raf);
  }, [isPlaying]);
  const togglePlay = () => {
    if (playbackProgress >= 1) { const a = audioRef.current; if (a) a.currentTime = 0; setPlaybackProgress(0); }
    setIsPlaying(!isPlaying);
  };
  const handleTabChange = (tab: 'neutral' | 'empathetic' | 'urgent') => {
    setActiveTab(tab);
    setIsPlaying(false);
    setPlaybackProgress(0);
  };
  const waveform = useWaveform(USP3_AUDIO[activeTab], 120, visible);
  const sectionRef = useRef<HTMLDivElement>(null);
  const scrollYProgress = useMotionValue(0);
  useEffect(() => {
    const container = document.getElementById('main-scroll-container');
    const section = sectionRef.current;
    if (!container || !section) return;
    const handleScroll = () => {
      let sectionTop = 0;
      let el: HTMLElement | null = section;
      while (el && el !== container) {
        sectionTop += el.offsetTop;
        el = el.offsetParent as HTMLElement | null;
      }
      const sectionHeight = section.offsetHeight;
      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;
      const start = sectionTop;
      const end = sectionTop + sectionHeight - containerHeight;
      const progress = Math.max(0, Math.min(1, (scrollTop - start) / (end - start)));
      scrollYProgress.set(progress);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  // Lazy-load audio when section enters viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  // Scroll-driven animation timeline (same as USP1 but shorter max height)
  const headingOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const widgetBorderRadius = useTransform(scrollYProgress, [0.1, 0.6], [24, 16]);
  const widgetMarginLR = useTransform(scrollYProgress, [0, 0.35], [24, 0]);
  const widgetMarginB = useTransform(scrollYProgress, [0.1, 0.6], [16, 0]);
  const widgetHeight = useTransform(scrollYProgress, (v) => {
    const p = Math.min(1, Math.max(0, v / 0.8));
    const vh = window.innerHeight;
    const startH = vh * 0.38;
    const endH = vh - 90;
    return startH + (endH - startH) * p;
  });
  const detailsOpacity = useTransform(scrollYProgress, [0.2, 0.65], [0.7, 1]);
  const detailsBlurAmount = useTransform(scrollYProgress, [0.2, 0.65], [4, 0]);
  const detailsBlurFilter = useMotionTemplate`blur(${detailsBlurAmount}px)`;
  const [isExpanded, setIsExpanded] = useState(false);
  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    const expanded = v > 0.35;
    if (expanded !== isExpanded) setIsExpanded(expanded);
  });
  return (
    <div ref={sectionRef} id="usp3-mobile" className="md:hidden h-[200dvh] w-full relative shrink-0">
      <div className="sticky top-0 h-[100dvh] w-full flex flex-col overflow-hidden">
        {/* Heading */}
        <motion.div
          style={{ opacity: headingOpacity }}
          className="flex-1 flex flex-col items-center justify-center text-center px-[var(--px-container)] overflow-hidden pointer-events-auto pt-[70px]"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)] mb-6">
            Directed Performance
          </div>
          <h3 className="font-display text-[length:var(--heading-usp)] font-black mb-6 text-[#EEEEEE] leading-[1.05] tracking-tighter uppercase overflow-visible">
            DIRECTED FOR<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic pr-[0.15em]">REAL EMOTIONS.</span>
          </h3>
          <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed max-w-sm mx-auto">
            Flat recordings produce flat AI. Every Voqals session is directed by experienced voice UI directors who understand both the craft of performance and the technical requirements of AI training data.
          </p>
        </motion.div>
        {/* Widget */}
        <motion.div
          style={{ borderRadius: widgetBorderRadius, marginLeft: widgetMarginLR, marginRight: widgetMarginLR, marginBottom: widgetMarginB, height: widgetHeight }}
          className="gradient-border shrink-0 pointer-events-auto"
        >
          <div className="w-full h-full overflow-hidden" style={{ borderRadius: 'inherit' }}>
            <Usp3ExpandedWidget
              activeTab={activeTab}
              handleTabChange={handleTabChange}
              isPlaying={isPlaying}
              togglePlay={togglePlay}
              playbackProgress={playbackProgress}
              duration={duration}
              waveform={waveform}
              detailsOpacity={detailsOpacity}
              detailsBlur={detailsBlurFilter}
              isExpanded={isExpanded}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};
const TheDifferenceSection = ({ onOpenTalentForm, onOpenStudioForm }: { onOpenTalentForm: () => void; onOpenStudioForm: () => void }) => {
  const [activeSection, setActiveSection] = useState('overview');
  useEffect(() => {
    const container = document.getElementById('main-scroll-container');
    if (!container) return;
    const getVisibleSection = () => {
      const isMobile = window.innerWidth < 768;
      const sectionIds = isMobile
        ? ['overview', 'usp1-mobile', 'usp2-mobile', 'usp3-mobile']
        : ['overview', 'usp1', 'usp2', 'usp3'];
      const scrollTop = container.scrollTop;
      const viewportH = container.clientHeight;
      const viewportCenter = scrollTop + viewportH / 2;
      let closest = 'overview';
      let closestDist = Infinity;
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        let top = 0;
        let node: HTMLElement | null = el;
        while (node && node !== container) {
          top += node.offsetTop;
          node = node.offsetParent as HTMLElement | null;
        }
        const center = top + el.offsetHeight / 2;
        const dist = Math.abs(viewportCenter - center);
        if (dist < closestDist) {
          closestDist = dist;
          closest = id.replace('-mobile', '');
        }
      }
      setActiveSection(closest);
    };
    container.addEventListener('scroll', getVisibleSection, { passive: true });
    getVisibleSection();
    return () => container.removeEventListener('scroll', getVisibleSection);
  }, []);
  const showStickyHeader = activeSection !== 'overview';
  return (
    <div id="difference" className="relative w-full">
      {/* Sticky Header Container */}
      <div className="hidden md:block absolute top-0 left-0 w-full h-full pointer-events-none z-50">
        <div className="sticky top-[clamp(90px,6.3vw,115px)] w-full flex justify-center">
          <AnimatePresence>
            {showStickyHeader && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="pointer-events-auto flex flex-col items-center gap-[clamp(8px,0.93vw,16px)]"
              >
                <motion.div
                  layoutId="difference-header"
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="inline-flex items-center gap-[clamp(4px,0.46vw,8px)] px-[clamp(16px,1.39vw,24px)] py-[clamp(7px,0.58vw,10px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]"
                >
                  <motion.span layout="position" transition={{ duration: 0.5, ease: "easeOut" }}>The Voqals Advantage</motion.span>
                </motion.div>
                {/* Text-based Progress Indicator */}
                <div className="flex items-center gap-[clamp(8px,0.69vw,12px)] text-[clamp(8px,0.69vw,12px)] font-medium tracking-widest uppercase mt-[clamp(4px,0.46vw,8px)]">
                  <button
                    onClick={() => scrollToId('usp1')}
                    className={`transition-all duration-300 relative ${activeSection === 'usp1' ? 'text-[#56CFFC]' : 'text-white/30 hover:text-white/50'}`}
                  >
                    <span className="relative z-10">Authentic Code-Switching</span>
                  </button>
                  <div className="flex items-center gap-[clamp(4px,0.46vw,8px)] text-white/10">
                    <div className="w-[clamp(10px,0.93vw,16px)] h-[1px] bg-white/10" />
                  </div>
                  <button
                    onClick={() => scrollToId('usp2')}
                    className={`transition-all duration-300 relative ${activeSection === 'usp2' ? 'text-[#56CFFC]' : 'text-white/30 hover:text-white/50'}`}
                  >
                    <span className="relative z-10">The Voqals Quality Standard</span>
                  </button>
                  <div className="flex items-center gap-[clamp(4px,0.46vw,8px)] text-white/10">
                    <div className="w-[clamp(10px,0.93vw,16px)] h-[1px] bg-white/10" />
                  </div>
                  <button
                    onClick={() => {
                      const id = window.innerWidth < 768 ? 'usp3-mobile' : 'usp3';
                      scrollToId(id);
                    }}
                    className={`transition-all duration-300 relative ${activeSection === 'usp3' ? 'text-[#56CFFC]' : 'text-white/30 hover:text-white/50'}`}
                  >
                    <span className="relative z-10">Directed Performance</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      {/* Section Title & Overview */}
      <section id="overview" className="min-h-[100dvh] w-full shrink-0 relative flex flex-col items-center md:justify-center px-[var(--px-container)] pt-24 md:pt-[clamp(60px,5.5vw,96px)] pb-4 overflow-hidden">

        {/* Mobile Dynamic Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[60%] bg-gradient-to-br from-[#1B36FF]/10 to-[#56CFFC]/10 blur-[80px] rounded-full hidden pointer-events-none animate-pulse" style={{ animationDuration: '5s' }} />
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: false, amount: 0.3 }}
          className="text-center w-full z-10 mb-6 md:mb-[clamp(16px,1.85vw,32px)] flex flex-col items-center justify-center flex-1 md:flex-initial"
        >
          <div className="h-10 md:h-[clamp(28px,2.31vw,40px)] mb-6 md:mb-[clamp(16px,1.39vw,24px)] flex items-center justify-center">
            <AnimatePresence>
              {!showStickyHeader && (
                <motion.div
                  layoutId="difference-header"
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="inline-flex items-center gap-2 px-6 md:px-[clamp(16px,1.39vw,24px)] py-2.5 md:py-[clamp(7px,0.58vw,10px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-xs md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]"
                >
                  <motion.span layout="position" transition={{ duration: 0.5, ease: "easeOut" }}>The Voqals Advantage</motion.span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <h2 className="font-display text-[clamp(36px,11.87vw,52px)] md:text-[length:var(--heading-usp)] font-black tracking-tighter leading-[1.05] text-[#EEEEEE]">
            MESSY REALITY.<br className="hidden md:block" />{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">FLAWLESS DATA.</span>
          </h2>
          <p className="mt-6 md:mt-[clamp(16px,1.39vw,24px)] max-w-sm md:max-w-3xl mx-auto text-white/90 text-[length:var(--text-body)] leading-relaxed px-0">
            To build AI that truly understands India, sheer data volume isn't enough. You need context, precision, and emotion. We close the gap between how people speak and how models learn by combining the messy reality of natural code-switching with uncompromising audio quality and expertly performed and directed vocal performance.
          </p>
        </motion.div>
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: false, amount: 0.3 }}
          className="hidden md:grid md:grid-cols-3 gap-[clamp(12px,1.4vw,24px)] max-w-[var(--max-content)] w-full mx-auto z-10 overflow-x-auto snap-x snap-mandatory py-[clamp(12px,1.16vw,20px)] no-scrollbar px-[var(--px-container)]"
        >
          {[
            {
              title: "Authentic Code-Switching",
              subtitle: "Real-World Speech",
              desc: "We capture natural, complex code-switched speech so your model understands how India actually communicates, not a sanitized, single-language approximation.",
              color: "from-[#1B36FF]/20",
              id: "usp1"
            },
            {
              title: "Voqals Quality Standard",
              subtitle: "Engineered Purity",
              desc: "Our proprietary studio certification guarantees \u221260dB noise floor, under 200ms RT60, and zero electrical interference. Absolute audio purity.",
              color: "from-[#56CFFC]/20",
              id: "usp2"
            },
            {
              title: "Directed Performance",
              subtitle: "Directed Expression",
              desc: "Every session is directed by experienced Voice UI directors who extract real emotion, genuine intent, and naturalistic delivery so your model sounds like a real person.",
              color: "from-[#1B36FF]/20",
              id: "usp3"
            },
          ].map((box, i) => (
            <div key={i} className="h-auto min-h-0 w-auto shrink snap-center glass-panel p-[clamp(20px,1.85vw,32px)] rounded-[clamp(20px,1.85vw,32px)] border border-white/10 flex flex-col items-start text-left group hover:border-[#56CFFC]/50 transition-all duration-500 relative overflow-hidden hover:-translate-y-2 cursor-default" onClick={() => { if(window.innerWidth < 768) scrollToId(box.id) }}>
              <div className={`absolute inset-0 bg-gradient-to-br ${box.color} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
              <div className="flex-1 flex flex-col justify-start w-full mt-1">
                <h3 className="font-display text-[length:var(--heading-card)] font-black mb-[clamp(4px,0.46vw,8px)] text-white relative z-10 tracking-tight leading-tight">{box.title}</h3>
                <div className="text-[clamp(8px,0.69vw,12px)] font-bold uppercase tracking-widest md:mb-[clamp(8px,0.93vw,16px)] relative z-10">{box.subtitle}</div>
                <p className="hidden md:block text-white/90 text-[clamp(10px,0.81vw,14px)] leading-relaxed mb-[clamp(16px,1.85vw,32px)] relative z-10">
                  {box.desc}
                </p>
              </div>
              <div className="hidden md:block mt-auto relative z-10 w-full">
                <div className="h-px w-full bg-white/10 mb-[clamp(8px,0.93vw,16px)] group-hover:bg-white/20 transition-colors" />
                <button
                  onClick={(e) => { e.stopPropagation(); scrollToId(box.id); }}
                  className="flex items-center justify-between w-full text-[clamp(8px,0.69vw,12px)] font-bold text-white uppercase tracking-[0.2em] group/btn"
                >
                  <span>See how we do it</span>
                  <div className="w-[clamp(22px,1.85vw,32px)] h-[clamp(22px,1.85vw,32px)] rounded-full bg-white/5 flex items-center justify-center group-hover/btn:bg-[#56CFFC] group-hover/btn:text-black transition-colors">
                    <ArrowDown className="w-[clamp(11px,0.93vw,16px)] h-[clamp(11px,0.93vw,16px)] transform group-hover/btn:translate-y-1 transition-transform duration-300" />
                  </div>
                </button>
              </div>
              <div className="hidden absolute bottom-4 right-4 text-[#56CFFC]/50 group-hover:text-[#56CFFC] transition-colors">
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          ))}
        </motion.div>
        {/* Mobile spacer to push card to bottom */}
        {/* Mobile-only consolidated USP card — styled like Why India widget */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: false, amount: 0.1 }}
          className="md:hidden w-full z-10"
        >
          <div className="gradient-border rounded-2xl backdrop-blur-xl shadow-2xl relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' }}>
            <div className="px-5 py-4 flex flex-col overflow-hidden rounded-[inherit]">
              {/* Header row — mimics play bar label style */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-2 h-2 rounded-full bg-[#56CFFC] opacity-80" />
                  <span className="text-[10px] font-mono text-white/40 uppercase tracking-[0.1em]">The Voqals Advantage · 3 Pillars</span>
                </div>
              </div>
              {/* Divider */}
              <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent mb-3" />
              {/* USP list items */}
              <div className="flex flex-col">
              {[
                { title: "Authentic Code-Switching", subtitle: "Real-World Speech", id: "usp1-mobile" },
                { title: "The Voqals Quality Standard", subtitle: "Engineered Purity", id: "usp2-mobile" },
                { title: "Directed Performance", subtitle: "Directed Expression", id: "usp3-mobile" },
              ].map((item, i) => (
                <div key={i} onClick={() => scrollToId(item.id)} className={"flex items-center gap-2 py-2.5 cursor-pointer group/row"}>
                  <div className="flex-1 min-w-0 truncate">
                    <span className="font-display font-normal text-[13px] tracking-tight text-white/80">{item.title}</span>
                  </div>
                  <span className="text-[8px] font-medium uppercase tracking-widest text-white/20 shrink-0">{item.subtitle}</span>
                  <ArrowRight className="w-3 h-3 text-white/10 group-hover/row:text-[#56CFFC] group-hover/row:translate-x-0.5 transition-all shrink-0" />
                </div>
              ))}
              </div>
              {/* Divider */}
              <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent mt-1 mb-3" />
              {/* CTA row */}
              <button
                onClick={() => scrollToId('usp1-mobile')}
                className="w-full flex items-center justify-center gap-2.5 text-[10px] font-bold text-white/40 uppercase tracking-[0.2em] group/btn hover:text-[#56CFFC] transition-colors"
              >
                <span>Explore</span>
                <ArrowDown className="w-3 h-3 transform group-hover/btn:translate-y-0.5 transition-transform duration-300" />
              </button>
            </div>
          </div>
        </motion.div>
      </section>
      {/* USP 1: Mobile scroll-driven peek→fullscreen / Desktop side-by-side */}
      <Usp1MobileWrapper />
      <section id="usp1" className="hidden md:flex min-h-[100dvh] w-full shrink-0 relative items-center justify-center px-[var(--px-container)] pt-[clamp(96px,10.2vw,176px)] pb-[clamp(4px,0.46vw,8px)]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: false, amount: 0.1 }}
          className="w-full flex justify-center"
        >
          <Usp1Complexity />
        </motion.div>
      </section>
      {/* USP 2: Mobile */}
      <Usp2MobileWrapper />
      {/* USP 2: Technicality (Desktop) */}
      <section id="usp2" className="hidden md:flex min-h-screen w-full shrink-0 relative items-center justify-center px-[var(--px-container)] pt-[clamp(96px,10.2vw,176px)] pb-[clamp(4px,0.46vw,8px)] overflow-hidden">
        <div className="w-full flex justify-center">
          <Usp2Technicality />
        </div>
        {/* Swipe-in Studio Certification CTA */}
        <motion.div 
          initial={{ opacity: 0, x: 100, rotate: 5 }}
          whileInView={{ opacity: 1, x: 0, rotate: 0 }}
          transition={{ delay: 0.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: false, amount: 0.5 }}
          className="hidden md:block absolute bottom-8 right-8 z-50"
        >
          <button onClick={onOpenStudioForm} className="flex items-center gap-4 p-4 rounded-2xl bg-black/60 border border-[#56CFFC]/30 backdrop-blur-xl group hover:bg-black/80 hover:border-[#56CFFC] transition-all duration-300 shadow-[0_10px_40px_rgba(0,0,0,0.5)] text-left">
            <div className="w-12 h-12 rounded-full bg-[#56CFFC]/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-[0_0_15px_rgba(86,207,252,0.3)]">
              <Mic2 className="w-6 h-6 text-[#56CFFC]" />
            </div>
            <div className="pr-2">
              <p className="text-[10px] uppercase tracking-widest font-bold mb-1">Are you a studio?</p>
              <p className="text-sm text-white font-medium flex items-center gap-2 group-hover:text-[#56CFFC] transition-colors">
                Get Voqals Certified
                <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
              </p>
            </div>
          </button>
        </motion.div>
      </section>
      {/* USP 3: Mobile scroll-driven widget */}
      <Usp3MobileWrapper />
      {/* USP 3: Creativity (Desktop) */}
      <section id="usp3" className="hidden md:flex min-h-screen w-full shrink-0 relative items-center justify-center px-[var(--px-container)] pt-[clamp(96px,10.2vw,176px)] pb-[clamp(4px,0.46vw,8px)] overflow-hidden">
        <div className="w-full flex justify-center">
          <Usp3Creativity onOpenTalentForm={onOpenTalentForm} />
        </div>
      </section>
    </div>
  );
};
const CustomDataCollection = ({ onStartProject }: { onStartProject: () => void }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
  };
  return (
    <div className="max-w-[var(--max-content)] w-full mx-auto flex-1 flex flex-col items-center text-center z-10 relative">
      {/* Background Glow */}
      <motion.div
        animate={{ scale: [1, 1.05, 1], opacity: [0.4, 0.6, 0.4] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[500px] bg-[#1B36FF]/15 blur-[150px] rounded-full pointer-events-none"
      />
      {/* Content wrapper - vertically centered */}
      <div className="flex-1 flex flex-col items-center justify-center w-full">
      {/* Top Label */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: false, amount: 0.3 }}
        className="mb-6 md:mb-[clamp(24px,2.31vw,40px)]"
      >
        <div className="inline-flex items-center gap-2 px-5 py-2 md:gap-[clamp(4px,0.46vw,8px)] md:px-[clamp(16px,1.39vw,24px)] md:py-[clamp(7px,0.58vw,10px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
          Custom Data Collection
        </div>
      </motion.div>
      {/* Headlines */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: false, amount: 0.3 }}
        className="mb-8 md:mb-[clamp(22px,1.85vw,32px)] relative flex flex-col items-center"
      >
        <h2 className="font-display text-[clamp(28px,9.2vw,56px)] md:text-[length:var(--heading-hero)] font-black tracking-tighter leading-[1.05] uppercase">
          INDIAN SPEECH DATA.<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">AS A SERVICE.</span>
        </h2>
      </motion.div>
      {/* Body Copy */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: false, amount: 0.3 }}
        className="text-white/90 text-[length:var(--text-body)] max-w-sm md:max-w-[clamp(540px,46.3vw,960px)] mx-auto leading-relaxed mb-8 md:mb-[clamp(32px,2.78vw,48px)]"
      >
        Need data that doesn't exist yet? We build it. You define the use case, the languages, the personas, the emotional range, and the volume. We design the collection strategy, source the right talent, run certified recording sessions, handle post-production and annotation, and deliver structured, SFT-ready files to your exact schema.
      </motion.p>
      {/* CTA Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: false, amount: 0.3 }}
        className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-[clamp(14px,1.16vw,20px)] w-full md:w-auto mb-12 md:mb-[clamp(32px,2.78vw,48px)]"
      >
        <Link
          to="/custom-data-collection/"
          className="group relative w-full sm:w-auto px-[clamp(28px,2.31vw,40px)] py-[clamp(14px,1.16vw,20px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(10px,0.69vw,12px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden hover:scale-105 hover:shadow-[0_0_30px_rgba(27,54,255,0.3)] whitespace-nowrap"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
          <span className="relative z-10 flex items-center gap-2">
            Explore Custom Data Collection
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </Link>
        <button onClick={onStartProject} className="group relative w-full sm:w-auto px-[clamp(28px,2.31vw,40px)] py-[clamp(14px,1.16vw,20px)] rounded-full border border-white/20 text-white text-[clamp(10px,0.69vw,12px)] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all hover:bg-white/5 hover:text-white hover:border-white/40 flex items-center justify-center whitespace-nowrap">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
          <span className="relative z-10 flex items-center gap-2">
            Start a project
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </button>
      </motion.div>
      </div>
      {/* Pipeline Services — Mobile: marquee, Desktop: static */}
      <div className="md:hidden relative z-20 w-full pt-3 pb-6 overflow-hidden">
        <div className="flex w-max animate-marquee">
          {[1, 2].map((set) => (
            <div key={set} className="flex items-center">
              {["Scenario Engineering", "Precision Casting", "Certified Recording", "Post-Production & QA", "Structured Delivery"].map((service, index) => (
                <div key={`${set}-${index}`} className="flex items-center group px-8">
                  <span className="text-[10px] font-display font-medium text-white/90 uppercase tracking-[0.3em] group-hover:text-white transition-colors duration-500 cursor-default">
                    {service}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="hidden md:flex relative z-20 w-full max-w-[var(--max-content)] mx-auto justify-between items-center pt-[clamp(11px,0.93vw,16px)] pb-[clamp(22px,1.85vw,32px)] px-[var(--px-container)]">
        {["Scenario Engineering", "Precision Casting", "Certified Recording", "Post-Production & QA", "Structured Delivery"].map((service, index) => (
          <React.Fragment key={index}>
            {index > 0 && <div className="w-1 h-1 rounded-full bg-white/20 shrink-0" />}
            <span className="text-[clamp(8px,0.69vw,12px)] font-display font-medium text-white/40 uppercase tracking-[0.4em] hover:text-white/70 transition-colors duration-500 cursor-default whitespace-nowrap">
              {service}
            </span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
const DesktopPipelineAccordion = ({ steps, onStartProject }: { steps: { title: string; subtitle: string; pointers: string[]; tags: string[]; icon: React.ReactNode }[]; onStartProject: () => void }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  return (
    <div className="hidden md:flex items-stretch max-w-[var(--max-content)] w-full mx-auto px-[var(--px-container)] gap-[var(--gap-large)]">
      {/* Left Side — Pill + Heading + CTA */}
      <div className="w-[40%] flex flex-col shrink-0 text-left">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: false, amount: 0.3 }}
          className="flex flex-col items-start h-full justify-center"
        >
          {/* Pill */}
          <div className="mb-[clamp(16px,1.39vw,24px)] inline-flex items-center gap-[clamp(4px,0.46vw,8px)] px-[clamp(16px,1.39vw,24px)] py-[clamp(7px,0.58vw,10px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)] w-fit">
            Custom Data Collection
          </div>
          {/* Heading + Body + CTA */}
          <div className="flex flex-col items-start">
            <h2 className="font-display text-[length:var(--heading-pipeline)] font-black text-white leading-[1.1] tracking-tighter uppercase mb-[clamp(16px,1.39vw,24px)]">
              DATA COLLECTION <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">PIPELINE.</span>
            </h2>
            <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed max-w-[clamp(303px,25.93vw,448px)] mb-[clamp(27px,2.31vw,40px)]">
              Tell us what your model needs to master. We engineer the execution. Our fully managed pipeline handles the entire complexity of custom data creation—from the blank page to the flawlessly structured JSON.
            </p>
            <button onClick={onStartProject} className="group relative px-[clamp(22px,1.85vw,32px)] py-[clamp(11px,0.93vw,16px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(8px,0.69vw,12px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-[clamp(8px,0.69vw,12px)] overflow-hidden shadow-[0_0_20px_rgba(27,54,255,0.3)] hover:shadow-[0_0_30px_rgba(86,207,252,0.4)] hover:scale-[1.02] w-fit">
              <span className="relative z-10 flex items-center gap-[clamp(5px,0.46vw,8px)]">
                Start Your Project
                <ArrowRight className="w-[clamp(11px,0.93vw,16px)] h-[clamp(11px,0.93vw,16px)] group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
      {/* Right Side — Dynamic Accordion */}
      <div className="w-[60%] flex flex-col justify-center gap-[clamp(4px,0.35vw,6px)] relative">
        {steps.map((step, index) => {
          const isActive = index === activeIndex;
          return (
            <div
              key={step.title}
              onClick={() => setActiveIndex(index)}
              className="cursor-pointer relative rounded-[clamp(11px,0.93vw,16px)] overflow-hidden"
              style={{
                transform: isActive ? 'scale(1)' : 'scale(0.97)',
                opacity: isActive ? 1 : 0.7,
                background: isActive ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.03)',
                border: isActive ? '1px solid rgba(86,207,252,0.2)' : '1px solid rgba(255,255,255,0.08)',
                boxShadow: isActive ? '0 0 50px rgba(86,207,252,0.1), 0 0 100px rgba(27,54,255,0.05), inset 0 1px 0 rgba(255,255,255,0.08)' : 'none',
                backdropFilter: 'blur(16px)',
                transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            >
              {/* Active glow accent — left bar */}
              <div
                className="absolute left-0 top-[10%] bottom-[10%] w-[2px] origin-center bg-gradient-to-b from-[#56CFFC] via-[#56CFFC] to-[#1B36FF] rounded-full"
                style={{
                  transform: isActive ? 'scaleY(1)' : 'scaleY(0)',
                  opacity: isActive ? 1 : 0,
                  boxShadow: isActive ? '0 0 12px rgba(86,207,252,0.5)' : 'none',
                  transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              />
              {/* Active background shimmer */}
              {isActive && (
                <div className="absolute inset-0 bg-gradient-to-r from-[#56CFFC]/[0.03] via-transparent to-[#1B36FF]/[0.03] pointer-events-none" />
              )}
              {/* Card Header */}
              <div className={`flex items-center gap-[clamp(11px,0.93vw,16px)] px-[clamp(19px,1.62vw,28px)] ${isActive ? 'py-[clamp(11px,0.93vw,16px)]' : 'py-[clamp(7px,0.58vw,10px)]'} transition-all duration-300`}>
                {/* Icon */}
                <div
                  className="w-[clamp(27px,2.31vw,40px)] h-[clamp(27px,2.31vw,40px)] rounded-[clamp(8px,0.69vw,12px)] flex items-center justify-center shrink-0"
                  style={{
                    transform: isActive ? 'scale(1) rotate(0deg)' : 'scale(0.9) rotate(-5deg)',
                    background: isActive ? 'linear-gradient(135deg, rgba(27,54,255,0.3), rgba(86,207,252,0.1))' : 'rgba(255,255,255,0.04)',
                    border: isActive ? '1px solid rgba(86,207,252,0.4)' : '1px solid rgba(255,255,255,0.06)',
                    boxShadow: isActive ? '0 0 20px rgba(86,207,252,0.2)' : 'none',
                    transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                >
                  <div className="scale-[0.8]">{step.icon}</div>
                </div>
                {/* Title */}
                <h3
                  className="flex-1 min-w-0 font-black uppercase tracking-wide"
                  style={{
                    color: isActive ? '#fff' : 'rgba(255,255,255,0.6)',
                    fontSize: isActive ? 'clamp(10px,0.87vw,15px)' : 'clamp(9px,0.81vw,14px)',
                    transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                >
                  {step.title}
                </h3>
                {/* Expand indicator */}
                <div
                  className="w-[clamp(19px,1.62vw,28px)] h-[clamp(19px,1.62vw,28px)] rounded-full flex items-center justify-center shrink-0"
                  style={{
                    transform: isActive ? 'rotate(180deg) scale(1.1)' : 'rotate(0deg) scale(0.8)',
                    background: isActive ? 'rgba(86,207,252,0.15)' : 'rgba(255,255,255,0.03)',
                    border: isActive ? '1px solid rgba(86,207,252,0.3)' : '1px solid rgba(255,255,255,0.05)',
                    transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                >
                  <ChevronDown className="w-[clamp(9px,0.81vw,14px)] h-[clamp(9px,0.81vw,14px)]" style={{ color: isActive ? '#56CFFC' : 'rgba(255,255,255,0.2)', transition: 'color 0.5s' }} />
                </div>
              </div>
              {/* Expandable Body */}
              {isActive && (
                <div className="px-[clamp(19px,1.62vw,28px)] pb-[clamp(14px,1.16vw,20px)] pt-0 ml-[clamp(38px,3.24vw,56px)] max-h-[30vh] overflow-y-auto no-scrollbar">
                  {/* Divider line */}
                  <div className="w-[clamp(32px,2.78vw,48px)] h-px bg-gradient-to-r from-[#56CFFC]/40 to-transparent mb-[clamp(8px,0.69vw,12px)]" />
                  {/* Subtitle */}
                  <p className="text-[#56CFFC]/90 text-[clamp(8px,0.69vw,12px)] font-bold mb-[clamp(11px,0.93vw,16px)] tracking-wider leading-relaxed">{step.subtitle}</p>
                  {/* Pointer list */}
                  <ul className="space-y-[clamp(5px,0.46vw,8px)] mb-[clamp(11px,0.93vw,16px)]">
                    {step.pointers.map((pointer, i) => (
                      <li key={i} className="flex items-start gap-[clamp(8px,0.69vw,12px)] text-[clamp(9px,0.75vw,13px)] leading-relaxed font-medium text-[#A4BDE5]/80">
                        <div className="w-[clamp(4px,0.35vw,6px)] h-[clamp(4px,0.35vw,6px)] rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] mt-[clamp(4px,0.35vw,6px)] shrink-0 shadow-[0_0_6px_rgba(86,207,252,0.4)]" />
                        {pointer}
                      </li>
                    ))}
                  </ul>
                  {/* Tags */}
                  <div className="flex flex-wrap gap-[clamp(4px,0.35vw,6px)]">
                    {step.tags.map((tag, i) => (
                      <span key={i} className="px-[clamp(8px,0.69vw,12px)] py-[clamp(3px,0.23vw,4px)] rounded-full bg-[#56CFFC]/[0.08] border border-[#56CFFC]/20 text-[clamp(7px,0.58vw,10px)] uppercase tracking-[0.15em] font-bold text-[#56CFFC]/80 shadow-[0_0_10px_rgba(86,207,252,0.05)]">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
const DataCollectionPipeline = ({ onStartProject, isProjectOpen, onProjectClose }: { onStartProject: () => void; isProjectOpen?: boolean; onProjectClose?: () => void }) => {
  const steps = [
    {
      title: "SCENARIO ENGINEERING",
      subtitle: "Designing scripts and conversational scenarios for real-world use cases.",
      pointers: [
        "Design scripts, prompts, and conversational scenarios",
        "Build around real-world use cases, not synthetic approximations",
        "Engineer speech patterns your model needs",
        "Map phonetic and emotional boundaries"
      ],
      tags: ["Linguistics", "Context Design", "Prompting"],
      icon: <FileText className="w-6 h-6 text-[#56CFFC]" />
    },
    {
      title: "PRECISION CASTING",
      subtitle: "Talent cast to match your exact persona requirements.",
      pointers: [
        "Cast voice talent that matches your defined persona profiles",
        "Age, region, dialect, register, and speaking style — all specified and verified",
        "Multilingual and code-switching speakers sourced on demand",
        "No mic goes live without demographic and profile verification"
      ],
      tags: ["Persona Matching", "Talent Sourcing", "Dialects"],
      icon: <Mic2 className="w-6 h-6 text-[#56CFFC]" />
    },
    {
      title: "CERTIFIED RECORDING",
      subtitle: "Directed sessions in Voqals Quality Standard-certified studios.",
      pointers: [
        "Voqals Quality Standard-certified studios only",
        "Every session directed by specialised Voice UI directors",
        "Directed for expressiveness, intent, and naturalistic delivery",
        "Real-time monitoring ensures every take meets spec before moving on"
      ],
      tags: ["Acoustics", "Studio Grade", "Voice UI Direction"],
      icon: <Speaker className="w-6 h-6 text-[#56CFFC]" />
    },
    {
      title: "POST-PRODUCTION & QA",
      subtitle: "Cleaned, mastered, and validated to perfection.",
      pointers: [
        "Artifacts like mouth clicks, pops, and breaths cleaned up",
        "Audio mastered for uniform loudness — every token sounds the same",
        "Linguistic QA: accuracy, naturalness, and intent alignment verified",
        "Anything that doesn't pass gets re-recorded, not patched"
      ],
      tags: ["Mastering", "Quality Assurance", "Artifact Removal"],
      icon: <Sliders className="w-6 h-6 text-[#56CFFC]" />
    },
    {
      title: "STRUCTURED DELIVERY",
      subtitle: "Files delivered in your required format, annotated to your schema.",
      pointers: [
        "Delivered in your required format with full metadata",
        "Speaker ID, language, intent, emotion, persona, and action tags",
        "Annotated to your exact schema specifications",
        "Ready to load into your training pipeline immediately"
      ],
      tags: ["Metadata", "SFT-Ready", "Integration"],
      icon: <Database className="w-6 h-6 text-[#56CFFC]" />
    }
  ];
  return (
    <>
      {/* Mobile Layout */}
      <div className="md:hidden w-full flex-1 flex flex-col items-center text-center justify-center overflow-hidden relative">
        {/* Top Section - Pill, Heading, Body */}
        <div className="px-5">
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
              Custom Data Collection
            </div>
          </div>
          <h2 className="font-display text-[clamp(32px,10.67vw,48px)] font-black text-white leading-[1.1] tracking-tighter uppercase mb-6">
            DATA COLLECTION<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">PIPELINE.</span>
          </h2>
          <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed max-w-sm mx-auto mb-7">
            Tell us what your model needs to master. We engineer the execution. Our fully managed pipeline handles the entire complexity of custom data creation—from the blank page to the flawlessly structured JSON.
          </p>
        </div>
        {/* Horizontal Carousel */}
        <div
          className="w-full relative"
          style={{
            maskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
            WebkitMaskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
          }}
        >
          <div className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar gap-3 px-5" style={{ scrollPaddingInline: "20px" }}>
            {steps.map((step, index) => (
              <div
                key={index}
                className="w-[85%] min-w-[85%] shrink-0 snap-center rounded-2xl bg-black/40 border border-white/10 backdrop-blur-md p-4 text-left"
              >
                <h3 className="text-white text-[13px] font-black uppercase tracking-tight mb-0.5">
                  {step.title}
                </h3>
                <h4 className="text-[#56CFFC] text-[11px] font-bold mb-3 tracking-wider">
                  {step.subtitle}
                </h4>
                <ul className="space-y-1.5 mb-3">
                  {step.pointers.map((pointer, i) => (
                    <li key={i} className="flex items-start gap-2 text-[11px] text-white/90 leading-snug font-medium">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#56CFFC] mt-1 shrink-0 opacity-60" />
                      {pointer}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-1.5">
                  {step.tags.map((tag, i) => (
                    <span key={i} className="px-2.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-[8px] uppercase tracking-widest font-bold text-white/60">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* CTA */}
        <div className="px-5 w-full">
          <button onClick={onStartProject} className="group relative w-full mt-5 mb-3 py-3.5 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[11px] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden shadow-[0_0_20px_rgba(27,54,255,0.3)]">
            <span className="relative z-10 flex items-center gap-2">
              Start Your Project
              <ArrowRight className="w-4 h-4" />
            </span>
          </button>
        </div>
      </div>
      {/* Desktop Layout — Accordion */}
      <DesktopPipelineAccordion steps={steps} onStartProject={onStartProject} />
    </>
  );
};
const datasets = [
  {
    id: 'ds-01',
    name: 'Conversational Assistant',
    category: 'Personas',
    languages: ['Hindi', 'English', 'Hinglish'],
    speakers: 8,
    status: 'In Pre-Production' as const,
    description: 'Natural multi-turn dialogue for building voice assistants that handle real conversational flows.',
  },
  {
    id: 'ds-02',
    name: 'Customer Support',
    category: 'Personas',
    languages: ['Tamil', 'Telugu', 'English'],
    speakers: 6,
    status: 'In Pre-Production' as const,
    description: 'Complaint resolution, escalation, de-escalation, and empathetic agent responses.',
  },
  {
    id: 'ds-03',
    name: 'Film Characters',
    category: 'Personas',
    languages: ['Hindi', 'Marathi'],
    speakers: 6,
    status: 'In Pre-Production' as const,
    description: 'Character-driven, emotionally heightened speech for TTS and voice synthesis.',
  },
  {
    id: 'ds-04',
    name: 'General Population',
    category: 'Personas',
    languages: ['Hindi', 'Bengali', 'Gujarati'],
    speakers: 20,
    status: 'In Pre-Production' as const,
    description: 'Broad-coverage foundational corpus across age groups, regions, and registers.',
  },
  {
    id: 'ds-05',
    name: 'Overlap & Interruption',
    category: 'Training Use Case',
    languages: ['Hinglish', 'Hindi'],
    speakers: 8,
    status: 'In Pre-Production' as const,
    description: 'Real-world conversational dynamics: turn-stealing, overlapping speech, false starts.',
  },
  {
    id: 'ds-06',
    name: 'Emotional Spectrum',
    category: 'Training Use Case',
    languages: ['Hindi', 'English'],
    speakers: 10,
    status: 'In Pre-Production' as const,
    description: 'Full emotional range: joy, frustration, anxiety, sarcasm, urgency, calm.',
  },
  {
    id: 'ds-07',
    name: 'Foundational Monologues',
    category: 'Training Use Case',
    languages: ['Hindi', 'Tamil', 'Telugu', 'Malayalam'],
    speakers: 12,
    status: 'In Pre-Production' as const,
    description: 'Long-form solo speech for foundational pre-training. Diverse speaker profiles.',
  },
  {
    id: 'ds-08',
    name: 'Code-Switching Mastery',
    category: 'Training Use Case',
    languages: ['Hinglish', 'Tanglish'],
    speakers: 8,
    status: 'In Pre-Production' as const,
    description: 'The definitive dataset for training models on natural code-switching.',
  },
];
const ReadyToUseDatasets = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [datasetEnquiryOpen, setDatasetEnquiryOpen] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);
  const deliverablesRef = useRef<HTMLDivElement>(null);
  const mobileSectionRef = useRef<HTMLDivElement>(null);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyEmailError, setNotifyEmailError] = useState('');
  const [notifySubmitting, setNotifySubmitting] = useState(false);
  const [notifyDone, setNotifyDone] = useState(false);
  const FREE_DOMAINS = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com'];
  const validateNotifyEmail = (val: string) => {
    setNotifyEmail(val);
    if (!val) { setNotifyEmailError(''); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) { setNotifyEmailError('Please enter a valid email.'); return; }
    const domain = val.split('@')[1]?.toLowerCase();
    if (domain && FREE_DOMAINS.includes(domain)) { setNotifyEmailError('Please use your work email.'); return; }
    setNotifyEmailError('');
  };
  const handleNotify = async () => {
    if (!notifyEmail || notifyEmailError || notifySubmitting) return;
    setNotifySubmitting(true);
    try {
      await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: 'd3cbc6a2-6428-46cc-83a8-ee15a7f434ae',
          subject: 'Dataset Notify — ' + notifyEmail,
          from_name: 'Voqals Dataset Notify',
          replyto: notifyEmail,
          Email: notifyEmail,
          'Form Type': 'Dataset Launch Notification',
        }),
      });
      setNotifyDone(true);
    } catch { setNotifyDone(true); }
    finally { setNotifySubmitting(false); }
  };
  const mobileScrollProgress = useMotionValue(0);
  const mobileTabRef = useRef<HTMLDivElement>(null);
  const [mobileTabIndicator, setMobileTabIndicator] = useState({ left: 0, width: 0 });
  const filteredDatasets = activeFilter === 'All'
    ? datasets
    : datasets.filter(d => d.category === activeFilter);
  // Mobile scroll-driven widget expansion (manual, matching USP pattern)
  useEffect(() => {
    const container = document.getElementById('main-scroll-container');
    const section = mobileSectionRef.current;
    if (!container || !section) return;
    const handleScroll = () => {
      let sectionTop = 0;
      let el: HTMLElement | null = section;
      while (el && el !== container) {
        sectionTop += el.offsetTop;
        el = el.offsetParent as HTMLElement | null;
      }
      const sectionHeight = section.offsetHeight;
      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;
      const start = sectionTop;
      const end = sectionTop + sectionHeight - containerHeight;
      const progress = Math.max(0, Math.min(1, (scrollTop - start) / (end - start)));
      mobileScrollProgress.set(progress);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  // ── Scroll-driven animation timeline ──
  // Heading fades slowly as widget covers it
  // Widget grows from 25% peek to fullscreen
  const mobileHeadingOpacity = useTransform(mobileScrollProgress, [0.15, 0.7], [1, 0]);
  // Heading area shrinks from 70% to 0 as widget grows
  const mobileHeadingHeight = useTransform(mobileScrollProgress, (v) => {
    const p = Math.min(1, Math.max(0, v / 0.8));
    const vh = window.innerHeight;
    const startH = vh * 0.72;
    const endH = 0;
    return startH + (endH - startH) * p;
  });
  // Widget peek is tall enough that CTAs fall below viewport edge
  // heading min-h keeps hero content from collapsing too small initially
  const mobileWidgetHeight = useTransform(mobileScrollProgress, (v) => {
    const p = Math.min(1, Math.max(0, v / 0.8));
    const vh = window.innerHeight;
    const startH = vh * 0.28;
    const endH = vh - 120;
    return startH + (endH - startH) * p;
  });
  const mobileWidgetRadius = useTransform(mobileScrollProgress, [0.1, 0.6], [24, 16]);
  const mobileWidgetMarginLR = useTransform(mobileScrollProgress, [0, 0.35], [24, 0]);
  const mobileWidgetMarginB = useTransform(mobileScrollProgress, [0.1, 0.6], [16, 0]);
  const statusColor = (s: string) => s === 'Ready' ? 'text-emerald-400' : s === 'Production' ? 'text-amber-400' : 'text-amber-400/70';
  const statusDot = (s: string) => s === 'Ready' ? 'bg-emerald-400' : s === 'Production' ? 'bg-amber-400' : 'bg-amber-400/70';
  // Sliding tab indicator (matching USP3 pattern)
  const filterTabs = ['All', 'Personas', 'Training'];
  useEffect(() => {
    const container = mobileTabRef.current;
    if (!container) return;
    const buttons = container.querySelectorAll('button');
    const tabIndex = filterTabs.findIndex(f => (f === 'Training' ? 'Training Use Case' : f) === activeFilter);
    const btn = buttons[tabIndex >= 0 ? tabIndex : 0];
    if (btn) {
      setMobileTabIndicator({ left: (btn as HTMLElement).offsetLeft, width: (btn as HTMLElement).offsetWidth });
    }
  }, [activeFilter]);
  return (
    <>
      {/* Mobile Layout — 200dvh outer for scroll distance, sticky viewport inside */}
      <div ref={mobileSectionRef} id="datasets" className="md:hidden h-[200dvh] w-full relative shrink-0">
        <div className="sticky top-0 h-[100dvh] flex flex-col overflow-hidden pt-20">
          {/* Background Glow */}
          <motion.div
            animate={{ scale: [1, 1.05, 1], opacity: [0.4, 0.6, 0.4] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#56CFFC]/10 blur-[150px] rounded-full pointer-events-none z-0"
          />
          {/* Hero Content — fades out and shrinks on scroll */}
          <motion.div style={{ opacity: mobileHeadingOpacity, height: mobileHeadingHeight }} className="shrink-0 flex flex-col items-center justify-center text-center px-5 z-10 overflow-hidden">
            {/* Pill */}
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
                Ready To Use Datasets
              </div>
            </div>
            {/* Heading */}
            <h2 className="font-display text-[clamp(24px,8vw,48px)] md:text-[length:var(--heading-hero)] font-black text-white leading-[1.05] tracking-tighter uppercase mb-1">
              PRODUCTION DATASETS.
            </h2>
            <h3 className="font-display text-[clamp(24px,8vw,48px)] md:text-[length:var(--heading-hero)] font-black text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic leading-[1.05] tracking-tighter uppercase mb-6">
              LAUNCHING SOON.
            </h3>
            {/* Body */}
            <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed max-w-sm mx-auto mb-7">
              Skip the custom collection pipeline. We're packaging our first wave of production-ready Indian speech datasets. Fully annotated, certified to the Voqals Quality Standard, and ready to license so you can start training your models immediately.
            </p>
            {/* Email Capture */}
            <div className="w-full mb-5">
              <p className="text-[9px] uppercase tracking-[0.12em] font-bold text-white/40 mb-2 text-center">{notifyDone ? 'You\'re on the list!' : 'Be the first to access them when they go live.'}</p>
              {!notifyDone ? (
              <div className="relative">
                <input
                  type="email"
                  value={notifyEmail}
                  onChange={e => validateNotifyEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleNotify()}
                  placeholder="Enter your work email..."
                  className={`w-full bg-white/5 border rounded-full py-2.5 pl-4 pr-32 text-white placeholder:text-gray-600 focus:outline-none transition-all text-[13px] ${notifyEmailError ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-[#56CFFC]/50 focus:bg-white/10'}`}
                />
                <button onClick={handleNotify} disabled={notifySubmitting || !notifyEmail || !!notifyEmailError} className="absolute right-1 top-1/2 -translate-y-1/2 px-3.5 py-1.5 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[8px] tracking-[0.12em] uppercase shadow-[0_0_15px_rgba(27,54,255,0.3)] disabled:opacity-40">
                  {notifySubmitting ? '...' : 'Notify Me'}
                </button>
              </div>
              ) : (
              <div className="flex items-center justify-center gap-2 py-2.5">
                <Check className="w-4 h-4 text-[#56CFFC]" />
                <span className="text-white/70 text-[13px]">We'll notify you at launch.</span>
              </div>
              )}
              {notifyEmailError && <p className="text-[8px] text-red-400/80 mt-1 text-center">{notifyEmailError}</p>}
              <p className="text-[8px] text-white/25 mt-1.5 text-center">No spam. Just a single email when datasets are available.</p>
            </div>
            {/* Included With Every Dataset */}
            <div className="w-full">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-white/10"></div>
                <span className="text-[7px] uppercase tracking-[0.2em] font-bold whitespace-nowrap text-white/50">Included With Every Dataset</span>
                <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-white/10"></div>
              </div>
              <div className="grid grid-cols-3 gap-x-2 gap-y-2">
                {[
                  { title: "CLEAN MIX", desc: "Studio Baseline" },
                  { title: "CHAOS MIX", desc: "Simulated Noise" },
                  { title: "ISO TRACKS", desc: "For Overlaps" },
                  { title: "NEAR FIELD", desc: "Intimate Distance" },
                  { title: "FAR FIELD", desc: "Env. Distance" },
                  { title: "METADATA", desc: "Dense JSON" }
                ].map((feature, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <span className="text-[10px] font-display font-bold text-white uppercase tracking-[0.08em] mb-0.5">
                      {feature.title}
                    </span>
                    <span className="text-[7px] uppercase tracking-widest text-[#A4BDE5]/50">
                      {feature.desc}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
          {/* Widget — grows from peek to fullscreen */}
          <motion.div
            ref={widgetRef}
            style={{
              height: mobileWidgetHeight,
              borderRadius: mobileWidgetRadius,
              marginLeft: mobileWidgetMarginLR,
              marginRight: mobileWidgetMarginLR,
              marginBottom: mobileWidgetMarginB,
              background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.02) 100%), rgba(8,8,12,0.75)',
            }}
            className="gradient-border shrink-0 pointer-events-auto backdrop-blur-xl shadow-2xl"
          >
            <div className="w-full h-full flex flex-col relative overflow-hidden">
              {/* Top accent line */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent z-20" />
              {/* Header with title + sliding tab toggle */}
              <div className="px-5 pt-4 pb-3 shrink-0 relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <Database className="w-4 h-4 text-[#56CFFC]" />
                    <h4 className="text-white font-bold tracking-[0.2em] uppercase text-[10px]">Production Datasets</h4>
                  </div>
                  <span className="text-[9px] text-[#A4BDE5]/30 font-mono">{filteredDatasets.length} results</span>
                </div>
                {/* Sliding Tab Toggle — USP3 style */}
                <div ref={mobileTabRef} className="bg-white/[0.04] p-1 rounded-full flex items-center relative border border-white/[0.06]">
                  <motion.div
                    className="absolute top-1 bottom-1 rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] shadow-[0_0_20px_rgba(27,54,255,0.3)]"
                    animate={{ left: mobileTabIndicator.left, width: mobileTabIndicator.width }}
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                  {filterTabs.map(filter => (
                    <button
                      key={filter}
                      onClick={() => setActiveFilter(filter === 'Training' ? 'Training Use Case' : filter)}
                      className={`relative z-10 flex-1 py-2 rounded-full text-[9px] font-bold tracking-[0.15em] uppercase transition-colors duration-300 ${
                        (filter === 'Training' ? 'Training Use Case' : filter) === activeFilter
                          ? 'text-white'
                          : 'text-white/35 hover:text-white/55'
                      }`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>
              {/* Column Headers */}
              <div className="flex items-center px-5 py-2 border-t border-b border-white/[0.04] shrink-0">
                <span className="flex-1 text-[8px] uppercase tracking-[0.2em] text-[#A4BDE5]/30 font-semibold">Dataset</span>
                <span className="w-12 text-center text-[8px] uppercase tracking-[0.2em] text-[#A4BDE5]/30 font-semibold">Spkrs</span>
                <span className="w-10 text-center text-[8px] uppercase tracking-[0.2em] text-[#A4BDE5]/30 font-semibold">Lang</span>
                <span className="w-24 text-right text-[8px] uppercase tracking-[0.2em] text-[#A4BDE5]/30 font-semibold">Status</span>
              </div>
              {/* Dataset Rows */}
              <div className="flex-1 overflow-hidden">
                <AnimatePresence mode="popLayout">
                  {filteredDatasets.map((ds, i) => (
                    <motion.div
                      key={ds.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.97 }}
                      transition={{ delay: i * 0.03, duration: 0.25 }}
                      className="flex items-center px-5 py-3.5 border-b border-white/[0.04] active:bg-white/[0.03] transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <span className="text-white/90 text-[13px] font-medium truncate block">{ds.name}</span>
                      </div>
                      <div className="w-12 text-center">
                        <span className="text-[#56CFFC]/80 text-[12px] font-bold font-mono">{ds.speakers}</span>
                      </div>
                      <div className="w-10 text-center">
                        <span className="text-[#A4BDE5]/60 text-[12px] font-bold font-mono">{ds.languages.length}</span>
                      </div>
                      <div className="w-24 flex items-center justify-end gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${statusDot(ds.status)}`}></span>
                        <span className={`text-[9px] font-medium tracking-wide ${statusColor(ds.status)}`}>{ds.status}</span>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
              {/* Action Buttons */}
              <div className="px-5 pt-4 pb-7 border-t border-white/[0.06] shrink-0">
                <button onClick={() => setDatasetEnquiryOpen(true)} className="w-full py-3 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[9px] tracking-[0.2em] uppercase text-center shadow-[0_0_25px_rgba(27,54,255,0.25)]">
                  Enquire About Datasets
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      {/* Desktop Layout */}
    <section id="datasets-dt" className="hidden md:flex min-h-screen w-full shrink-0 relative flex-col items-center justify-start px-[var(--px-container)] pt-[clamp(60px,5.09vw,88px)] pb-[clamp(22px,1.85vw,32px)]">
    <div className="max-w-[var(--max-content)] w-full mx-auto flex-1 flex flex-col items-center text-center z-10 relative">
      {/* Background Glow */}
      <motion.div
        animate={{ scale: [1, 1.05, 1], opacity: [0.4, 0.6, 0.4] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[500px] bg-[#56CFFC]/10 blur-[150px] rounded-full pointer-events-none"
      />
      {/* Top Content Container */}
      <div className="w-full flex flex-col items-center pt-[clamp(27px,2.31vw,40px)] pb-0 shrink-0">
        
        {/* Centered Content Wrapper */}
        <div className="flex-1 flex flex-col items-center justify-center w-full mt-0">
          {/* Top Label */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false, amount: 0.3 }}
            className="mb-[clamp(27px,2.31vw,40px)] shrink-0"
          >
            <div className="inline-flex items-center gap-[clamp(4px,0.46vw,8px)] px-[clamp(16px,1.39vw,24px)] py-[clamp(7px,0.58vw,10px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
              Ready To Use Datasets
            </div>
          </motion.div>
          {/* Headlines */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false, amount: 0.3 }}
            className="mb-[clamp(27px,2.31vw,40px)] relative flex flex-col items-center shrink-0"
          >
            <h2 className="font-display text-[length:var(--heading-datasets)] font-black text-white leading-[0.85] tracking-tighter uppercase text-center">
              PRODUCTION DATASETS.
            </h2>
            <h3 className="font-display text-[length:var(--heading-datasets)] font-black text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic leading-[0.85] tracking-tighter uppercase mt-[clamp(8px,0.69vw,12px)] text-center">
              LAUNCHING SOON.
            </h3>
          </motion.div>
          {/* Body Copy */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false, amount: 0.3 }}
            className="text-white/90 text-[length:var(--text-body)] max-w-[clamp(520px,45vw,780px)] mx-auto leading-relaxed shrink-0 text-center mb-[clamp(27px,2.31vw,40px)] px-[clamp(11px,0.93vw,16px)]"
          >
            Skip the custom collection pipeline. We're packaging our first wave of production-ready Indian speech datasets. Fully annotated, certified to the Voqals Quality Standard, and ready to license so you can start training your models immediately.
          </motion.p>
          {/* Email Capture */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false, amount: 0.3 }}
            className="flex flex-col items-center shrink-0 w-full max-w-[clamp(303px,25.93vw,448px)] mx-auto"
          >
            <p className="text-[clamp(8px,0.69vw,12px)] uppercase tracking-[0.15em] font-bold text-white/50 mb-[clamp(11px,0.93vw,16px)]">{notifyDone ? 'You\'re on the list!' : 'Be the first to access them when they go live.'}</p>
            {!notifyDone ? (
            <div className="relative group w-full">
              <input
                type="email"
                value={notifyEmail}
                onChange={e => validateNotifyEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleNotify()}
                placeholder="Enter your work email..."
                className={`w-full bg-white/5 border rounded-full py-[clamp(11px,0.93vw,16px)] pl-[clamp(16px,1.39vw,24px)] pr-[clamp(130px,11.11vw,192px)] text-white placeholder:text-gray-600 focus:outline-none transition-all text-[clamp(9px,0.81vw,14px)] ${notifyEmailError ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-[#56CFFC]/50 focus:bg-white/10'}`}
              />
              <button
                onClick={handleNotify}
                disabled={notifySubmitting || !notifyEmail || !!notifyEmailError}
                className="absolute right-[clamp(4px,0.35vw,6px)] top-1/2 -translate-y-1/2 px-[clamp(16px,1.39vw,24px)] py-[clamp(7px,0.58vw,10px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(7px,0.58vw,10px)] tracking-[0.15em] uppercase hover:scale-105 transition-transform shadow-[0_0_20px_rgba(27,54,255,0.3)] disabled:opacity-40"
              >
                {notifySubmitting ? '...' : 'Notify Me at Launch'}
              </button>
            </div>
            ) : (
            <div className="flex items-center justify-center gap-2 py-[clamp(11px,0.93vw,16px)]">
              <Check className="w-[clamp(14px,1.16vw,20px)] h-[clamp(14px,1.16vw,20px)] text-[#56CFFC]" />
              <span className="text-white/70 text-[clamp(9px,0.81vw,14px)]">We'll notify you at launch.</span>
            </div>
            )}
            {notifyEmailError && <p className="text-[clamp(7px,0.58vw,10px)] text-red-400/80 mt-1 text-center">{notifyEmailError}</p>}
            <p className="text-[clamp(7px,0.58vw,10px)] text-white/30 mt-[clamp(8px,0.69vw,12px)]">No spam. Just a single email when datasets are available to license.</p>
          </motion.div>
        </div>
        {/* Dynamic Deliverables List */}
        <motion.div
          ref={deliverablesRef}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          viewport={{ once: false, amount: 0.3 }}
          className="w-full relative pt-0 pb-[clamp(22px,1.85vw,32px)] mt-[clamp(38px,3.24vw,56px)] shrink-0 flex flex-col items-center z-20"
        >
          <div className="flex items-center gap-[clamp(11px,0.93vw,16px)] mb-[clamp(27px,2.31vw,40px)] w-full max-w-4xl px-[clamp(11px,0.93vw,16px)]">
            <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-white/10"></div>
            <span className="text-[clamp(6px,0.52vw,9px)] uppercase tracking-[0.2em] tracking-[0.3em] font-bold text-center">Included With Every Dataset</span>
            <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-white/10"></div>
          </div>
          
          <div className="flex flex-wrap justify-center items-stretch gap-y-[clamp(22px,1.85vw,32px)] w-full max-w-6xl px-[clamp(11px,0.93vw,16px)]">
            {[
              { title: "CLEAN MIX", desc: "Studio Baseline" },
              { title: "CHAOS MIX", desc: "Simulated Noise" },
              { title: "ISO TRACKS", desc: "For Overlaps" },
              { title: "NEAR FIELD", desc: "Intimate Distance" },
              { title: "FAR FIELD", desc: "Env. Distance" },
              { title: "METADATA", desc: "Dense JSON" }
            ].map((feature, index, array) => (
              <React.Fragment key={index}>
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="flex flex-col items-center px-[clamp(16px,1.39vw,24px)] group w-auto"
                >
                  <span className="text-[clamp(9px,0.81vw,14px)] font-display font-bold text-white uppercase tracking-[0.1em] tracking-[0.2em] mb-[clamp(3px,0.23vw,4px)] group-hover:text-[#56CFFC] transition-colors text-center">
                    {feature.title}
                  </span>
                  <span className="text-[clamp(6px,0.52vw,9px)] uppercase tracking-wider tracking-widest text-center">
                    {feature.desc}
                  </span>
                </motion.div>
                {index < array.length - 1 && index !== 2 && (
                  <div className="block w-[1px] bg-gradient-to-b from-transparent via-white/10 to-transparent" />
                )}
              </React.Fragment>
            ))}
          </div>
        </motion.div>
      </div>
      {/* Dataset Widget */}
      <motion.div
        id="datasets-widget"
        ref={widgetRef}
        layout
        initial={{ opacity: 1, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, layout: { duration: 0.5, ease: "easeOut" } }}
        className="w-full relative flex flex-col items-center justify-center transition-all duration-700 pb-0 mt-[clamp(32px,2.78vw,48px)] z-10 px-[clamp(11px,0.93vw,16px)] shrink-0 scroll-mt-[clamp(90px,7.64vw,132px)]"
      >
        {/* Subtle Animated Glow Behind Widget */}
        <motion.div
          animate={{ opacity: [0.4, 0.7, 0.4], scale: [0.98, 1.02, 0.98] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0 max-w-[var(--max-content)] mx-auto bg-[#1B36FF]/40 blur-[100px] rounded-[3rem] pointer-events-none"
        />
        
        <motion.div 
          layout
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="gradient-border w-full h-full max-w-6xl mx-auto bg-[#050505]/60 backdrop-blur-2xl shadow-[0_0_50px_rgba(0,0,0,0.5)] flex flex-col relative rounded-2xl overflow-hidden"
        >
          {/* Subtle top highlight */}
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent z-20" />
          {/* Subtle background glow */}
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#1B36FF]/10 blur-[100px] rounded-full pointer-events-none" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#56CFFC]/10 blur-[100px] rounded-full pointer-events-none" />
          {/* Header & Filters */}
          <div className="p-[clamp(12px,1.39vw,24px)] border-b border-white/10 flex flex-row justify-between items-center gap-4 bg-white/[0.02] shrink-0 relative z-10">
            <div className="flex items-center gap-[clamp(8px,0.69vw,12px)]">
              <Database className="w-[clamp(14px,1.16vw,20px)] h-[clamp(14px,1.16vw,20px)] text-[#56CFFC]" />
              <h4 className="text-white font-bold tracking-widest uppercase text-[clamp(9px,0.81vw,14px)]">Production Datasets</h4>
            </div>
            
            <div className="flex flex-row items-center gap-[clamp(11px,0.93vw,16px)] w-auto">
              <div className="flex bg-white/5 p-[clamp(4px,0.35vw,6px)] rounded-[clamp(8px,0.69vw,12px)] border border-white/10">
                {['All', 'Personas', 'Training Use Case'].map(filter => (
                  <button
                    key={filter}
                    onClick={() => setActiveFilter(filter)}
                    className={`px-[clamp(12px,1.39vw,24px)] py-[clamp(7px,0.58vw,10px)] rounded-lg text-[clamp(10px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase transition-all duration-300 ${
                      activeFilter === filter 
                        ? 'bg-[#1B36FF]/40 text-white shadow-[0_0_15px_rgba(27,54,255,0.3)]' 
                        : 'text-[#A4BDE5]/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
              
              <button onClick={() => setDatasetEnquiryOpen(true)} className="px-[clamp(16px,1.39vw,24px)] py-[clamp(8px,0.69vw,12px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(7px,0.58vw,10px)] tracking-[0.2em] uppercase transition-all duration-300 hover:scale-105 shadow-[0_0_20px_rgba(27,54,255,0.3)] whitespace-nowrap">
                Enquire About Datasets
              </button>
            </div>
          </div>
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-[clamp(11px,0.93vw,16px)] py-[clamp(11px,0.93vw,16px)] px-[clamp(12px,1.39vw,24px)] border-b border-white/10 bg-white/[0.01] text-[clamp(7px,0.58vw,10px)] uppercase tracking-widest font-bold text-left shrink-0 relative z-10">
            <div className="col-span-4">Dataset</div>
            <div className="col-span-2">Speakers</div>
            <div className="col-span-3">Languages</div>
            <div className="col-span-3 text-right">Status</div>
          </div>
          {/* Table Body */}
          <div className="relative flex-1 overflow-y-auto no-scrollbar">
            <div className="flex flex-col pb-4">
              <AnimatePresence mode="popLayout">
                {filteredDatasets.map((ds, i) => (
                  <motion.div 
                    key={ds.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ delay: i * 0.05, duration: 0.3, ease: "easeOut" }}
                    className="grid grid-cols-12 gap-[clamp(11px,0.93vw,16px)] py-[clamp(14px,1.16vw,20px)] px-[clamp(12px,1.39vw,24px)] border-b border-white/5 hover:bg-gradient-to-r hover:from-white/[0.05] hover:to-transparent transition-all duration-300 items-center text-[clamp(9px,0.81vw,14px)] text-left group cursor-pointer relative"
                  >
                    <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#56CFFC] scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-center" />
                    <div className="col-span-4 font-medium text-white flex flex-col gap-[clamp(3px,0.23vw,4px)]">
                      <div className="flex items-center gap-[clamp(5px,0.46vw,8px)]">
                        <div className="w-[clamp(4px,0.35vw,6px)] h-[clamp(4px,0.35vw,6px)] rounded-full bg-[#56CFFC] opacity-0 group-hover:opacity-100 transition-opacity" />
                        <span className="truncate text-[clamp(11px,0.93vw,16px)]">{ds.name}</span>
                      </div>
                      <span className="text-[clamp(7px,0.58vw,10px)] uppercase tracking-wider text-white/40 ml-[clamp(9px,0.81vw,14px)]">{ds.category}</span>
                    </div>

                    <div className="col-span-2 font-mono text-[#56CFFC] text-[clamp(10px,0.81vw,14px)] font-bold">
                      {ds.speakers}
                    </div>

                    <div className="col-span-3 font-mono text-[#A4BDE5]/80 text-[clamp(10px,0.81vw,14px)]">
                      {ds.languages.length}
                    </div>

                    <div className="col-span-3 flex items-center justify-end gap-[clamp(5px,0.46vw,8px)]">
                      <span className={`w-[clamp(5px,0.35vw,6px)] h-[clamp(5px,0.35vw,6px)] rounded-full ${statusDot(ds.status)}`} />
                      <span className={`text-[clamp(8px,0.69vw,12px)] font-medium tracking-wide ${statusColor(ds.status)}`}>{ds.status}</span>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
    </section>
    <DatasetEnquiryOverlay isOpen={datasetEnquiryOpen} onClose={() => setDatasetEnquiryOpen(false)} />
    </>
  );
};
/* ── Why India, Why Voqals — shared content, mobile scroll-driven widget reveal ── */
const WhyIndiaSection = ({ whyIndiaPlaying, setWhyIndiaPlaying, whyIndiaProgress, whyIndiaDuration }: {
  whyIndiaPlaying: boolean;
  setWhyIndiaPlaying: (v: boolean) => void;
  whyIndiaProgress: number;
  whyIndiaDuration: number;
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const scrollYProgress = useMotionValue(0);
  // Mobile scroll handler — same pattern as USP1
  useEffect(() => {
    const container = document.getElementById('main-scroll-container');
    const section = sectionRef.current;
    if (!container || !section) return;
    const handleScroll = () => {
      // Only track on mobile
      if (window.innerWidth >= 768) { scrollYProgress.set(0); return; }
      let sectionTop = 0;
      let el: HTMLElement | null = section;
      while (el && el !== container) {
        sectionTop += el.offsetTop;
        el = el.offsetParent as HTMLElement | null;
      }
      const sectionHeight = section.offsetHeight;
      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;
      const start = sectionTop;
      const end = sectionTop + sectionHeight - containerHeight;
      const progress = Math.max(0, Math.min(1, (scrollTop - start) / (end - start)));
      scrollYProgress.set(progress);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  // Mobile scroll-driven transforms
  // Widget grows from bottom upward, overlaying the text
  // Paragraphs + trust line blur slightly as widget covers them
  // Pill + heading stay crisp and visible
  // Paragraphs/trust line get slight blur as widget overlays
  const bodyBlurAmount = useTransform(scrollYProgress, [0.1, 0.45], [0, 4]);
  const bodyBlurFilter = useMotionTemplate`blur(${bodyBlurAmount}px)`;
  const bodyOpacity = useTransform(scrollYProgress, [0.1, 0.45], [1, 0.5]);
  // Widget details (below play bar) — blurred initially, revealed on scroll
  const widgetDetailsBlurAmount = useTransform(scrollYProgress, [0.1, 0.5], [6, 0]);
  const widgetDetailsBlurFilter = useMotionTemplate`blur(${widgetDetailsBlurAmount}px)`;
  const widgetDetailsOpacity = useTransform(scrollYProgress, [0.1, 0.5], [0.4, 1]);
  // Widget height grows from peek (play bar only ~150px) to fit content
  const widgetHeight = useTransform(scrollYProgress, (v) => {
    const p = Math.min(1, Math.max(0, v / 0.65));
    const startH = 150; // just the play bar area
    const endH = 495; // fits all widget content including persona
    return startH + (endH - startH) * p;
  });
  // Scroll indicator opacity — visible only in closed/near-closed state
  const scrollIndicatorOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
  // Waveform + play bar — shared between mobile and desktop
  const renderPlayBar = () => (
    <>
      {/* Top highlight line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      {/* File label + timecode */}
      <div className="flex items-center justify-between mb-[clamp(8px,0.6vw,12px)]">
        <span className="text-[clamp(9px,0.6vw,13px)] font-mono text-white/40 uppercase tracking-[0.1em]">Code-Switched Audio · 4 Languages</span>
        <span className="text-[clamp(9px,0.6vw,13px)] font-mono text-white/25">
          {`${Math.floor(whyIndiaProgress * whyIndiaDuration / 60).toString().padStart(2, '0')}:${Math.floor(whyIndiaProgress * whyIndiaDuration % 60).toString().padStart(2, '0')}`} / {`${Math.floor(whyIndiaDuration / 60).toString().padStart(2, '0')}:${Math.floor(whyIndiaDuration % 60).toString().padStart(2, '0')}`}
        </span>
      </div>
      {/* Waveform + Play button row */}
      <div className="flex items-stretch gap-[clamp(12px,1vw,20px)] p-[clamp(12px,1vw,20px)] rounded-[clamp(12px,1vw,20px)] bg-white/[0.03] border border-white/[0.06] mb-[clamp(16px,1.4vw,28px)]">
        <div className="flex-1 flex flex-col gap-[clamp(4px,0.35vw,7px)] justify-center min-w-0">
          <div className="flex items-center gap-[clamp(8px,0.6vw,12px)]">
            <span className="text-[clamp(7px,0.45vw,10px)] font-mono text-white/25 uppercase tracking-[0.1em] w-[clamp(28px,2vw,40px)] shrink-0">User</span>
            <div className="flex-1 h-[clamp(20px,1.8vw,36px)] flex items-center gap-[clamp(1px,0.1vw,2px)] relative">
              {Array.from({ length: 64 }).map((_, i) => {
                const heights = [25, 45, 65, 85, 55, 75, 40, 92, 50, 70, 35, 80, 60, 45, 85, 30, 65, 75, 50, 92, 40, 55, 80, 70, 35, 88, 50, 65, 75, 30, 60, 92, 45, 70, 40, 80, 55, 88, 35, 65, 50, 75, 60, 92, 30, 45, 70, 40, 82, 55, 68, 42, 90, 48, 72, 38, 85, 58, 78, 32, 62, 88, 52, 75];
                // Language segments mapped to normalized positions (0-1)
                const segments = [
                  { start: 0.092, end: 0.249, color: '#FF7F6B' },   // Anna — Tamil
                  { start: 0.249, end: 0.333, color: '#F59E0B' }, // don — Marathi
                  { start: 0.333, end: 0.385, color: '#56CFFC' }, // cup — English
                  { start: 0.385, end: 0.468, color: '#A78BFA' }, // chai — Hindi
                  { start: 0.468, end: 0.580, color: '#F59E0B' }, // dya na — Marathi
                  { start: 0.655, end: 0.695, color: '#A78BFA' }, // Aur — Hindi
                  { start: 0.695, end: 0.793, color: '#56CFFC' }, // sugar — English
                  { start: 0.793, end: 0.891, color: '#A78BFA' }, // kam — Hindi
                ];
                const barPos = i / 64;
                const seg = segments.find(s => barPos >= s.start && barPos < s.end);
                const color = seg ? seg.color : '#888888';
                const isSilence = !seg;
                const isPlayed = barPos <= whyIndiaProgress;
                return (
                  <div
                    key={i}
                    className="rounded-full flex-1"
                    style={{
                      backgroundColor: color,
                      opacity: isSilence ? 0.1 : (isPlayed ? 0.9 : 0.25),
                      height: isSilence ? '15%' : `${heights[i % heights.length]}%`,
                    }}
                  />
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-[clamp(8px,0.6vw,12px)]">
            <span className="w-[clamp(28px,2vw,40px)] shrink-0" />
            <div className="flex-1 h-[1.5px] rounded-full bg-white/[0.06] relative overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-white/60 rounded-full transition-all duration-75"
                style={{ width: `${whyIndiaProgress * 100}%` }}
              />
            </div>
          </div>
        </div>
        <button
          aria-label={whyIndiaPlaying ? "Pause audio" : "Play audio"}
          onClick={() => setWhyIndiaPlaying(!whyIndiaPlaying)}
          className="w-[clamp(40px,3.2vw,64px)] shrink-0 rounded-full bg-gradient-to-br from-[#56CFFC] to-[#1B36FF] flex items-center justify-center self-center aspect-square shadow-[0_0_20px_rgba(27,54,255,0.3)] hover:shadow-[0_0_30px_rgba(27,54,255,0.5)] transition-shadow cursor-pointer"
        >
          {whyIndiaPlaying ? (
            <svg viewBox="0 0 24 24" fill="white" className="w-[clamp(14px,1.1vw,22px)] h-[clamp(14px,1.1vw,22px)]">
              <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="white" className="w-[clamp(14px,1.1vw,22px)] h-[clamp(14px,1.1vw,22px)] ml-[2px]">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>
      </div>
    </>
  );
  // Widget details: legend, pills, callout — shared content
  const renderWidgetDetails = () => (
    <>
      {/* Divider */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent mb-[clamp(16px,1.4vw,28px)]" />
      {/* Language legend */}
      <div className="flex items-center justify-center gap-[clamp(14px,1.2vw,24px)] mb-[clamp(12px,1vw,20px)]">
        {[
          { color: '#FF7F6B', label: 'Tamil' },
          { color: '#F59E0B', label: 'Marathi' },
          { color: '#56CFFC', label: 'English' },
          { color: '#A78BFA', label: 'Hindi' },
        ].map((lang, i) => (
          <div key={i} className="flex items-center gap-[clamp(4px,0.3vw,6px)]">
            <div className="w-[clamp(5px,0.4vw,8px)] h-[clamp(5px,0.4vw,8px)] rounded-full" style={{ backgroundColor: lang.color, opacity: 0.9, boxShadow: `0 0 6px ${lang.color}50` }} />
            <span className="text-[10px] md:text-[clamp(8px,0.5vw,11px)] font-mono uppercase tracking-[0.05em]" style={{ color: lang.color, opacity: 0.7 }}>{lang.label}</span>
          </div>
        ))}
      </div>
      {/* Sentence as flowing pills */}
      <div className="flex flex-wrap gap-[clamp(4px,0.4vw,10px)] mb-[clamp(16px,1.4vw,28px)] px-1">
        {[
          { text: '"Anna,"', lang: 'Tamil', color: '#FF7F6B', start: 0.092, end: 0.249 },
          { text: '"don"', lang: 'Marathi', color: '#F59E0B', start: 0.249, end: 0.333 },
          { text: '"cup"', lang: 'English', color: '#56CFFC', start: 0.333, end: 0.385 },
          { text: '"chai"', lang: 'Hindi', color: '#A78BFA', start: 0.385, end: 0.468 },
          { text: '"dya na."', lang: 'Marathi', color: '#F59E0B', start: 0.468, end: 0.580 },
          { text: '"Aur"', lang: 'Hindi', color: '#A78BFA', start: 0.655, end: 0.695 },
          { text: '"sugar"', lang: 'English', color: '#56CFFC', start: 0.695, end: 0.793 },
          { text: '"kam."', lang: 'Hindi', color: '#A78BFA', start: 0.793, end: 0.891 },
        ].map((seg, i) => {
          const isActive = whyIndiaPlaying && whyIndiaProgress >= seg.start && whyIndiaProgress < seg.end;
          const isPast = whyIndiaProgress >= seg.end;
          return (
            <div
              key={i}
              className="transition-all duration-200"
              style={{ opacity: isActive ? 1 : isPast ? 0.7 : (whyIndiaProgress > 0 ? 0.35 : 0.7) }}
            >
              <div
                className="px-[clamp(10px,0.8vw,20px)] py-[clamp(5px,0.45vw,12px)] rounded-[clamp(8px,0.6vw,12px)] text-[12px] md:text-[clamp(10px,0.65vw,14px)] font-medium font-mono transition-all duration-200 whitespace-nowrap"
                style={{
                  color: seg.color,
                  backgroundColor: isActive ? `${seg.color}20` : `${seg.color}10`,
                  border: `1px solid ${isActive ? seg.color + '50' : seg.color + '25'}`,
                  boxShadow: isActive ? `0 0 20px ${seg.color}25` : 'none',
                  transform: isActive ? 'scale(1.05)' : 'scale(1)',
                }}
              >
                {seg.text}
              </div>
            </div>
          );
        })}
      </div>
      {/* English translation */}
      <p className="text-[14px] md:text-[clamp(13px,0.85vw,15px)] text-white/35 italic mb-[clamp(12px,1vw,20px)] leading-relaxed">
        "Big brother, give me two cups of tea. And less sugar."
      </p>
      {/* Persona + Bottom callout */}
      <div className="pt-[clamp(12px,1vw,20px)] border-t border-white/5 flex flex-col flex-1">
        {/* Desktop: 2-column grid persona */}
        <div className="hidden md:grid grid-cols-2 gap-x-[clamp(16px,1.4vw,28px)] gap-y-[clamp(6px,0.5vw,10px)] mb-[clamp(4px,0.3vw,8px)] text-[clamp(9px,0.6vw,12px)]">
          <div>
            <span className="font-bold text-white/20 uppercase tracking-widest text-[clamp(7px,0.45vw,9px)] block mb-[2px]">Persona</span>
            <span className="text-white/60">GenPop · Mumbai, Urban · 25 yrs</span>
          </div>
          <div>
            <span className="font-bold text-white/20 uppercase tracking-widest text-[clamp(7px,0.45vw,9px)] block mb-[2px]">Native</span>
            <span style={{ color: '#F59E0B', opacity: 0.7 }}>Marathi</span>
          </div>
          <div>
            <span className="font-bold text-white/20 uppercase tracking-widest text-[clamp(7px,0.45vw,9px)] block mb-[2px]">Secondary</span>
            <span style={{ color: '#A78BFA', opacity: 0.7 }}>Hindi</span>
            <span className="text-white/10"> · </span>
            <span style={{ color: '#56CFFC', opacity: 0.7 }}>English</span>
          </div>
          <div>
            <span className="font-bold text-white/20 uppercase tracking-widest text-[clamp(7px,0.45vw,9px)] block mb-[2px]">Context</span>
            <span className="text-white/45">Uses <span style={{ color: '#FF7F6B', opacity: 0.8 }}>Tamil</span> — listener is Tamil</span>
          </div>
        </div>
        {/* Mobile: two-column compact layout */}
        <div className="md:hidden grid grid-cols-2 gap-x-4 gap-y-3 text-[11px] mb-1">
          <div>
            <span className="font-bold text-white/20 uppercase tracking-widest text-[9px] block mb-0.5">Persona</span>
            <span className="text-white/60">GenPop · Mumbai · 25 yrs</span>
          </div>
          <div>
            <span className="font-bold text-white/20 uppercase tracking-widest text-[9px] block mb-0.5">Native</span>
            <span style={{ color: '#F59E0B', opacity: 0.7 }}>Marathi</span>
          </div>
          <div>
            <span className="font-bold text-white/20 uppercase tracking-widest text-[9px] block mb-0.5">Secondary</span>
            <span style={{ color: '#A78BFA', opacity: 0.7 }}>Hindi</span>
            <span className="text-white/10"> · </span>
            <span style={{ color: '#56CFFC', opacity: 0.7 }}>English</span>
          </div>
          <div>
            <span className="font-bold text-white/20 uppercase tracking-widest text-[9px] block mb-0.5">Context</span>
            <span className="text-white/45">Uses <span style={{ color: '#FF7F6B', opacity: 0.8 }}>Tamil</span> — listener is Tamil</span>
          </div>
        </div>
        {/* Bottom callout — always at bottom */}
        <div className="flex items-center justify-end mt-auto">
          <div className="flex items-center gap-[clamp(4px,0.35vw,8px)]">
            <span className="text-[13px] md:text-[clamp(10px,0.7vw,15px)] font-display font-black text-white/90 uppercase tracking-[0.05em]">4 languages.</span>
            <span className="text-[13px] md:text-[clamp(10px,0.7vw,15px)] font-display font-black text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#A78BFA] italic uppercase tracking-[0.05em]">1 breath.</span>
          </div>
        </div>
      </div>
    </>
  );
  const widgetCardStyle = { background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' };
  // Shared text content
  const renderTextContent = () => (
    <>
      <h2 className="font-display text-[clamp(32px,8vw,52px)] md:text-[clamp(32px,2.8vw,68px)] font-black tracking-tighter leading-[1.05] text-[#EEEEEE] uppercase mb-[clamp(16px,1.5vw,28px)]">
        INDIAN SPEECH IS CHAOTIC.<br className="hidden md:block" />
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pb-1 pr-2">AND WE LIVE IN IT.</span>
      </h2>
      <p className="text-white/90 text-[length:var(--text-body)] max-w-sm md:max-w-full leading-relaxed mb-[clamp(12px,1vw,20px)]">
        Hindi into English into regional dialects, mid-sentence, mid-thought. When India speaks four languages in a single breath, bilingual models break. We don't just study this linguistic chaos — we grew up in it.
      </p>
      <p className="text-white/90 text-[length:var(--text-body)] max-w-sm md:max-w-full leading-relaxed mb-[clamp(16px,1.4vw,28px)]">
        Our team lives, breathes, and engineers the true, unfiltered voice of the subcontinent.
      </p>
      {/* Closing line — same style as hero trust line */}
      <div className="flex items-center gap-3 md:gap-4">
        <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#56CFFC] to-[#1B36FF] shrink-0" />
        <p className="text-[10px] md:text-[clamp(10px,0.65vw,16px)] tracking-[0.15em] font-semibold text-white/50 leading-relaxed uppercase">
          The only way to build{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-bold">AI that understands India</span>,{' '}
          is to have{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-bold">India build it.</span>
        </p>
        <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#1B36FF] to-[#56CFFC] shrink-0 md:hidden" />
      </div>
    </>
  );
  return (
    <>
      {/* ── Mobile: scroll-driven widget reveal ── */}
      <div ref={sectionRef} id="why-india-mobile" className="md:hidden h-[200dvh] w-full relative shrink-0">
        <div className="sticky top-0 h-[100dvh] w-full overflow-hidden">
          {/* Text content — vertically centered between nav and widget peek */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-[var(--px-container)] pt-[60px] pb-[170px] pointer-events-auto">
            {/* Pill — always crisp */}
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)] mb-6 z-10">
              Why India, Why Voqals
            </div>
            {/* Heading — always crisp */}
            <h2 className="font-display text-[clamp(22px,7.07vw,52px)] font-black tracking-tighter leading-[1.05] text-[#EEEEEE] uppercase mb-6 z-10">
              INDIAN SPEECH IS CHAOTIC.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pb-1 pr-2">AND WE LIVE IN IT.</span>
            </h2>
            {/* Body text + trust line — blurs slightly as widget overlays */}
            <motion.div
              style={{ opacity: bodyOpacity, filter: bodyBlurFilter }}
              className="flex flex-col items-center"
            >
              <p className="text-white/90 text-[length:var(--text-body)] max-w-sm leading-relaxed mb-4">
                Hindi into English into regional dialects, mid-sentence, mid-thought. When India speaks four languages in a single breath, bilingual models break. We don't just study this linguistic chaos — we grew up in it.
              </p>
              <p className="text-white/90 text-[length:var(--text-body)] max-w-sm leading-relaxed mb-6">
                Our team lives, breathes, and engineers the true, unfiltered voice of the subcontinent.
              </p>
              <div className="flex items-center gap-3">
                <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#56CFFC] to-[#1B36FF] shrink-0" />
                <p className="text-[10px] tracking-[0.15em] font-semibold text-white/50 leading-relaxed uppercase">
                  The only way to build{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-bold">AI that understands India</span>,{' '}
                  is to have{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-bold">India build it.</span>
                </p>
                <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#1B36FF] to-[#56CFFC] shrink-0" />
              </div>
            </motion.div>
          </div>
          {/* Widget — positioned at bottom, grows upward on scroll */}
          <motion.div
            style={{ height: widgetHeight }}
            className="absolute bottom-4 left-4 right-4 overflow-hidden rounded-2xl pointer-events-auto z-20"
          >
            {/* Scroll-up indicator — bouncing chevron at bottom, fades on scroll */}
            <motion.div
              style={{ opacity: scrollIndicatorOpacity }}
              className="absolute bottom-0 left-0 right-0 z-30 flex flex-col items-center pb-2.5 pointer-events-none"
            >
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                className="flex flex-col items-center gap-1"
              >
                <svg width="22" height="10" viewBox="0 0 22 10" fill="none" className="opacity-40">
                  <path d="M2 8L11 2L20 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <svg width="22" height="10" viewBox="0 0 22 10" fill="none" className="opacity-20 -mt-1.5">
                  <path d="M2 8L11 2L20 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </motion.div>
            </motion.div>
            <div className="gradient-border rounded-2xl backdrop-blur-xl shadow-2xl relative h-full" style={widgetCardStyle}>
              <div className="px-5 py-5 flex flex-col overflow-hidden rounded-[inherit] h-full">
                {renderPlayBar()}
                {/* Details — blurred initially, revealed on scroll */}
                <motion.div
                  className="flex flex-col flex-1 overflow-hidden"
                  style={{ filter: widgetDetailsBlurFilter, opacity: widgetDetailsOpacity }}
                >
                  {renderWidgetDetails()}
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      {/* ── Desktop: static layout ── */}
      <section id="why-india" className="hidden md:flex min-h-[100dvh] w-full shrink-0 relative flex-col items-center justify-center pt-20 md:pt-24 pb-8 overflow-hidden">
        {/* Background Glows */}
        <div className="absolute top-1/3 left-1/4 w-[50%] h-[50%] bg-[#1B36FF]/6 blur-[150px] rounded-full pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[40%] h-[40%] bg-[#56CFFC]/4 blur-[120px] rounded-full pointer-events-none" />
        <div className="max-w-[var(--max-content)] w-full mx-auto z-10 flex flex-col px-[var(--px-container)]">
          {/* Pill — above the two-column row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: false, amount: 0.3 }}
            className="flex justify-start mb-[clamp(16px,1.4vw,28px)]"
          >
            <div className="inline-flex items-center gap-[clamp(4px,0.46vw,8px)] px-[clamp(16px,1.39vw,24px)] py-[clamp(7px,0.58vw,10px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
              Why India, Why Voqals
            </div>
          </motion.div>
          {/* Two-column row — vertically centered */}
          <div className="flex flex-row items-center gap-[clamp(32px,3vw,64px)]">
            {/* Left Column — Copy */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: false, amount: 0.3 }}
              className="w-[45%] min-w-0 flex flex-col items-start text-left"
            >
              {renderTextContent()}
            </motion.div>
            {/* Right Column — Code-Switching Visualizer */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
              viewport={{ once: false, amount: 0.3 }}
              className="flex-1 flex flex-col items-end min-w-0"
            >
              <div className="w-full relative">
                <div className="gradient-border rounded-[clamp(20px,1.85vw,32px)] backdrop-blur-xl shadow-2xl relative" style={widgetCardStyle}>
                  <div className="px-[clamp(20px,1.6vw,32px)] py-[clamp(20px,1.8vw,36px)] flex flex-col overflow-hidden rounded-[inherit]">
                    {renderPlayBar()}
                    {renderWidgetDetails()}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};
const languages = [
  "Telugu", "Bengali", "Marathi", "Gujarati", "Kannada", "Malayalam",
  "Punjabi", "Tamil", "Odia", "Urdu", "Bhojpuri", "Maithili",
  "Konkani", "Sindhi", "Dogri", "Santali", "Kashmiri"
];
/* ──── Use Cases Accordion (Hero right column) ──── */
const heroUseCases = [
  {
    id: 'conversational',
    title: 'Conversational Assistants',
    icon: Mic2,
    description: 'Voice agents that understand and respond fluently — across every Indian language and dialect.',
    items: ['Voice Agents', 'IVR Replacements', 'In-Car Assistants', 'Customer Service Bots', 'Personal AI'],
  },
  {
    id: 'video',
    title: 'Generative Video',
    icon: Play,
    description: 'Synthesized speech and lip-sync that match Indian faces, emotion, and intent.',
    items: ['Talking Avatars', 'AI Dubbing', 'Lip-Sync Synthesis', 'Video Localization', 'AI Anchors'],
  },
  {
    id: 'music',
    title: 'Generative Music',
    icon: Music,
    description: 'AI vocalists and music models tuned to the texture and tonality of Indian voices.',
    items: ['AI Vocalists', 'Singing Voice Synthesis', 'Music Generation', 'Vocal Cloning', 'Lyric-to-Song Models'],
  },
];

/* ──── Per-Use-Case Signature Visualizations ──── */

const ConversationalWaveform = ({ active }: { active: boolean }) => (
  <svg viewBox="0 0 200 60" className="w-full h-full overflow-visible" preserveAspectRatio="none">
    <defs>
      <linearGradient id="wave-grad" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#56CFFC" stopOpacity="0.2" />
        <stop offset="50%" stopColor="#56CFFC" stopOpacity="1" />
        <stop offset="100%" stopColor="#1B36FF" stopOpacity="0.2" />
      </linearGradient>
    </defs>
    <motion.path
      initial={false}
      animate={{
        d: active
          ? 'M 0 30 Q 12.5 5, 25 30 Q 37.5 55, 50 30 Q 62.5 5, 75 30 Q 87.5 55, 100 30 Q 112.5 5, 125 30 Q 137.5 55, 150 30 Q 162.5 5, 175 30 Q 187.5 55, 200 30'
          : 'M 0 30 Q 12.5 26, 25 30 Q 37.5 34, 50 30 Q 62.5 26, 75 30 Q 87.5 34, 100 30 Q 112.5 26, 125 30 Q 137.5 34, 150 30 Q 162.5 26, 175 30 Q 187.5 34, 200 30',
      }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      stroke="url(#wave-grad)"
      strokeWidth="1.5"
      fill="none"
    />
  </svg>
);

const VideoFrameStrip = ({ active }: { active: boolean }) => (
  <div className="flex items-center gap-[3px] w-full h-full">
    {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
      <motion.div
        key={i}
        initial={false}
        animate={{
          opacity: active ? [0.3, 1, 0.6][i % 3] : 0.25,
          scaleY: active ? 1 : 0.7,
        }}
        transition={{ duration: 0.4, delay: active ? i * 0.04 : 0, ease: [0.16, 1, 0.3, 1] }}
        className="flex-1 h-full rounded-[2px] border border-[#56CFFC]/30 bg-gradient-to-br from-[#1B36FF]/30 to-[#56CFFC]/10"
      />
    ))}
  </div>
);

const MusicEqualizer = ({ active }: { active: boolean }) => {
  const bars = [0.5, 0.85, 0.35, 0.95, 0.55, 0.7, 0.4, 0.9, 0.5, 0.75, 0.45, 0.65];
  return (
    <div className="flex items-end justify-center gap-[3px] w-full h-full">
      {bars.map((h, i) => (
        <motion.div
          key={i}
          initial={false}
          animate={{
            height: active ? `${h * 100}%` : `${h * 35}%`,
            opacity: active ? 1 : 0.55,
          }}
          transition={{ duration: 0.4, delay: active ? i * 0.025 : 0, ease: [0.16, 1, 0.3, 1] }}
          className="flex-1 bg-gradient-to-t from-[#1B36FF] to-[#56CFFC] rounded-t-[2px]"
          style={{ minHeight: 2 }}
        />
      ))}
    </div>
  );
};

const UseCaseVisual = ({ id, active }: { id: string; active: boolean }) => {
  if (id === 'conversational') return <ConversationalWaveform active={active} />;
  if (id === 'video') return <VideoFrameStrip active={active} />;
  if (id === 'music') return <MusicEqualizer active={active} />;
  return null;
};

/* ──── Use Cases Showcase (Hero right column) ──── */
const UseCasesGenerativeDemo = () => {
  const DEFAULT_ID = heroUseCases[0].id;
  const [activeId, setActiveId] = useState(DEFAULT_ID);

  return (
    <div
      className="absolute inset-0 max-w-[var(--card-max-w)] mx-auto flex flex-col gap-[clamp(8px,0.65vw,12px)]"
      onMouseLeave={() => setActiveId(DEFAULT_ID)}
    >
      {heroUseCases.map((uc, index) => {
        const isActive = activeId === uc.id;
        const Icon = uc.icon;
        return (
          <div
            key={uc.id}
            onMouseEnter={() => setActiveId(uc.id)}
            onClick={() => setActiveId(uc.id)}
            className={`relative cursor-pointer rounded-[clamp(14px,1.2vw,22px)] border overflow-hidden transition-[border-color,background-color,box-shadow] duration-500 ease-out ${
              isActive
                ? 'flex-grow border-[#56CFFC]/45 bg-[#0a0a0a] shadow-[0_24px_60px_rgba(0,0,0,0.55),0_0_40px_rgba(86,207,252,0.18)]'
                : 'border-white/10 bg-black/30'
            }`}
          >
            {/* Background visualization (always rendered, opacity-toggled) */}
            <div
              className="absolute inset-0 pointer-events-none transition-opacity duration-500 ease-out"
              style={{ opacity: isActive ? 0.55 : 0 }}
            >
              <UseCaseVisual id={uc.id} active={isActive} />
            </div>

            {/* Glass overlays */}
            <div
              className="absolute inset-0 pointer-events-none transition-opacity duration-500 ease-out"
              style={{ opacity: isActive ? 1 : 0 }}
            >
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0a0a0a]/95 via-[#0a0a0a]/55 to-[#0a0a0a]/10" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1B36FF]/14 via-transparent to-[#56CFFC]/4" />
            </div>

            {/* Big italic watermark number */}
            <div
              className="absolute bottom-[-6px] right-3 font-display font-black italic text-[clamp(78px,6vw,116px)] leading-none tabular-nums text-transparent bg-clip-text bg-gradient-to-br from-[#56CFFC]/30 to-[#1B36FF]/5 pointer-events-none select-none z-0 transition-[opacity,transform] duration-500 ease-out"
              style={{
                opacity: isActive ? 1 : 0,
                transform: isActive ? 'translateX(0)' : 'translateX(10px)',
                transitionDelay: isActive ? '120ms' : '0ms',
              }}
            >
              0{index + 1}
            </div>

            {/* CONTENT */}
            <div className="relative z-10">
              {/* Top row */}
              <div
                className={`flex items-center gap-[clamp(10px,0.85vw,16px)] transition-[padding] duration-500 ease-out ${
                  isActive
                    ? 'pt-[clamp(18px,1.5vw,26px)] pb-[clamp(4px,0.35vw,8px)] px-[clamp(18px,1.5vw,26px)]'
                    : 'py-[clamp(11px,0.95vw,15px)] px-[clamp(14px,1.15vw,20px)]'
                }`}
              >
                {/* Title */}
                <h3
                  className={`flex-1 min-w-0 font-display font-black tracking-tight uppercase leading-[1.05] transition-all duration-500 ease-out ${
                    isActive
                      ? 'text-[clamp(18px,1.5vw,26px)] text-transparent bg-clip-text bg-gradient-to-r from-white via-[#E0F4FF] to-[#56CFFC] whitespace-normal line-clamp-2'
                      : 'text-[clamp(12px,0.95vw,15px)] text-white/55 whitespace-nowrap overflow-hidden text-ellipsis'
                  }`}
                >
                  {uc.title}
                </h3>

                {/* Mini visualization (collapses to 0 width when active) */}
                <div
                  className="shrink-0 h-[clamp(20px,1.6vw,28px)] overflow-hidden transition-[width,opacity] duration-500 ease-out"
                  style={{
                    width: isActive ? '0px' : 'clamp(54px,4.2vw,76px)',
                    opacity: isActive ? 0 : 0.45,
                  }}
                >
                  <UseCaseVisual id={uc.id} active={false} />
                </div>

                {/* Icon */}
                <div
                  className={`shrink-0 rounded-full border flex items-center justify-center transition-[width,height,border-color,background-color,box-shadow] duration-500 ease-out ${
                    isActive
                      ? 'w-[clamp(40px,2.9vw,48px)] h-[clamp(40px,2.9vw,48px)] border-[#56CFFC]/55 bg-[#56CFFC]/[0.12] shadow-[0_0_28px_rgba(86,207,252,0.4)]'
                      : 'w-[clamp(28px,2.1vw,36px)] h-[clamp(28px,2.1vw,36px)] border-white/[0.12] bg-white/[0.025]'
                  }`}
                >
                  <Icon
                    className={`transition-[width,height,color] duration-500 ease-out ${
                      isActive
                        ? 'w-[clamp(16px,1.2vw,19px)] h-[clamp(16px,1.2vw,19px)] text-[#56CFFC]'
                        : 'w-[clamp(12px,0.9vw,15px)] h-[clamp(12px,0.9vw,15px)] text-white/40'
                    }`}
                  />
                </div>
              </div>

              {/* Expanded: eyebrow + pills (max-height + opacity transition) */}
              <div
                className="overflow-hidden transition-[max-height,opacity] duration-500 ease-out"
                style={{
                  maxHeight: isActive ? '260px' : '0px',
                  opacity: isActive ? 1 : 0,
                  transitionDelay: isActive ? '60ms' : '0ms',
                }}
              >
                <div className="px-[clamp(18px,1.5vw,26px)] pb-[clamp(18px,1.5vw,26px)] pt-[clamp(2px,0.2vw,4px)]">
                  <span className="block text-[clamp(9px,0.7vw,12px)] font-bold tracking-[0.25em] uppercase text-[#56CFFC] mb-[clamp(8px,0.7vw,12px)]">
                    Use Case
                  </span>
                  <p className="text-[clamp(11px,0.85vw,14px)] text-white/75 leading-snug mb-[clamp(12px,1vw,16px)] max-w-[90%]">
                    {uc.description}
                  </p>
                  <div className="flex flex-wrap gap-[clamp(5px,0.4vw,8px)]">
                    {uc.items.map((item) => (
                      <span
                        key={item}
                        className="text-[clamp(9px,0.7vw,12px)] font-semibold text-white/90 tracking-tight px-[clamp(10px,0.85vw,14px)] py-[clamp(4px,0.4vw,7px)] rounded-full border border-[#56CFFC]/40 bg-gradient-to-br from-[#56CFFC]/[0.18] to-[#1B36FF]/[0.08] backdrop-blur-md whitespace-nowrap shadow-[0_4px_14px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.06)]"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ──── How We Work — Section 2 (scroll-driven sticky timeline) ──── */
type IllustrationKey = 'brief' | 'casting' | 'production' | 'post' | 'delivery' | 'qc';

type PhaseFormKey = 'project' | 'casting' | 'liveSession';

const HOW_WE_WORK_PHASES: {
  num: string;
  title: string;
  icon: typeof FileText;
  body: string;
  tags: string[];
  /** Whether to render the tag pill row beneath the body. Defaults to false. */
  tagsVisible?: boolean;
  /** Optional CTA. Omit to render no CTA button on this phase. */
  cta?: string;
  /** Which overlay form to open when the CTA is clicked. */
  formKey?: PhaseFormKey;
  /** Whether to render the enterprise email line beneath the CTA. */
  showEnterpriseEmail?: boolean;
  illustration: IllustrationKey;
}[] = [
  {
    num: '01',
    title: 'Brief & Proposal',
    icon: FileText,
    body: 'Every project starts with a brief and a custom plan. We map your scope, casting strategy, studio plan, post pipeline, deliverable schema, and timeline into a written proposal. Nothing starts until you greenlight it.',
    tags: ['Custom Plan', 'Written Proposal', 'Your Greenlight'],
    cta: 'Request A Quote',
    formKey: 'project',
    showEnterpriseEmail: true,
    illustration: 'brief',
  },
  {
    num: '02',
    title: 'Casting',
    icon: Users,
    body: 'A custom casting plan is built for every project — including multiple audition rounds for complex requirements. We source from thousands of voices: general population across every region, age, and dialect; professional voice artists; musicians and singers for generative music; trained actors for emotionally complex performances. No mic goes live until the persona fit is verified.',
    tags: ['Thousands of Voices', 'Multi-Round Auditions', 'Persona-Verified'],
    cta: 'Request Talent Samples',
    formKey: 'casting',
    illustration: 'casting',
  },
  {
    num: '03',
    title: 'Production',
    icon: Mic2,
    body: 'Recording happens across our network of Voqals-certified studios. Complex performances are run by experienced voice directors with expert technicians on every mic. Music projects are produced with live musicians, singers, producers, and mix engineers in the room. Captured right the first time, every time.',
    tags: ['Voqals-Certified Studios', 'Voice Directors', 'Live Musicians'],
    cta: 'Sit In A Live Session',
    formKey: 'liveSession',
    illustration: 'production',
  },
  {
    num: '04',
    title: 'Post-Production',
    icon: Sliders,
    body: 'Sessions are edited and prepared by our post team. For speech projects, every token is mastered for uniform loudness and clarity. For music projects, mix and mastering engineers handle the full audio chain. Artifact removal, level normalization, and creative finishing — all in-house.',
    tags: ['Audio Clean Up', 'Mix & Master'],
    tagsVisible: true,
    illustration: 'post',
  },
  {
    num: '05',
    title: 'Structured Delivery',
    icon: Database,
    body: 'Files arrive in your required format with dense, structured metadata — speaker ID, language, intent, emotion, persona, action, and any custom tags your end-client schema requires. Ready to load directly into your training pipeline.',
    tags: ['Custom Schema', 'Dense Metadata', 'Pipeline-Ready'],
    illustration: 'delivery',
  },
  {
    num: '06',
    title: 'Quality Control',
    icon: ShieldCheck,
    body: 'Every phase has its own QC built in. Before delivery, every project passes two final gates: proprietary software validates technical compliance — peak levels, format integrity, naming, metadata — then PMs and technical heads do the final human review for creative fit and end-client spec alignment. If anything fails, it gets re-recorded. We don’t patch.',
    tags: ['Software Pass', 'Human Review', 'Don’t Patch'],
    cta: 'Start A Project',
    formKey: 'project',
    illustration: 'qc',
  },
];

/* ──── Per-phase Illustrations (SVG primitives + framer motion) ──── */

const BriefIllustration = ({ active }: { active: boolean }) => (
  <svg viewBox="0 0 400 400" className="w-full h-full overflow-visible">
    <defs>
      <linearGradient id="brief-grad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#56CFFC" stopOpacity="0.35" />
        <stop offset="100%" stopColor="#1B36FF" stopOpacity="0.18" />
      </linearGradient>
    </defs>
    {/* Document */}
    <motion.rect
      x="80" y="50" width="240" height="300" rx="14"
      fill="rgba(10,10,10,0.6)"
      stroke="#56CFFC" strokeOpacity="0.4" strokeWidth="2"
      initial={false}
      animate={{ scale: active ? 1 : 0.96, opacity: active ? 1 : 0.7 }}
      style={{ transformOrigin: '200px 200px' }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    />
    {/* Header bar */}
    <motion.rect
      x="100" y="80" width="120" height="10" rx="5"
      fill="url(#brief-grad)"
      initial={false}
      animate={{ width: active ? 120 : 80 }}
      transition={{ duration: 0.6, delay: active ? 0.1 : 0 }}
    />
    {/* Text lines */}
    {[120, 142, 164, 186, 208].map((y, i) => (
      <motion.rect
        key={y}
        x="100" y={y} width="200" height="6" rx="3"
        fill="rgba(255,255,255,0.18)"
        initial={false}
        animate={{ width: active ? (i % 2 ? 180 : 200) : 60, opacity: active ? 1 : 0.4 }}
        transition={{ duration: 0.5, delay: active ? 0.15 + i * 0.05 : 0 }}
      />
    ))}
    {/* Checklist */}
    {[245, 275, 305].map((y, i) => (
      <motion.g
        key={y}
        initial={false}
        animate={{ opacity: active ? 1 : 0.25, x: active ? 0 : -8 }}
        transition={{ duration: 0.5, delay: active ? 0.4 + i * 0.1 : 0 }}
      >
        <circle cx="115" cy={y} r="8" fill="rgba(86,207,252,0.18)" stroke="#56CFFC" strokeOpacity="0.6" strokeWidth="1.5" />
        <motion.path
          d={`M 110 ${y} L 114 ${y + 4} L 121 ${y - 4}`}
          stroke="#56CFFC" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"
          initial={false}
          animate={{ pathLength: active ? 1 : 0 }}
          transition={{ duration: 0.4, delay: active ? 0.5 + i * 0.1 : 0 }}
        />
        <rect x="135" y={y - 3} width={140 - i * 24} height="6" rx="3" fill="rgba(255,255,255,0.22)" />
      </motion.g>
    ))}
  </svg>
);

const CastingIllustration = ({ active }: { active: boolean }) => {
  const personas = [
    { x: 120, y: 100, r: 28 },
    { x: 220, y: 110, r: 32 },
    { x: 300, y: 95, r: 26 },
    { x: 95, y: 200, r: 30 },
    { x: 200, y: 215, r: 38 }, // featured
    { x: 305, y: 200, r: 28 },
    { x: 130, y: 305, r: 26 },
    { x: 230, y: 310, r: 30 },
    { x: 320, y: 300, r: 24 },
  ];
  return (
    <svg viewBox="0 0 400 400" className="w-full h-full overflow-visible">
      <defs>
        <radialGradient id="casting-glow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#56CFFC" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#56CFFC" stopOpacity="0" />
        </radialGradient>
      </defs>
      {personas.map((p, i) => {
        const featured = i === 4;
        return (
          <motion.g
            key={i}
            initial={false}
            animate={{ opacity: active ? 1 : 0.35, scale: active ? 1 : 0.85 }}
            style={{ transformOrigin: `${p.x}px ${p.y}px` }}
            transition={{ duration: 0.5, delay: active ? i * 0.05 : 0, ease: [0.16, 1, 0.3, 1] }}
          >
            {featured && active && (
              <motion.circle
                cx={p.x} cy={p.y} r={p.r + 16}
                fill="url(#casting-glow)"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.25 }}
              />
            )}
            <circle
              cx={p.x} cy={p.y} r={p.r}
              fill="rgba(10,10,10,0.7)"
              stroke={featured ? '#56CFFC' : 'rgba(86,207,252,0.4)'}
              strokeWidth={featured ? 2.5 : 1.5}
            />
            {/* Mini waveform inside */}
            <g>
              {[-12, -6, 0, 6, 12].map((dx, j) => {
                const heights = [4, 8, 12, 6, 10];
                const h = heights[(i + j) % 5];
                return (
                  <motion.rect
                    key={j}
                    x={p.x + dx - 1}
                    y={p.y - h / 2}
                    width="2"
                    height={h}
                    rx="1"
                    fill={featured ? '#56CFFC' : 'rgba(86,207,252,0.55)'}
                    initial={false}
                    animate={{ scaleY: active ? 1 : 0.4 }}
                    style={{ transformOrigin: `${p.x + dx}px ${p.y}px` }}
                    transition={{ duration: 0.4, delay: active ? 0.3 + i * 0.04 + j * 0.02 : 0 }}
                  />
                );
              })}
            </g>
          </motion.g>
        );
      })}
    </svg>
  );
};

const ProductionIllustration = ({ active }: { active: boolean }) => (
  <svg viewBox="0 0 400 400" className="w-full h-full overflow-visible">
    <defs>
      <linearGradient id="prod-grad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#56CFFC" />
        <stop offset="100%" stopColor="#1B36FF" />
      </linearGradient>
    </defs>
    {/* Mic stand */}
    <motion.line
      x1="200" y1="220" x2="200" y2="340"
      stroke="rgba(255,255,255,0.3)" strokeWidth="3" strokeLinecap="round"
      initial={false}
      animate={{ pathLength: active ? 1 : 0 }}
      transition={{ duration: 0.5, delay: active ? 0.2 : 0 }}
    />
    <motion.rect
      x="160" y="338" width="80" height="6" rx="3"
      fill="rgba(255,255,255,0.3)"
      initial={false}
      animate={{ scaleX: active ? 1 : 0 }}
      style={{ transformOrigin: '200px 341px' }}
      transition={{ duration: 0.5, delay: active ? 0.4 : 0 }}
    />
    {/* Mic body */}
    <motion.g
      initial={false}
      animate={{ scale: active ? 1 : 0.92, y: active ? 0 : 4 }}
      style={{ transformOrigin: '200px 160px' }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    >
      <rect x="170" y="100" width="60" height="120" rx="30" fill="rgba(10,10,10,0.85)" stroke="#56CFFC" strokeOpacity="0.55" strokeWidth="2" />
      {/* Mic grille lines */}
      {[120, 140, 160, 180, 200].map((y) => (
        <line key={y} x1="180" y1={y} x2="220" y2={y} stroke="rgba(86,207,252,0.4)" strokeWidth="1" />
      ))}
    </motion.g>
    {/* Sound waves emanating */}
    {[60, 90, 120].map((r, i) => (
      <motion.circle
        key={r}
        cx="200" cy="160" r={r}
        fill="none"
        stroke="#56CFFC"
        strokeOpacity={0.4 - i * 0.1}
        strokeWidth="1.5"
        initial={false}
        animate={{ scale: active ? 1 : 0.6, opacity: active ? 1 : 0 }}
        style={{ transformOrigin: '200px 160px' }}
        transition={{ duration: 0.7, delay: active ? 0.4 + i * 0.12 : 0 }}
      />
    ))}
    {/* REC indicator */}
    <motion.g
      initial={false}
      animate={{ opacity: active ? 1 : 0, scale: active ? 1 : 0.6 }}
      style={{ transformOrigin: '85px 90px' }}
      transition={{ duration: 0.4, delay: active ? 0.6 : 0 }}
    >
      <circle cx="85" cy="90" r="8" fill="#FF4D4D" />
      <text x="100" y="95" fontSize="14" fontWeight="900" letterSpacing="2" fill="#FF4D4D">REC</text>
    </motion.g>
  </svg>
);

const PostProductionIllustration = ({ active }: { active: boolean }) => {
  const eqBars = [0.4, 0.7, 0.5, 0.85, 0.55, 0.95, 0.65, 0.45, 0.8, 0.5, 0.7, 0.35];
  const wavePoints = [40, 70, 50, 90, 65, 80, 45, 75, 55, 85, 60, 70];
  return (
    <svg viewBox="0 0 400 400" className="w-full h-full overflow-visible">
      <defs>
        <linearGradient id="eq-grad" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#1B36FF" />
          <stop offset="100%" stopColor="#56CFFC" />
        </linearGradient>
      </defs>
      {/* EQ bars */}
      {eqBars.map((h, i) => {
        const x = 80 + i * 22;
        const barH = h * 140;
        return (
          <motion.rect
            key={i}
            x={x} y={200 - barH} width="14" height={barH} rx="4"
            fill="url(#eq-grad)"
            initial={false}
            animate={{ scaleY: active ? 1 : 0.3 }}
            style={{ transformOrigin: `${x + 7}px 200px` }}
            transition={{ duration: 0.5, delay: active ? i * 0.04 : 0, ease: [0.16, 1, 0.3, 1] }}
          />
        );
      })}
      {/* Baseline */}
      <line x1="60" y1="220" x2="340" y2="220" stroke="rgba(86,207,252,0.3)" strokeWidth="1" />
      {/* Waveform region with edits */}
      <motion.g
        initial={false}
        animate={{ opacity: active ? 1 : 0.4 }}
        transition={{ duration: 0.5, delay: active ? 0.5 : 0 }}
      >
        <rect x="60" y="260" width="280" height="80" rx="8" fill="rgba(255,255,255,0.025)" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
        {wavePoints.map((y, i) => {
          const x = 80 + i * 20;
          const barH = y * 0.5;
          return (
            <motion.rect
              key={i}
              x={x} y={300 - barH / 2} width="3" height={barH} rx="1.5"
              fill="rgba(86,207,252,0.7)"
              initial={false}
              animate={{ scaleY: active ? 1 : 0.5 }}
              style={{ transformOrigin: `${x + 1.5}px 300px` }}
              transition={{ duration: 0.4, delay: active ? 0.7 + i * 0.025 : 0 }}
            />
          );
        })}
        {/* Cut markers */}
        {[140, 220, 280].map((x) => (
          <line key={x} x1={x} y1="265" x2={x} y2="335" stroke="#56CFFC" strokeOpacity="0.6" strokeWidth="1" strokeDasharray="3 3" />
        ))}
      </motion.g>
    </svg>
  );
};

const DeliveryIllustration = ({ active }: { active: boolean }) => {
  const tags = [
    { text: 'speaker_id', x: 270, y: 80 },
    { text: 'language', x: 270, y: 130 },
    { text: 'intent', x: 270, y: 180 },
    { text: 'emotion', x: 270, y: 230 },
    { text: 'persona', x: 270, y: 280 },
    { text: 'custom', x: 270, y: 330 },
  ];
  return (
    <svg viewBox="0 0 400 400" className="w-full h-full overflow-visible">
      {/* File icon */}
      <motion.g
        initial={false}
        animate={{ x: active ? 0 : -8, opacity: active ? 1 : 0.5 }}
        transition={{ duration: 0.5 }}
      >
        <rect x="60" y="140" width="120" height="140" rx="12" fill="rgba(10,10,10,0.7)" stroke="#56CFFC" strokeOpacity="0.55" strokeWidth="2" />
        <path d="M 60 160 L 100 160 L 110 145 L 180 145" stroke="#56CFFC" strokeOpacity="0.55" strokeWidth="2" fill="none" />
        {/* Folder content lines */}
        {[180, 198, 216, 234, 252].map((y, i) => (
          <rect key={y} x="76" y={y} width={i % 2 ? 70 : 90} height="4" rx="2" fill="rgba(255,255,255,0.2)" />
        ))}
        <text x="120" y="225" textAnchor="middle" fontSize="11" fontWeight="900" letterSpacing="2" fill="#56CFFC" opacity="0.6">.WAV</text>
      </motion.g>
      {/* Connection lines */}
      {tags.map((tag, i) => (
        <motion.line
          key={i}
          x1="180" y1="210"
          x2={tag.x - 10} y2={tag.y}
          stroke="#56CFFC"
          strokeOpacity="0.4"
          strokeWidth="1.5"
          strokeDasharray="3 3"
          initial={false}
          animate={{ pathLength: active ? 1 : 0 }}
          transition={{ duration: 0.5, delay: active ? 0.3 + i * 0.06 : 0 }}
        />
      ))}
      {/* Tag pills */}
      {tags.map((tag, i) => (
        <motion.g
          key={tag.text}
          initial={false}
          animate={{ opacity: active ? 1 : 0.3, x: active ? 0 : 12 }}
          transition={{ duration: 0.5, delay: active ? 0.4 + i * 0.06 : 0 }}
        >
          <rect x={tag.x} y={tag.y - 13} width="100" height="26" rx="13" fill="rgba(86,207,252,0.12)" stroke="rgba(86,207,252,0.4)" strokeWidth="1" />
          <text x={tag.x + 50} y={tag.y + 4} textAnchor="middle" fontSize="12" fontFamily="monospace" fill="#56CFFC">{tag.text}</text>
        </motion.g>
      ))}
    </svg>
  );
};

const QualityControlIllustration = ({ active }: { active: boolean }) => {
  const bars = [0.45, 0.7, 0.55, 0.85, 0.6, 0.95, 0.5, 0.8, 0.6, 0.75, 0.5];
  return (
    <svg viewBox="0 0 400 400" className="w-full h-full overflow-visible">
      <defs>
        <linearGradient id="qc-shield-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#56CFFC" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#1B36FF" stopOpacity="0.25" />
        </linearGradient>
        <radialGradient id="qc-glow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#56CFFC" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#56CFFC" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Outer dotted ring */}
      <motion.circle
        cx="200" cy="210" r="170"
        fill="none"
        stroke="#56CFFC"
        strokeOpacity="0.18"
        strokeWidth="1.5"
        strokeDasharray="2 6"
        initial={false}
        animate={{ opacity: active ? 1 : 0.25, scale: active ? 1 : 0.94 }}
        style={{ transformOrigin: '200px 210px' }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      />

      {/* Soft inner glow */}
      {active && (
        <motion.circle
          cx="200" cy="210" r="120"
          fill="url(#qc-glow)"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        />
      )}

      {/* Shield body */}
      <motion.path
        d="M 200 80 L 310 124 L 310 224 Q 310 296 200 348 Q 90 296 90 224 L 90 124 Z"
        fill="rgba(10,10,10,0.72)"
        stroke="url(#qc-shield-grad)"
        strokeWidth="2.5"
        initial={false}
        animate={{ scale: active ? 1 : 0.94, opacity: active ? 1 : 0.55 }}
        style={{ transformOrigin: '200px 210px' }}
        transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
      />

      {/* Shield rim accent — top edge highlight */}
      <motion.path
        d="M 200 80 L 310 124 L 310 168"
        fill="none"
        stroke="#56CFFC"
        strokeOpacity="0.6"
        strokeWidth="2.5"
        strokeLinecap="round"
        initial={false}
        animate={{ pathLength: active ? 1 : 0 }}
        transition={{ duration: 0.55, delay: active ? 0.15 : 0 }}
      />

      {/* Waveform bars inside shield (the audio being protected) */}
      {bars.map((h, i) => {
        const x = 130 + i * 14;
        const barH = h * 90;
        return (
          <motion.rect
            key={i}
            x={x}
            y={232 - barH / 2}
            width="5"
            height={barH}
            rx="2.5"
            fill="#56CFFC"
            fillOpacity="0.7"
            initial={false}
            animate={{ scaleY: active ? 1 : 0.4, opacity: active ? 0.85 : 0.35 }}
            style={{ transformOrigin: `${x + 2.5}px 232px` }}
            transition={{ duration: 0.45, delay: active ? 0.3 + i * 0.03 : 0, ease: [0.16, 1, 0.3, 1] }}
          />
        );
      })}

      {/* Center checkmark badge — shield "crest" */}
      <motion.g
        initial={false}
        animate={{ scale: active ? 1 : 0.6, opacity: active ? 1 : 0.3 }}
        style={{ transformOrigin: '200px 150px' }}
        transition={{ duration: 0.55, delay: active ? 0.25 : 0, ease: [0.16, 1, 0.3, 1] }}
      >
        <circle cx="200" cy="150" r="26" fill="rgba(86,207,252,0.2)" stroke="#56CFFC" strokeOpacity="0.85" strokeWidth="2.5" />
        <motion.path
          d="M 186 150 L 196 160 L 215 140"
          stroke="#56CFFC"
          strokeWidth="3.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={false}
          animate={{ pathLength: active ? 1 : 0 }}
          transition={{ duration: 0.55, delay: active ? 0.5 : 0 }}
        />
      </motion.g>

      {/* Left satellite — Pass 01 */}
      <motion.g
        initial={false}
        animate={{ x: active ? 0 : -14, opacity: active ? 1 : 0 }}
        transition={{ duration: 0.55, delay: active ? 0.7 : 0, ease: [0.16, 1, 0.3, 1] }}
      >
        <line x1="90" y1="195" x2="55" y2="195" stroke="#56CFFC" strokeOpacity="0.4" strokeWidth="1.5" strokeDasharray="3 3" />
        <circle cx="42" cy="195" r="16" fill="rgba(10,10,10,0.85)" stroke="#56CFFC" strokeOpacity="0.65" strokeWidth="2" />
        <motion.path
          d="M 35 195 L 40 200 L 50 188"
          stroke="#56CFFC"
          strokeWidth="2.2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={false}
          animate={{ pathLength: active ? 1 : 0 }}
          transition={{ duration: 0.45, delay: active ? 0.85 : 0 }}
        />
        <text x="42" y="225" textAnchor="middle" fontSize="9" fontWeight="900" letterSpacing="2" fill="#56CFFC" opacity="0.7">01</text>
      </motion.g>

      {/* Right satellite — Pass 02 */}
      <motion.g
        initial={false}
        animate={{ x: active ? 0 : 14, opacity: active ? 1 : 0 }}
        transition={{ duration: 0.55, delay: active ? 0.85 : 0, ease: [0.16, 1, 0.3, 1] }}
      >
        <line x1="310" y1="195" x2="345" y2="195" stroke="#56CFFC" strokeOpacity="0.4" strokeWidth="1.5" strokeDasharray="3 3" />
        <circle cx="358" cy="195" r="16" fill="rgba(10,10,10,0.85)" stroke="#56CFFC" strokeOpacity="0.65" strokeWidth="2" />
        <motion.path
          d="M 351 195 L 356 200 L 366 188"
          stroke="#56CFFC"
          strokeWidth="2.2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={false}
          animate={{ pathLength: active ? 1 : 0 }}
          transition={{ duration: 0.45, delay: active ? 1 : 0 }}
        />
        <text x="358" y="225" textAnchor="middle" fontSize="9" fontWeight="900" letterSpacing="2" fill="#56CFFC" opacity="0.7">02</text>
      </motion.g>

      {/* Bottom verified label */}
      <motion.text
        x="200" y="320"
        textAnchor="middle"
        fontSize="10"
        fontWeight="900"
        letterSpacing="4"
        fill="#56CFFC"
        opacity="0.85"
        initial={false}
        animate={{ opacity: active ? 0.85 : 0.25 }}
        transition={{ duration: 0.5, delay: active ? 1.1 : 0 }}
      >
        QC VERIFIED
      </motion.text>
    </svg>
  );
};

const ILLUSTRATIONS: Record<IllustrationKey, React.FC<{ active: boolean }>> = {
  brief: BriefIllustration,
  casting: CastingIllustration,
  production: ProductionIllustration,
  post: PostProductionIllustration,
  delivery: DeliveryIllustration,
  qc: QualityControlIllustration,
};

/* ──── Single phase row — flow-based, alternating sides, useInView for activation ──── */
type PhaseRowProps = {
  phase: typeof HOW_WE_WORK_PHASES[number];
  index: number;
  total: number;
  isReversed: boolean;
  onOpenForm: (key: PhaseFormKey) => void;
};

const PhaseRow: React.FC<PhaseRowProps> = ({ phase, index: _index, total, isReversed, onOpenForm }) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(rowRef, { amount: 0.4, margin: '0px 0px -15% 0px' });

  const Illustration = ILLUSTRATIONS[phase.illustration];
  const Icon = phase.icon;

  return (
    <div
      ref={rowRef}
      className="relative grid grid-cols-[1fr_auto_1fr] gap-[clamp(28px,2.8vw,56px)] items-center min-h-[54vh] py-[clamp(14px,1.4vw,28px)]"
    >
      {/* Illustration column — alternating sides, pushed to the OUTER edge so the
         section's visible bounds reach the navbar pill's bounds. */}
      <div className={`relative flex ${isReversed ? 'order-3 justify-end' : 'order-1 justify-start'}`}>
        <div className="relative aspect-square w-full max-w-[clamp(320px,36vw,560px)]">
          {/* Soft glow behind illustration */}
          <div className="absolute inset-[15%] bg-[#1B36FF]/15 blur-[80px] rounded-full pointer-events-none" />
          <div className="relative w-full h-full">
            <Illustration active={isInView} />
          </div>
        </div>
      </div>

      {/* Center marker — circle on the rail */}
      <div className="flex order-2 shrink-0 relative z-10 items-center justify-center">
        <div
          className={`relative rounded-full bg-[#0a0a0a] border flex items-center justify-center transition-all duration-700 ease-out ${
            isInView
              ? 'w-[clamp(48px,3.5vw,64px)] h-[clamp(48px,3.5vw,64px)] border-[#56CFFC] shadow-[0_0_36px_rgba(86,207,252,0.5)] text-[#56CFFC]'
              : 'w-[clamp(40px,2.8vw,52px)] h-[clamp(40px,2.8vw,52px)] border-[#56CFFC]/30 shadow-[0_0_14px_rgba(86,207,252,0.08)] text-[#56CFFC]/55'
          }`}
        >
          <Icon
            className={`transition-all duration-700 ${
              isInView
                ? 'w-[clamp(18px,1.4vw,24px)] h-[clamp(18px,1.4vw,24px)]'
                : 'w-[clamp(14px,1.05vw,18px)] h-[clamp(14px,1.05vw,18px)]'
            }`}
          />
          <span
            className={`absolute -top-1.5 -right-1.5 text-[clamp(8px,0.6vw,11px)] font-black bg-[#0a0a0a] px-1.5 py-0.5 rounded-full border tabular-nums leading-none transition-colors duration-700 ${
              isInView ? 'text-[#56CFFC] border-[#56CFFC]/55' : 'text-[#56CFFC]/45 border-[#56CFFC]/25'
            }`}
          >
            {phase.num}
          </span>
        </div>
      </div>

      {/* Content column — alternating sides */}
      <div
        className={`flex flex-col ${
          isReversed
            ? 'order-1 items-end text-right'
            : 'order-3 items-start text-left'
        }`}
      >
        <span className="text-[#56CFFC] font-bold uppercase tracking-[0.25em] text-[clamp(10px,0.7vw,12px)] mb-[clamp(10px,0.85vw,16px)]">
          Phase {phase.num} <span className="text-white/30">/ 0{total}</span>
        </span>
        <h3 className="font-display font-black text-[clamp(26px,3vw,48px)] tracking-tight uppercase leading-[1.04] mb-[clamp(14px,1.2vw,22px)] text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF]">
          {phase.title}
        </h3>
        <p className="text-white/80 text-[clamp(13px,1vw,17px)] leading-relaxed mb-[clamp(16px,1.3vw,24px)] max-w-[60ch]">
          {phase.body}
        </p>
        {phase.tagsVisible && (
          <div className={`flex flex-wrap gap-[clamp(5px,0.4vw,8px)] mb-[clamp(20px,1.7vw,32px)] ${isReversed ? 'justify-end' : 'justify-start'}`}>
            {phase.tags.map((tag) => (
              <span
                key={tag}
                className="text-[clamp(9px,0.7vw,12px)] font-semibold text-white/85 tracking-tight px-[clamp(10px,0.85vw,14px)] py-[clamp(4px,0.4vw,7px)] rounded-full border border-[#56CFFC]/40 bg-gradient-to-br from-[#56CFFC]/15 to-[#1B36FF]/8 backdrop-blur-md whitespace-nowrap shadow-[0_4px_14px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.06)]"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        {phase.cta && phase.formKey && (
          <button
            onClick={() => onOpenForm(phase.formKey!)}
            className="group relative px-[clamp(22px,1.9vw,40px)] py-[clamp(13px,1.1vw,22px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(10px,0.7vw,14px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden hover:scale-105 hover:shadow-[0_0_30px_rgba(27,54,255,0.4)] whitespace-nowrap"
          >
            <span className="relative z-10 flex items-center gap-3">
              {phase.cta}
              <ArrowRight className="w-[clamp(14px,1vw,18px)] h-[clamp(14px,1vw,18px)] group-hover:translate-x-1 transition-transform" />
            </span>
          </button>
        )}
        {phase.showEnterpriseEmail && (
          <a
            href="mailto:enterprise@voqals.com"
            className={`mt-[clamp(10px,0.8vw,16px)] text-[clamp(11px,0.78vw,14px)] text-white/55 hover:text-[#56CFFC] transition-colors tracking-wide ${isReversed ? 'self-end' : 'self-start'}`}
          >
            or email{' '}
            <span className="underline decoration-white/20 underline-offset-4 hover:decoration-[#56CFFC]">
              enterprise@voqals.com
            </span>
          </a>
        )}
      </div>
    </div>
  );
};

/* ──── Mobile phase row — left-rail timeline (replaces the desktop alternating layout
   on phones; same data, different geometry). ──── */
const MobilePhaseRow: React.FC<{
  phase: typeof HOW_WE_WORK_PHASES[number];
  total: number;
  onOpenForm: (key: PhaseFormKey) => void;
}> = ({ phase, total, onOpenForm }) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(rowRef, { amount: 0.4, margin: '0px 0px -15% 0px' });
  const Icon = phase.icon;

  return (
    <div ref={rowRef} className="relative pb-12 last:pb-0">
      {/* Marker circle on the rail — beefier on mobile since it carries the
         per-phase visual identity (no separate illustration on mobile).
         center stays at left=20px regardless of active-size change. */}
      <div
        className={`absolute left-[20px] -translate-x-1/2 top-0 z-10 rounded-full bg-[#0a0a0a] border flex items-center justify-center transition-all duration-700 ease-out ${
          isInView
            ? 'w-[52px] h-[52px] border-[#56CFFC] shadow-[0_0_28px_rgba(86,207,252,0.55)] text-[#56CFFC]'
            : 'w-11 h-11 border-[#56CFFC]/30 shadow-[0_0_12px_rgba(86,207,252,0.08)] text-[#56CFFC]/55'
        }`}
      >
        <Icon className={`transition-all duration-700 ${isInView ? 'w-5 h-5' : 'w-4 h-4'}`} />
        <span
          className={`absolute -top-1.5 -right-2 text-[9px] font-black bg-[#0a0a0a] px-1.5 py-0.5 rounded-full border tabular-nums leading-none transition-colors duration-700 ${
            isInView ? 'text-[#56CFFC] border-[#56CFFC]/55' : 'text-[#56CFFC]/45 border-[#56CFFC]/25'
          }`}
        >
          {phase.num}
        </span>
      </div>

      {/* Content right of the rail. pl-[68px] clears the active 52px circle (extends
         to 46px from left) plus a comfortable gap. */}
      <div className="pl-[68px] flex flex-col items-start text-left">
        <span className="text-white font-bold uppercase tracking-[0.25em] text-[10px] mb-3 mt-2">
          Phase {phase.num} <span className="text-white/30">/ 0{total}</span>
        </span>
        <h3 className="font-display font-black text-[22px] tracking-tight uppercase leading-[1.04] mb-3 text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF]">
          {phase.title}
        </h3>
        <p className="text-white/80 text-[13.5px] leading-relaxed mb-5">
          {phase.body}
        </p>

        {/* Tags — only when phase has tagsVisible */}
        {phase.tagsVisible && (
          <div className="flex flex-wrap gap-1.5 mb-6">
            {phase.tags.map((tag) => (
              <span
                key={tag}
                className="text-[10px] font-semibold text-white/85 tracking-tight px-3 py-1.5 rounded-full border border-[#56CFFC]/40 bg-gradient-to-br from-[#56CFFC]/15 to-[#1B36FF]/8 backdrop-blur-md whitespace-nowrap shadow-[0_4px_14px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.06)]"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* CTA — only when phase has one */}
        {phase.cta && phase.formKey && (
          <button
            onClick={() => onOpenForm(phase.formKey!)}
            className="group relative px-6 py-3 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[10px] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-2.5 overflow-hidden hover:scale-105 shadow-[0_8px_20px_rgba(27,54,255,0.25)] whitespace-nowrap"
          >
            <span className="relative z-10 flex items-center gap-2.5">
              {phase.cta}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </button>
        )}

        {/* Enterprise email link — phase 1 only */}
        {phase.showEnterpriseEmail && (
          <a
            href="mailto:enterprise@voqals.com"
            className="mt-3 text-[12px] text-white/55 hover:text-[#56CFFC] transition-colors tracking-wide"
          >
            or email{' '}
            <span className="underline decoration-white/20 underline-offset-4 hover:decoration-[#56CFFC]">
              enterprise@voqals.com
            </span>
          </a>
        )}
      </div>
    </div>
  );
};

const HowWeWorkSection = ({ onOpenForm, scrollContainerRef }: { onOpenForm: (key: PhaseFormKey) => void; scrollContainerRef: React.RefObject<HTMLDivElement | null> }) => {
  const phasesContainerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: phasesContainerRef,
    container: scrollContainerRef,
    offset: ['start center', 'end center'],
  });
  const fillHeight = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  // Parallel mobile-only scroll progress for the left-rail fill.
  const mobilePhasesRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: mobileScroll } = useScroll({
    target: mobilePhasesRef,
    container: scrollContainerRef,
    offset: ['start center', 'end center'],
  });
  const mobileFillHeight = useTransform(mobileScroll, [0, 1], ['0%', '100%']);

  return (
    <section
      id="why-india"
      className="w-full shrink-0 relative scroll-mt-[clamp(90px,7.64vw,132px)] pt-[clamp(80px,6vw,140px)] pb-[clamp(80px,6vw,140px)] overflow-hidden"
    >
      {/* Background glows */}
      <div className="absolute top-1/4 left-1/4 w-[50%] h-[50%] bg-[#1B36FF]/8 blur-[150px] rounded-full pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[45%] h-[45%] bg-[#56CFFC]/6 blur-[120px] rounded-full pointer-events-none" />

      {/* Section Header */}
      <div className="relative z-10 max-w-[var(--max-content)] w-full mx-auto px-[var(--px-container)] flex flex-col items-center text-center mb-[clamp(40px,4vw,80px)]">
        <div className="mb-5 md:mb-[clamp(14px,1.15vw,22px)]">
          <div className="inline-flex items-center gap-2 px-5 py-2 md:gap-[clamp(4px,0.46vw,8px)] md:px-[clamp(16px,1.39vw,24px)] md:py-[clamp(6px,0.52vw,9px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
            How We Work
          </div>
        </div>
        <h2 className="font-display text-[clamp(26px,7.5vw,42px)] md:text-[clamp(40px,4.2vw,76px)] font-black tracking-tighter leading-[1.08] md:leading-[1.02] uppercase">
          ONE PARTNER.<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pb-1 pr-2">EVERYTHING COVERED.</span>
        </h2>
        <p className="hidden md:block text-white/75 text-[length:var(--text-body)] md:text-[clamp(13px,0.95vw,17px)] leading-relaxed text-center max-w-[clamp(540px,52vw,820px)] mx-auto mt-5 md:mt-[clamp(14px,1.2vw,22px)]">
          Six phases. QC at every step. Two final checks before anything ships.
        </p>
      </div>

      {/* MOBILE phases — left-rail timeline */}
      <div ref={mobilePhasesRef} className="md:hidden relative w-full max-w-[var(--max-content)] mx-auto px-[var(--px-container)]">
        {/* Background rail (left-aligned, dim) */}
        <div className="absolute left-[calc(var(--px-container)+20px)] -translate-x-1/2 top-0 bottom-0 w-[2px] bg-white/[0.08] pointer-events-none" />
        {/* Filled rail — height grows with mobile scroll progress */}
        <motion.div
          style={{ height: mobileFillHeight }}
          className="absolute left-[calc(var(--px-container)+20px)] -translate-x-1/2 top-0 w-[2px] bg-gradient-to-b from-[#56CFFC] to-[#1B36FF] pointer-events-none shadow-[0_0_16px_rgba(86,207,252,0.5)]"
        />
        {HOW_WE_WORK_PHASES.map((phase) => (
          <MobilePhaseRow
            key={phase.num}
            phase={phase}
            total={HOW_WE_WORK_PHASES.length}
            onOpenForm={onOpenForm}
          />
        ))}
      </div>

      {/* DESKTOP phases timeline — vertical flow with alternating sides + center progress rail */}
      <div ref={phasesContainerRef} className="hidden md:block relative w-full max-w-[var(--max-content)] mx-auto px-[var(--px-container)]">
        {/* Background rail (full height, dim) */}
        <div className="absolute left-1/2 top-0 bottom-0 w-[2px] -translate-x-1/2 bg-white/[0.08] pointer-events-none" />

        {/* Filled rail — height grows with scroll progress */}
        <motion.div
          style={{ height: fillHeight }}
          className="absolute left-1/2 top-0 w-[2px] -translate-x-1/2 bg-gradient-to-b from-[#56CFFC] via-[#56CFFC] to-[#1B36FF] pointer-events-none shadow-[0_0_16px_rgba(86,207,252,0.5)]"
        />

        {/* Phase rows — alternating layout */}
        {HOW_WE_WORK_PHASES.map((phase, i) => (
          <PhaseRow
            key={phase.num}
            phase={phase}
            index={i}
            total={HOW_WE_WORK_PHASES.length}
            isReversed={i % 2 === 1}
            onOpenForm={onOpenForm}
          />
        ))}
      </div>
    </section>
  );
};

/* ──── What We've Produced — Section 3 (4 product data cards, alternating sides + per-card visuals) ──── */
type ProductVisualKey = 'tts' | 'cloning' | 'asr' | 'music';

const WHAT_WE_PRODUCED_CARDS: {
  num: string;
  title: string;
  tagline: string;
  whatsInside: string[];
  accent: string;
  visual: ProductVisualKey;
}[] = [
  {
    num: '01',
    title: 'Text To Speech',
    tagline: "Voice training data with the production polish AI buyers can't get elsewhere.",
    whatsInside: [
      'Voqals Certified Studio Recordings',
      'Voice UI directors on session.',
      'Persona-cast across regions, ages, dialects.',
      'Densely annotated for prosody, emotion, intent.',
    ],
    accent: '#56CFFC',
    visual: 'tts',
  },
  {
    num: '02',
    title: 'Voice Cloning',
    tagline: 'Every voice captured at full spectrum — every register, every shade of emotion, every texture.',
    whatsInside: [
      'Loud, conversational, soft, and whispered registers.',
      'Extreme emotional states — anger, joy, grief, awe.',
      'Natural performance across narrative, dialogue, reaction.',
      'Hours-deep per-speaker capture for stable cloning models.',
    ],
    accent: '#A07BFF',
    visual: 'cloning',
  },
  {
    num: '03',
    title: 'ASR / STT / NLU',
    tagline: 'Edge cases your model needs to learn from. Not just clean ground truth.',
    whatsInside: [
      'Code-switched audio, speaker overlap, interruptions.',
      'Dialect breadth, disfluency, emotion.',
      'Multi-speaker, real-world acoustic conditions.',
      'Schema-matched ground truth annotation.',
    ],
    accent: '#7B9CFF',
    visual: 'asr',
  },
  {
    num: '04',
    title: 'Generative Music',
    tagline: 'Multi-track stems at every recording phase — from first lyric to final master.',
    whatsInside: [
      'Per-song stems delivered at demo, tracking, mix, and master.',
      'End-to-end ownership — lyrics, composition, performance, mix, master.',
      'Indian classical, folk, regional, and global genres.',
      'Hundreds of per-song metadata points: tempo, key, instruments, mood, lyrical themes.',
    ],
    accent: '#5EE7C7',
    visual: 'music',
  },
];

/* — Visual 01: TTS waveform with three emotional zones — */
const TtsWaveformVisual: React.FC<{ active: boolean; accent: string }> = ({ active, accent }) => {
  const flatBars = [0.25, 0.3, 0.27, 0.32, 0.26, 0.3, 0.28, 0.32, 0.26, 0.3, 0.27, 0.32, 0.28, 0.3, 0.27, 0.31, 0.26, 0.3];
  const pitchedBars = [0.32, 0.42, 0.55, 0.68, 0.78, 0.88, 0.94, 0.96, 0.92, 0.86, 0.78, 0.68, 0.58, 0.48, 0.4, 0.36, 0.42, 0.5];
  const variedBars = [0.7, 0.4, 0.92, 0.5, 0.85, 0.32, 0.95, 0.55, 0.75, 0.42, 0.88, 0.6, 0.92, 0.38, 0.8, 0.5, 0.95, 0.65];
  const bars = [...flatBars, ...pitchedBars, ...variedBars];
  return (
    <svg viewBox="0 0 600 320" className="w-full h-full overflow-visible" preserveAspectRatio="xMidYMid meet">
      <text x="100" y="36" textAnchor="middle" fontSize="10" fontWeight="900" letterSpacing="3" fill={accent} opacity="0.65">FLAT</text>
      <text x="300" y="36" textAnchor="middle" fontSize="10" fontWeight="900" letterSpacing="3" fill={accent} opacity="0.65">PITCHED</text>
      <text x="500" y="36" textAnchor="middle" fontSize="10" fontWeight="900" letterSpacing="3" fill={accent} opacity="0.65">VARIED</text>
      <line x1="200" y1="60" x2="200" y2="250" stroke={accent} strokeOpacity="0.15" strokeWidth="1" strokeDasharray="2 5" />
      <line x1="400" y1="60" x2="400" y2="250" stroke={accent} strokeOpacity="0.15" strokeWidth="1" strokeDasharray="2 5" />
      {bars.map((h, i) => {
        const x = 20 + i * 10.7;
        const barH = h * 170;
        return (
          <motion.rect
            key={i}
            x={x} y={155 - barH / 2} width="6" height={barH} rx="3"
            fill={accent} fillOpacity="0.78"
            initial={false}
            animate={{ scaleY: active ? 1 : 0.35, opacity: active ? 0.85 : 0.4 }}
            style={{ transformOrigin: `${x + 3}px 155px` }}
            transition={{ duration: 0.45, delay: active ? i * 0.012 : 0, ease: [0.16, 1, 0.3, 1] }}
          />
        );
      })}
      <text x="300" y="290" textAnchor="middle" fontSize="11" fontWeight="900" letterSpacing="4" fill={accent} opacity="0.85">EMOTIONAL RANGE</text>
    </svg>
  );
};

/* — Visual 02: Voice Cloning — 4 stacked register/emotion bands of one voice,
   each row progressively more intense to convey "full spectrum capture per speaker." — */
const VoiceCloningVisual: React.FC<{ active: boolean; accent: string }> = ({ active, accent }) => {
  // A single base wave shape, scaled differently per row to suggest one voice
  // moving through whisper → soft → full → emotional extremes.
  const baseWave = [0.45, 0.7, 0.5, 0.65, 0.4, 0.6, 0.5, 0.75, 0.55, 0.68, 0.48, 0.7, 0.55, 0.62, 0.45, 0.72, 0.55, 0.65, 0.5, 0.6, 0.7, 0.55, 0.65, 0.5, 0.6, 0.55, 0.68, 0.5, 0.7, 0.55, 0.62, 0.5, 0.65, 0.55, 0.6];
  const tracks = [
    { label: 'WHISPER',     scale: 0.22, op: 0.55 },
    { label: 'SOFT',        scale: 0.45, op: 0.72 },
    { label: 'FULL VOICE',  scale: 0.75, op: 0.88 },
    { label: 'EMOTIONAL',   scale: 1.0,  op: 1.0, jitter: true },
  ];
  return (
    <svg viewBox="0 0 600 320" className="w-full h-full overflow-visible" preserveAspectRatio="xMidYMid meet">
      {tracks.map((t, i) => {
        const yOffset = 18 + i * 64;
        const center = 30;
        return (
          <g key={t.label} transform={`translate(0, ${yOffset})`}>
            <text x="20" y="10" fontSize="9" fontWeight="900" letterSpacing="2.5" fill={accent} opacity={t.op}>{t.label}</text>
            <line x1="20" y1={center} x2="580" y2={center} stroke={accent} strokeOpacity="0.12" strokeWidth="1" />
            {baseWave.map((h, j) => {
              const x = 110 + j * 13.5;
              const jitter = t.jitter ? Math.sin(j * 1.7) * 0.18 : 0;
              const intensity = Math.max(0.05, Math.min(1, h * t.scale + jitter));
              const barH = intensity * 38;
              return (
                <motion.rect
                  key={j}
                  x={x} y={center - barH / 2} width="4" height={barH} rx="2"
                  fill={accent} fillOpacity={t.op * 0.8}
                  initial={false}
                  animate={{ scaleY: active ? 1 : 0.35 }}
                  style={{ transformOrigin: `${x + 2}px ${center}px` }}
                  transition={{ duration: 0.45, delay: active ? i * 0.08 + j * 0.012 : 0, ease: [0.16, 1, 0.3, 1] }}
                />
              );
            })}
          </g>
        );
      })}
      <text x="300" y="312" textAnchor="middle" fontSize="11" fontWeight="900" letterSpacing="4" fill={accent} opacity="0.85">FULL SPECTRUM · ONE VOICE</text>
    </svg>
  );
};

/* — Visual 03: ASR — layered spectrogram with annotation overlays (static, "the chaos is the point") — */
const AsrSpectrogramVisual: React.FC<{ active: boolean; accent: string }> = ({ active, accent }) => {
  const cols = 28;
  const rows = 10;
  const hash = (a: number, b: number) => {
    const v = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453;
    return v - Math.floor(v);
  };
  return (
    <svg viewBox="0 0 600 320" className="w-full h-full overflow-visible" preserveAspectRatio="xMidYMid meet">
      {/* Spectrogram cell grid (static) */}
      {Array.from({ length: cols }).map((_, col) =>
        Array.from({ length: rows }).map((_, row) => {
          const intensity = hash(col, row);
          const w = 18, h = 20, gap = 2;
          const x = 20 + col * (w + gap);
          const y = 20 + row * (h + gap);
          return (
            <rect
              key={`${col}-${row}`}
              x={x} y={y} width={w} height={h} rx="2"
              fill={accent}
              fillOpacity={0.1 + intensity * 0.6}
            />
          );
        })
      )}
      {/* Annotation pills overlaid */}
      {[
        { label: 'code-switch', x: 60, y: 56, w: 110 },
        { label: 'speaker_2', x: 220, y: 116, w: 92 },
        { label: 'overlap', x: 360, y: 76, w: 78 },
        { label: 'disfluency', x: 100, y: 200, w: 96 },
        { label: 'dialect: hi-en', x: 380, y: 220, w: 110 },
      ].map((tag, i) => (
        <motion.g
          key={tag.label}
          initial={false}
          animate={{ opacity: active ? 1 : 0.3, y: active ? 0 : 6 }}
          transition={{ duration: 0.5, delay: active ? 0.2 + i * 0.1 : 0, ease: [0.16, 1, 0.3, 1] }}
        >
          <rect x={tag.x} y={tag.y} width={tag.w} height="24" rx="12" fill="rgba(10,10,10,0.85)" stroke={accent} strokeOpacity="0.7" strokeWidth="1.5" />
          <text x={tag.x + tag.w / 2} y={tag.y + 16} textAnchor="middle" fontSize="11" fontFamily="monospace" fill={accent}>{tag.label}</text>
        </motion.g>
      ))}
      <text x="300" y="304" textAnchor="middle" fontSize="11" fontWeight="900" letterSpacing="4" fill={accent} opacity="0.85">REAL-WORLD · ANNOTATED</text>
    </svg>
  );
};

/* — Visual 04: Generative Music — 4 stems (vocal / drums / bass / synth)
   visualized as parallel multi-track waveforms. — */
const MusicStemsVisual: React.FC<{ active: boolean; accent: string }> = ({ active, accent }) => {
  const stems = [
    { name: 'VOCAL', color: accent, wave: [0.4, 0.7, 0.85, 0.6, 0.92, 0.5, 0.75, 0.45, 0.88, 0.55, 0.7, 0.4, 0.95, 0.6, 0.78, 0.5, 0.85, 0.58, 0.72, 0.45, 0.9, 0.55, 0.7, 0.4, 0.85, 0.5, 0.75, 0.45] },
    { name: 'DRUMS', color: '#A07BFF', wave: [0.95, 0.3, 0.95, 0.3, 0.95, 0.3, 0.85, 0.4, 0.95, 0.3, 0.9, 0.35, 0.95, 0.3, 0.95, 0.3, 0.9, 0.35, 0.95, 0.3, 0.95, 0.3, 0.85, 0.4, 0.95, 0.3, 0.95, 0.3] },
    { name: 'BASS', color: '#7B9CFF', wave: [0.5, 0.55, 0.6, 0.58, 0.55, 0.5, 0.55, 0.6, 0.65, 0.6, 0.55, 0.5, 0.55, 0.6, 0.6, 0.55, 0.5, 0.55, 0.6, 0.65, 0.6, 0.55, 0.5, 0.55, 0.6, 0.55, 0.5, 0.55] },
    { name: 'SYNTH', color: '#56CFFC', wave: [0.7, 0.65, 0.55, 0.7, 0.6, 0.55, 0.7, 0.75, 0.7, 0.6, 0.55, 0.65, 0.7, 0.6, 0.7, 0.75, 0.7, 0.6, 0.55, 0.7, 0.65, 0.6, 0.55, 0.7, 0.65, 0.55, 0.7, 0.6] },
  ];
  return (
    <svg viewBox="0 0 600 320" className="w-full h-full overflow-visible" preserveAspectRatio="xMidYMid meet">
      {stems.map((stem, i) => {
        const yOffset = 18 + i * 64;
        return (
          <g key={stem.name} transform={`translate(0, ${yOffset})`}>
            <text x="20" y="12" fontSize="10" fontWeight="900" letterSpacing="3" fill={stem.color} opacity="0.85">{stem.name}</text>
            <line x1="20" y1="34" x2="580" y2="34" stroke={stem.color} strokeOpacity="0.15" strokeWidth="1" />
            {stem.wave.map((h, j) => {
              const x = 100 + j * 17;
              const barH = h * 46;
              return (
                <motion.rect
                  key={j}
                  x={x} y={34 - barH / 2} width="5" height={barH} rx="2.5"
                  fill={stem.color} fillOpacity="0.78"
                  initial={false}
                  animate={{ scaleY: active ? 1 : 0.35, opacity: active ? 0.85 : 0.4 }}
                  style={{ transformOrigin: `${x + 2.5}px 34px` }}
                  transition={{ duration: 0.45, delay: active ? i * 0.08 + j * 0.012 : 0, ease: [0.16, 1, 0.3, 1] }}
                />
              );
            })}
          </g>
        );
      })}
      <text x="300" y="310" textAnchor="middle" fontSize="11" fontWeight="900" letterSpacing="4" fill={accent} opacity="0.85">STEM-LEVEL · LABELED</text>
    </svg>
  );
};

const PRODUCT_VISUALS: Record<ProductVisualKey, React.FC<{ active: boolean; accent: string }>> = {
  tts: TtsWaveformVisual,
  cloning: VoiceCloningVisual,
  asr: AsrSpectrogramVisual,
  music: MusicStemsVisual,
};

/* — Single sticky card layer (slides up over previous card as you scroll) — */
type StickyCardLayerProps = {
  card: typeof WHAT_WE_PRODUCED_CARDS[number];
  index: number;
  total: number;
  progress: MotionValue<number>;
  isActive: boolean;
};

const StickyCardLayer: React.FC<StickyCardLayerProps> = ({ card, index, total, progress, isActive }) => {
  const transitions = Math.max(1, total - 1);
  const segmentSize = 1 / transitions;
  // Each segment is split into: pre-dwell → fade transition → post-dwell.
  // transitionFraction controls how much of the segment is the actual crossfade.
  const transitionFraction = 0.45;
  const dwellPad = (segmentSize * (1 - transitionFraction)) / 2;

  // Build a piecewise opacity curve so the card is invisible before/after its
  // active window, fully opaque during dwell, and crossfades at the boundaries.
  const fadeInStart = (index - 1) * segmentSize + dwellPad;
  const fadeInEnd = index * segmentSize - dwellPad;
  const fadeOutStart = index * segmentSize + dwellPad;
  const fadeOutEnd = (index + 1) * segmentSize - dwellPad;

  let inputRange: number[];
  let outputRange: number[];
  if (index === 0) {
    // First card: starts visible, fades out as card 1 fades in.
    inputRange = [0, fadeOutStart, fadeOutEnd, 1];
    outputRange = [1, 1, 0, 0];
  } else if (index === total - 1) {
    // Last card: fades in over its segment, then stays visible.
    inputRange = [0, fadeInStart, fadeInEnd, 1];
    outputRange = [0, 0, 1, 1];
  } else {
    // Middle card: fade in → dwell → fade out.
    inputRange = [0, fadeInStart, fadeInEnd, fadeOutStart, fadeOutEnd, 1];
    outputRange = [0, 0, 1, 1, 0, 0];
  }
  const opacity = useTransform(progress, inputRange, outputRange);
  const Visual = PRODUCT_VISUALS[card.visual];

  return (
    <motion.div
      style={{ opacity, zIndex: 10 + index }}
      className="absolute inset-0 will-change-[opacity]"
    >
      <div className="relative w-full h-full grid grid-cols-1 md:grid-cols-2 gap-[clamp(24px,3vw,72px)] items-center">
        {/* Left content — anchored to OUTER (left) edge so the section reaches navbar bounds */}
        <div className="flex flex-col items-start text-left max-w-[clamp(360px,38vw,640px)] justify-self-start w-full">
          <span
            className="font-bold uppercase tracking-[0.25em] text-[clamp(10px,0.7vw,12px)] mb-[clamp(10px,0.85vw,16px)]"
            style={{ color: card.accent }}
          >
            Data Type · {card.num} <span className="text-white/30">/ 0{total}</span>
          </span>
          <h3 className="font-display font-black text-[clamp(28px,3.2vw,56px)] tracking-tight uppercase leading-[1.03] mb-[clamp(12px,1vw,18px)] text-transparent bg-clip-text bg-gradient-to-r from-white via-[#E0F4FF] to-white">
            {card.title}
          </h3>
          <p className="text-white/85 text-[clamp(14px,1.05vw,18px)] leading-relaxed mb-[clamp(18px,1.4vw,26px)] italic">
            {card.tagline}
          </p>

          {/* What's Inside */}
          <div className="w-full">
            <div className="flex items-center gap-[clamp(8px,0.7vw,12px)] mb-[clamp(8px,0.7vw,12px)]">
              <div className="h-[1px] w-[clamp(12px,1.2vw,20px)]" style={{ background: card.accent + 'aa' }} />
              <span className="text-[clamp(9px,0.65vw,11px)] font-bold tracking-[0.25em] uppercase" style={{ color: card.accent }}>
                What's Inside
              </span>
            </div>
            <ul className="flex flex-col gap-[clamp(6px,0.55vw,10px)]">
              {card.whatsInside.map((item) => (
                <li key={item} className="flex items-start gap-[clamp(8px,0.7vw,12px)] text-white/80 text-[clamp(12px,0.88vw,15px)] leading-snug">
                  <span
                    className="shrink-0 mt-[0.55em] w-[clamp(4px,0.35vw,6px)] h-[clamp(4px,0.35vw,6px)] rounded-full"
                    style={{ background: card.accent, boxShadow: `0 0 8px ${card.accent}99` }}
                  />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right visual — anchored to OUTER (right) edge so the section reaches navbar bounds */}
        <div className="relative flex items-center w-full h-full justify-self-end justify-end">
          <div
            className="relative w-full max-w-[clamp(360px,38vw,640px)] aspect-[4/3] rounded-[clamp(14px,1.2vw,22px)] overflow-hidden p-[clamp(14px,1.2vw,22px)]"
            style={{
              background: `linear-gradient(160deg, ${card.accent}1a, rgba(8,8,16,0.85))`,
              border: `1px solid ${card.accent}40`,
            }}
          >
            <div className="relative w-full h-full">
              <Visual active={isActive} accent={card.accent} />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* — Stack progress dots (lives outside the cards so it doesn't move with them) — */
const StackProgressDots: React.FC<{
  total: number;
  progress: MotionValue<number>;
}> = ({ total, progress }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  useMotionValueEvent(progress, 'change', (latest) => {
    const v = typeof latest === 'number' ? latest : 0;
    const transitions = Math.max(1, total - 1);
    // Card i is "active" once it has fully covered the previous, i.e. progress >= i/transitions.
    // We snap to whichever card is most-covered at this scroll position.
    const i = Math.min(total - 1, Math.max(0, Math.round(v * transitions)));
    setActiveIndex(i);
  });

  return (
    <div className="flex items-center gap-[clamp(6px,0.55vw,10px)]">
      {Array.from({ length: total }).map((_, i) => {
        const isOn = i === activeIndex;
        const accent = WHAT_WE_PRODUCED_CARDS[i]?.accent ?? '#56CFFC';
        return (
          <span
            key={i}
            className="rounded-full transition-all duration-300"
            style={{
              width: isOn ? 'clamp(22px,1.8vw,32px)' : 'clamp(6px,0.55vw,10px)',
              height: 'clamp(6px,0.55vw,10px)',
              background: isOn ? accent : 'rgba(255,255,255,0.18)',
              boxShadow: isOn ? `0 0 14px ${accent}99` : 'none',
            }}
          />
        );
      })}
    </div>
  );
};

/* — Mobile sticky card: same crossfade-on-scroll pattern as desktop StickyCardLayer,
   but laid out for phones (stacked content, smaller visual). Used inside the mobile
   sticky-scroll stage in WhatWeProducedSection. — */
const MobileStickyCard: React.FC<{
  card: typeof WHAT_WE_PRODUCED_CARDS[number];
  index: number;
  total: number;
  progress: MotionValue<number>;
  isActive: boolean;
}> = ({ card, index, total, progress, isActive }) => {
  const transitions = Math.max(1, total - 1);
  const segmentSize = 1 / transitions;
  const transitionFraction = 0.45;
  const dwellPad = (segmentSize * (1 - transitionFraction)) / 2;

  const fadeInStart = (index - 1) * segmentSize + dwellPad;
  const fadeInEnd = index * segmentSize - dwellPad;
  const fadeOutStart = index * segmentSize + dwellPad;
  const fadeOutEnd = (index + 1) * segmentSize - dwellPad;

  let inputRange: number[];
  let outputRange: number[];
  if (index === 0) {
    inputRange = [0, fadeOutStart, fadeOutEnd, 1];
    outputRange = [1, 1, 0, 0];
  } else if (index === total - 1) {
    inputRange = [0, fadeInStart, fadeInEnd, 1];
    outputRange = [0, 0, 1, 1];
  } else {
    inputRange = [0, fadeInStart, fadeInEnd, fadeOutStart, fadeOutEnd, 1];
    outputRange = [0, 0, 1, 1, 0, 0];
  }
  const opacity = useTransform(progress, inputRange, outputRange);
  const Visual = PRODUCT_VISUALS[card.visual];

  return (
    <motion.div
      style={{ opacity, zIndex: 10 + index }}
      className="absolute inset-0 will-change-[opacity] overflow-hidden flex flex-col"
    >
      {/* Eyebrow — fixed h-4 so it sits at Y=0 across every card */}
      <span style={{ color: card.accent }} className="block h-4 text-[10px] font-bold uppercase tracking-[0.25em] leading-4">
        Data Type · {card.num} <span className="text-white/30">/ 0{total}</span>
      </span>
      {/* Title — fixed h-8, single line clipped if too long. */}
      <h3 className="h-8 mt-2 font-display font-black text-[26px] tracking-tight uppercase leading-[1.05] text-transparent bg-clip-text bg-gradient-to-r from-white via-[#E0F4FF] to-white whitespace-nowrap overflow-hidden">
        {card.title}
      </h3>
      {/* Tagline — fixed h-[40px] (2 lines × 20px), line-clamp keeps height consistent. */}
      <p className="h-[40px] mt-2 text-white/85 text-[13px] leading-[1.55] italic line-clamp-2">
        {card.tagline}
      </p>
      {/* Visual panel — height derived from fixed aspect ratio + identical width. */}
      <div
        className="relative w-full aspect-[15/8] rounded-[14px] overflow-hidden p-3 mt-2 shrink-0"
        style={{
          background: `linear-gradient(160deg, ${card.accent}1a, rgba(8,8,16,0.85))`,
          border: `1px solid ${card.accent}40`,
        }}
      >
        <div className="relative w-full h-full">
          <Visual active={isActive} accent={card.accent} />
        </div>
      </div>
      {/* What's Inside header — extra mt-4 to give breathing room after the visual */}
      <div className="flex items-center gap-2 h-4 mt-4">
        <div className="h-[1px] w-2.5 shrink-0" style={{ background: card.accent + 'aa' }} />
        <span className="text-[9px] font-bold tracking-[0.25em] uppercase leading-4" style={{ color: card.accent }}>
          What's Inside
        </span>
      </div>
      {/* What's Inside list — tight gap-0.5 (2px) between bullets, each item h-[30px]
         line-clamp-2 to keep layout identical across cards even on long copy. */}
      <ul className="flex flex-col gap-0.5 mt-2">
        {card.whatsInside.map((item) => (
          <li key={item} className="flex items-start gap-2 h-[30px]">
            <span
              className="shrink-0 mt-1.5 w-1 h-1 rounded-full"
              style={{ background: card.accent, boxShadow: `0 0 8px ${card.accent}99` }}
            />
            <span className="text-white/80 text-[12px] leading-snug line-clamp-2">{item}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

const WhatWeProducedSection: React.FC<{
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}> = ({ scrollContainerRef }) => {
  const desktopRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: desktopRef,
    container: scrollContainerRef,
    offset: ['start start', 'end end'],
  });

  // Parallel mobile scroll progress for the mobile sticky-scroll stage.
  const mobileSectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: mobileProgress } = useScroll({
    target: mobileSectionRef,
    container: scrollContainerRef,
    offset: ['start start', 'end end'],
  });

  // Track active card so the active visual animates and the dots highlight.
  // We snap to whichever card is "in residence" — i.e. transition midpoint passed.
  const [activeIndex, setActiveIndex] = useState(0);
  const [mobileActiveIndex, setMobileActiveIndex] = useState(0);
  const total = WHAT_WE_PRODUCED_CARDS.length;
  useMotionValueEvent(scrollYProgress, 'change', (latest) => {
    const v = typeof latest === 'number' ? latest : 0;
    const transitions = Math.max(1, total - 1);
    const i = Math.min(total - 1, Math.max(0, Math.round(v * transitions)));
    setActiveIndex(i);
  });
  useMotionValueEvent(mobileProgress, 'change', (latest) => {
    const v = typeof latest === 'number' ? latest : 0;
    const transitions = Math.max(1, total - 1);
    const i = Math.min(total - 1, Math.max(0, Math.round(v * transitions)));
    setMobileActiveIndex(i);
  });

  return (
    <section
      id="overview"
      className="w-full shrink-0 relative scroll-mt-[clamp(90px,7.64vw,132px)]"
    >
      {/* MOBILE — sticky-scroll with crossfade (mirrors desktop pattern).
          Uses px-4 (matching the navbar's mobile padding) so the visual panel
          reaches the same horizontal extent as the navbar pill.
          Stack uses justify-start so any content overflow clips at bottom only
          (instead of clipping top + bottom as with justify-center). */}
      <div ref={mobileSectionRef} className="md:hidden relative w-full h-[400vh]">
        <div className="sticky top-0 h-[100dvh] w-full flex flex-col items-center gap-[clamp(14px,3vw,24px)] pt-[clamp(80px,16vw,108px)] pb-3 px-4 overflow-hidden">
          {/* Background glows */}
          <div className="absolute top-1/4 right-0 w-[80%] h-[40%] bg-[#A07BFF]/8 blur-[120px] rounded-full pointer-events-none" />
          <div className="absolute bottom-1/4 left-0 w-[80%] h-[40%] bg-[#56CFFC]/8 blur-[120px] rounded-full pointer-events-none" />

          {/* Section header */}
          <div className="relative z-20 flex flex-col items-center text-center shrink-0">
            <div className="mb-3">
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
                What We've Produced
              </div>
            </div>
            <h2 className="font-display text-[clamp(26px,7.5vw,42px)] font-black tracking-tighter leading-[1.08] uppercase">
              DATA WE'VE BUILT.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pb-1 pr-1">FOR MODELS YOU KNOW.</span>
            </h2>
          </div>

          {/* Card stage — explicit height (not flex-1) sized to fit the fixed
              card-content layout (eyebrow + title + tagline + visual + WI header
              + 4 items at fixed heights ≈ 460px).
              Crossfading absolute cards driven by scroll progress. */}
          <div className="relative z-10 w-full shrink-0 h-[clamp(460px,58vh,520px)]">
            {WHAT_WE_PRODUCED_CARDS.map((card, i) => (
              <MobileStickyCard
                key={card.num}
                card={card}
                index={i}
                total={total}
                progress={mobileProgress}
                isActive={i === mobileActiveIndex}
              />
            ))}
          </div>

          {/* Progress dots */}
          <div className="relative z-30 shrink-0 flex justify-center">
            <StackProgressDots total={total} progress={mobileProgress} />
          </div>
        </div>
      </div>

      {/* DESKTOP — sticky-stack with crossfade. Hidden on mobile. */}
      <div ref={desktopRef} className="hidden md:block relative w-full h-[600vh]">
        {/* Sticky stage — pinned at top of viewport while user scrolls through the section. */}
        <div className="sticky top-0 h-screen w-full overflow-hidden flex flex-col items-center justify-center pt-[clamp(96px,8vw,140px)] pb-[clamp(28px,2.6vw,52px)] gap-[clamp(28px,2.6vw,52px)]">
          {/* Background glows */}
          <div className="absolute top-1/4 right-1/4 w-[45%] h-[45%] bg-[#A07BFF]/8 blur-[140px] rounded-full pointer-events-none" />
          <div className="absolute bottom-1/4 left-1/4 w-[45%] h-[45%] bg-[#56CFFC]/8 blur-[140px] rounded-full pointer-events-none" />

          {/* Section Header */}
          <div className="relative z-20 max-w-[var(--max-content)] w-full mx-auto px-[var(--px-container)] flex flex-col items-center text-center shrink-0">
            <div className="mb-[clamp(10px,0.85vw,16px)]">
              <div className="inline-flex items-center gap-[clamp(4px,0.46vw,8px)] px-[clamp(16px,1.39vw,24px)] py-[clamp(6px,0.52vw,9px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
                What We've Produced
              </div>
            </div>
            <h2 className="font-display text-[clamp(30px,3.2vw,56px)] font-black tracking-tighter leading-[1.04] uppercase">
              DATA WE'VE BUILT.{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pb-1 pr-2">FOR MODELS YOU KNOW.</span>
            </h2>
          </div>

          {/* Card stack — explicit height. Inner wrapper has no padding so absolute cards
              respect navbar bounds (inset-0 references content box, not padding box). */}
          <div className="relative w-full max-w-[var(--max-content)] mx-auto px-[var(--px-container)] z-10 h-[clamp(360px,50vh,560px)]">
            <div className="relative w-full h-full">
              {WHAT_WE_PRODUCED_CARDS.map((card, i) => (
                <StickyCardLayer
                  key={card.num}
                  card={card}
                  index={i}
                  total={total}
                  progress={scrollYProgress}
                  isActive={i === activeIndex}
                />
              ))}
            </div>
          </div>

          {/* Progress dots */}
          <div className="relative z-30 w-full flex justify-center shrink-0">
            <StackProgressDots total={total} progress={scrollYProgress} />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ──── About Us + Final CTA — Section 4 (identity close + conversion anchor) ──── */
const ABOUT_STATS: { num: string; label: string; sub: string }[] = [
  { num: '10+', label: 'Years', sub: 'In studio' },
  { num: '2,000+', label: 'Voices', sub: 'In our cast network' },
  { num: '12', label: 'Indian Languages', sub: 'Recorded natively' },
  { num: '5', label: 'Top AI Teams', sub: 'Built with us' },
];

/* — Subtle animated waveform decoration that sits behind the closer copy — */
const AboutWaveformBg: React.FC = () => {
  // Clamped: the sin/cos sum dips below 0 for a few bars, which produced
  // negative <rect height> values (console errors on every visit).
  const bars = Array.from({ length: 64 }).map((_, i) =>
    Math.max(0.04, 0.32 + Math.sin(i * 0.42) * 0.24 + Math.cos(i * 0.71) * 0.14),
  );
  return (
    <svg viewBox="0 0 1200 200" preserveAspectRatio="none" className="w-full h-[clamp(140px,18vw,220px)]">
      <defs>
        <linearGradient id="aboutWaveGrad" x1="0" x2="1">
          <stop offset="0%" stopColor="#56CFFC" stopOpacity="0" />
          <stop offset="35%" stopColor="#56CFFC" stopOpacity="0.55" />
          <stop offset="65%" stopColor="#1B36FF" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#1B36FF" stopOpacity="0" />
        </linearGradient>
      </defs>
      {bars.map((p, i) => {
        const x = (i / (bars.length - 1)) * 1200;
        const h = p * 110;
        return (
          <motion.rect
            key={i}
            x={x - 2}
            y={100 - h / 2}
            width="3"
            height={h}
            rx="1.5"
            fill="url(#aboutWaveGrad)"
            initial={{ scaleY: 0.4 }}
            animate={{ scaleY: [0.4, 1, 0.4] }}
            style={{ transformOrigin: `${x}px 100px` }}
            transition={{
              duration: 2.4 + (i % 4) * 0.35,
              delay: i * 0.035,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        );
      })}
    </svg>
  );
};

const AboutUsSection: React.FC<{ openProjectDrawer: () => void }> = ({ openProjectDrawer }) => {
  return (
  <section
    id="about-us"
    className="w-full shrink-0 relative scroll-mt-[clamp(90px,7.64vw,132px)] pt-[clamp(80px,6vw,140px)] pb-[clamp(80px,6vw,140px)] overflow-hidden"
  >
    {/* Background glows */}
    <div className="absolute top-1/4 left-1/4 w-[50%] h-[50%] bg-[#1B36FF]/12 blur-[160px] rounded-full pointer-events-none" />
    <div className="absolute bottom-1/4 right-1/4 w-[45%] h-[45%] bg-[#56CFFC]/12 blur-[140px] rounded-full pointer-events-none" />

    {/* Subtle animated waveform — gives the closer ambient on-brand motion */}
    <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.16] z-0">
      <AboutWaveformBg />
    </div>

    <div className="relative z-10 max-w-[clamp(640px,72vw,1120px)] w-full mx-auto px-[var(--px-container)] flex flex-col items-center text-center">
      {/* Eyebrow pill */}
      <div className="mb-5 md:mb-[clamp(14px,1.15vw,22px)]">
        <div className="inline-flex items-center gap-2 px-5 py-2 md:gap-[clamp(4px,0.46vw,8px)] md:px-[clamp(16px,1.39vw,24px)] md:py-[clamp(6px,0.52vw,9px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
          Start a Project
        </div>
      </div>

      {/* Heading — conversion-anchor question */}
      <h2 className="font-display text-[clamp(34px,9.5vw,56px)] md:text-[clamp(36px,3.6vw,68px)] font-black tracking-tighter leading-[1.06] md:leading-[1.05] uppercase mb-[clamp(20px,2vw,40px)] md:whitespace-nowrap">
        READY TO BUILD?<br className="md:hidden" />{' '}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pb-1 pr-2">SO ARE WE.</span>
      </h2>

      {/* Body — single paragraph: punch line + offer detail */}
      <div className="max-w-[clamp(500px,55vw,780px)] mx-auto mb-[clamp(28px,3vw,56px)]">
        <p className="text-white/85 text-[length:var(--text-body)] md:text-[clamp(14px,1.05vw,18px)] leading-relaxed">
          You know what your model needs. We know how to deliver it. Send us your parameters: languages, hours, use case. Within <span className="text-white font-semibold">48 hours</span>, you'll have a technical scope and a firm quote &mdash; built by the engineers who'll run your project. Production starts within a week.
        </p>
      </div>

      {/* Stats grid — by-the-numbers anchor (the visual weight this section was missing) */}
      <div className="grid grid-cols-2 md:grid-cols-4 w-full max-w-[clamp(640px,75vw,1120px)] mb-[clamp(32px,3.2vw,64px)] border-y border-white/[0.10] py-[clamp(20px,2.2vw,40px)]">
        {ABOUT_STATS.map((stat, i) => (
          <div
            key={stat.label}
            className={`relative flex flex-col items-center text-center px-[clamp(6px,1.2vw,28px)] py-[clamp(8px,0.8vw,14px)]
              ${i === 1 || i === 3 ? 'border-l border-white/[0.10]' : ''}
              ${i === 2 ? 'md:border-l md:border-white/[0.10]' : ''}
              ${i >= 2 ? 'border-t border-white/[0.10] md:border-t-0' : ''}
            `}
          >
            <span className="font-display font-black text-[clamp(28px,3.8vw,68px)] tracking-tight leading-[1] mb-1.5 md:mb-[clamp(6px,0.5vw,10px)] text-transparent bg-clip-text bg-gradient-to-br from-[#56CFFC] to-[#1B36FF]">
              {stat.num}
            </span>
            <span className="text-white/85 text-[9px] md:text-[clamp(10px,0.7vw,12px)] font-bold tracking-[0.22em] uppercase">
              {stat.label}
            </span>
            <span className="text-white/45 text-[9px] md:text-[clamp(10px,0.65vw,12px)] mt-0.5 md:mt-1 italic">
              {stat.sub}
            </span>
          </div>
        ))}
      </div>

      {/* CTAs — primary pill + secondary "Why Voqals" link to homepage,
          followed by Quick Connect form for low-friction lead capture. */}
      <div className="flex flex-col items-center gap-[clamp(14px,1.2vw,22px)]">
        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
          <button
            onClick={openProjectDrawer}
            className="group relative w-full sm:w-auto px-[clamp(28px,2.31vw,48px)] py-[clamp(14px,1.16vw,24px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(10px,0.69vw,14px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden hover:scale-105 hover:shadow-[0_0_30px_rgba(27,54,255,0.3)] whitespace-nowrap"
          >
            <span className="relative z-10 flex items-center gap-3">
              Bring Us Your Brief
              <ArrowRight className="w-4 h-4 md:w-[clamp(14px,1vw,18px)] md:h-[clamp(14px,1vw,18px)] group-hover:translate-x-1 transition-transform" />
            </span>
          </button>
          <Link
            to="/#why-india"
            className="group relative w-full sm:w-auto px-[clamp(28px,2.31vw,48px)] py-[clamp(14px,1.16vw,24px)] rounded-full border border-white/20 text-white text-[clamp(10px,0.69vw,14px)] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all hover:bg-white/5 hover:text-white hover:border-white/40 flex items-center justify-center whitespace-nowrap"
          >
            <span className="relative z-10 flex items-center gap-2">Why Voqals <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
          </Link>
        </div>
        {/* Quick connect — leave email + what they need, we reach out within 12 hours */}
        <QuickConnectForm source="Quick Connect — /about" />
      </div>
    </div>
  </section>
  );
};

const HeroSection = React.memo(({ openProjectDrawer }: { openProjectDrawer: (open: boolean) => void }) => (
  <section id="hero" className="min-h-[100dvh] w-full shrink-0 relative flex flex-col pt-20 md:pt-24 z-20 overflow-hidden will-change-transform">
    {/* Subtle Background Glow */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-gradient-to-br from-[#1B36FF]/10 to-[#56CFFC]/5 blur-[120px] rounded-full pointer-events-none animate-pulse-slow" />
    <main className="flex-1 flex flex-col items-center justify-center md:grid md:grid-cols-2 md:items-stretch md:content-center max-w-[var(--max-content)] w-full mx-auto px-[var(--px-container)] py-0 gap-8 md:gap-[var(--gap-hero)] pointer-events-auto relative z-10">
      {/* Left Column: Typography & CTAs */}
      <div className="flex flex-col items-center text-center md:items-start md:text-left justify-center w-full md:w-full max-w-none md:max-w-[clamp(440px,42vw,960px)] shrink-0 relative">
        {/* Pill - positioned above the centered content on desktop */}
        <div className="mb-6 md:absolute md:-top-[clamp(48px,3.5vw,86px)] md:left-0 md:mb-0 animate-hero-fade-in">
          <div className="inline-flex items-center gap-2 px-5 py-2 md:gap-[clamp(4px,0.46vw,8px)] md:px-[clamp(16px,1.39vw,24px)] md:py-[clamp(7px,0.58vw,10px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
            India's Audio Company For AI
          </div>
        </div>
        <h1 className="font-display text-[clamp(26px,7.5vw,42px)] md:text-[clamp(32px,3.1vw,58px)] font-black tracking-tighter leading-[1.08] md:leading-[1.05] mb-6 md:mb-[clamp(12px,1.2vw,28px)] w-full uppercase">
          INDIA'S PREMIER AUDIO<br />
          COMPANY. <span className="font-black text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pb-1 pr-2">BUILT FOR AI.</span>
        </h1>
        <p className="text-white/90 text-[length:var(--text-body)] max-w-sm md:max-w-full mb-7 md:mb-[clamp(12px,1.4vw,28px)] leading-relaxed">
          Casting, directed recordings, and post-production across our network of <span className="text-white font-bold">Voqals certified studios in India</span> — in every Indian language. We're the Indian audio company engineering complex, expertly directed speech for AI teams building conversational and generative models.
        </p>
        {/* Trust Line */}
        <div className="mb-7 md:mb-[clamp(14px,1.6vw,32px)] w-full md:w-auto animate-hero-fade-in [animation-delay:0.3s]">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#56CFFC] to-[#1B36FF] shrink-0" />
            <p className="text-[10px] md:text-[clamp(10px,0.65vw,16px)] tracking-[0.15em] font-semibold text-white/50 leading-relaxed uppercase">
              <Link
                to="/audio-company-india/"
                className="underline decoration-white/20 underline-offset-4 hover:decoration-[#56CFFC] hover:text-white/80 transition-colors"
              >
                From the team behind
              </Link>{' '}
              producing speech data powering AI used by{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-bold">hundreds of millions</span>{' '}
              worldwide.
            </p>
            <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#1B36FF] to-[#56CFFC] shrink-0 md:hidden" />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4 md:gap-5 w-full md:w-auto animate-hero-fade-in [animation-delay:0.4s]">
          <button
            onClick={() => openProjectDrawer(true)}
            className="group relative w-full sm:w-auto px-[clamp(28px,2.31vw,48px)] py-[clamp(14px,1.16vw,24px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(10px,0.69vw,14px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden hover:scale-105 hover:shadow-[0_0_30px_rgba(27,54,255,0.3)] whitespace-nowrap"
          >
            <span className="relative z-10 flex items-center gap-3 text-center whitespace-nowrap">
              Start a Project
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </span>
          </button>
          <button
            onClick={() => scrollToId('why-india')}
            className="group relative w-full sm:w-auto px-[clamp(28px,2.31vw,48px)] py-[clamp(14px,1.16vw,24px)] rounded-full border border-white/20 text-white text-[clamp(10px,0.69vw,14px)] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all hover:bg-white/5 hover:text-white hover:border-white/40 flex items-center justify-center whitespace-nowrap"
          >
            <span className="relative z-10 flex items-center gap-2 text-center whitespace-nowrap">See How We Work <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
          </button>
        </div>
      </div>
      {/* Right Column: Use Cases Showcase */}
      <div className="hidden md:block relative animate-hero-fade-in [animation-delay:0.4s]">
        <UseCasesGenerativeDemo />
      </div>
    </main>
    {/* Language Marquee */}
    <div className="relative z-20 w-full pt-3 pb-6 md:pb-8 py-6 overflow-hidden">
      <div className="flex w-max animate-marquee">
        {[1, 2].map((set) => (
          <div key={set} className="flex items-center">
            {languages.map((lang, index) => (
              <div key={`${set}-${index}`} className="flex items-center group px-8 md:px-[clamp(40px,2.5vw,95px)]">
                <span className="text-[10px] md:text-[clamp(9px,0.65vw,14px)] font-display font-medium text-white/90 uppercase tracking-[0.3em] md:tracking-[0.4em] group-hover:text-white transition-colors duration-500 cursor-default">
                  {lang}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </section>
));
export default function AboutPage() {
  const containerRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ container: containerRef });
  const [activeNav, setActiveNav] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProjectDrawerOpen, setIsProjectDrawerOpen] = useState(false);
  const [isTalentFormOpen, setIsTalentFormOpen] = useState(false);
  const [isStudioFormOpen, setIsStudioFormOpen] = useState(false);
  const [isCastingFormOpen, setIsCastingFormOpen] = useState(false);
  const [isLiveSessionOpen, setIsLiveSessionOpen] = useState(false);

  // Maps a phase form key to its overlay opener. Used by HowWeWorkSection rows.
  const openPhaseForm = (key: PhaseFormKey) => {
    if (key === 'project') setIsProjectDrawerOpen(true);
    else if (key === 'casting') setIsCastingFormOpen(true);
    else if (key === 'liveSession') setIsLiveSessionOpen(true);
  };

  usePageMeta({
    title: "India's Premier Audio Company for AI | Voqals",
    description: "India's premier audio company for AI. Casting, directed recordings, and post-production in Voqals certified studios. Every Indian language. Built for AI.",
    canonicalUrl: 'https://voqals.com/audio-company-india/',
    ogImage: 'https://voqals.com/og-image.png',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Indian Audio Production for AI',
        serviceType: 'Audio Production for AI Training Data',
        provider: {
          '@type': 'Organization',
          name: 'Voqals',
          url: 'https://voqals.com/',
        },
        areaServed: 'IN',
        description:
          'Casting, directed recordings, and post-production in Voqals certified studios for AI teams building conversational and generative models. Every Indian language.',
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Audio data for AI',
          itemListElement: [
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Text To Speech Training Data' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Voice Cloning Training Data' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'ASR / STT / NLU Training Data' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Generative Music Training Data' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Custom Data Collection' } },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://voqals.com/' },
          { '@type': 'ListItem', position: 2, name: "India's Audio Company for AI", item: 'https://voqals.com/audio-company-india/' },
        ],
      },
    ],
  });

  const scrollToSection = (id: string) => {
    setIsMobileMenuOpen(false);
    setTimeout(() => scrollToId(id), 400);
  };

  // Handle hash-based navigation (e.g. /#why-india from the landing page navbar).
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    // Wait for layout, then scroll
    const t = setTimeout(() => {
      scrollToId(hash);
      // Clean hash so refresh doesn't re-scroll awkwardly
      history.replaceState(null, '', window.location.pathname);
    }, 150);
    return () => clearTimeout(t);
  }, []);
  // Why India audio player state
  const [whyIndiaPlaying, setWhyIndiaPlaying] = useState(false);
  const [whyIndiaProgress, setWhyIndiaProgress] = useState(0);
  const [whyIndiaDuration, setWhyIndiaDuration] = useState(0);
  const whyIndiaAudioRef = useRef<HTMLAudioElement | null>(null);
  const whyIndiaRafRef = useRef<number>(0);
  const whyIndiaSectionRef = useRef<HTMLDivElement>(null);
  const [whyIndiaVisible, setWhyIndiaVisible] = useState(false);

  // Lazy-load audio when Why India section enters viewport
  useEffect(() => {
    const el = whyIndiaSectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setWhyIndiaVisible(true); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Create audio element once section is visible
  useEffect(() => {
    if (!whyIndiaVisible) return;
    const audio = new Audio('/Audio/4langs.wav');
    whyIndiaAudioRef.current = audio;
    audio.addEventListener('loadedmetadata', () => setWhyIndiaDuration(audio.duration));
    audio.addEventListener('ended', () => {
      setWhyIndiaPlaying(false);
      setWhyIndiaProgress(0);
    });
    return () => { audio.pause(); audio.src = ''; };
  }, [whyIndiaVisible]);

  // Sync playback with state
  useEffect(() => {
    const audio = whyIndiaAudioRef.current;
    if (!audio) return;
    if (whyIndiaPlaying) {
      audio.play().catch(() => {});
      const tick = () => {
        if (audio.duration) setWhyIndiaProgress(audio.currentTime / audio.duration);
        whyIndiaRafRef.current = requestAnimationFrame(tick);
      };
      whyIndiaRafRef.current = requestAnimationFrame(tick);
    } else {
      audio.pause();
      cancelAnimationFrame(whyIndiaRafRef.current);
    }
    return () => cancelAnimationFrame(whyIndiaRafRef.current);
  }, [whyIndiaPlaying]);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            startTransition(() => {
              if (['overview', 'usp1', 'usp2', 'usp3'].includes(id)) {
                setActiveNav('difference');
              } else if (id === 'why-india' || id === 'why-india-mobile') {
                setActiveNav('why-india');
              } else if (id === 'hero') {
                setActiveNav('');
              } else if (id === 'datasets-dt') {
                setActiveNav('datasets');
              } else {
                setActiveNav(id);
              }
            });
          }
        });
      },
      { threshold: 0.3 }
    );
    const sections = ['hero', 'why-india', 'why-india-mobile', 'overview', 'usp1', 'usp2', 'usp3', 'process', 'datasets', 'datasets-dt'];
    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

// Parallax transforms for the background "O"s
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 100]);
  
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  // Tighter spring for more responsive spotlight
  const springX = useSpring(mouseX, { stiffness: 100, damping: 30 });
  const springY = useSpring(mouseY, { stiffness: 100, damping: 30 });
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    mouseX.set(e.clientX - rect.left);
    mouseY.set(e.clientY - rect.top);
  };
  return (
    <>
    <div
      id="main-scroll-container"
      ref={containerRef}
      onMouseMove={handleMouseMove}
      className="h-[100dvh] w-full overflow-x-hidden overflow-y-scroll overscroll-y-contain  no-scrollbar bg-[#000000] text-[#EEEEEE] selection:bg-[#1B36FF]/40 font-sans relative flex flex-col"
    >
      {/* Brand Geometric Background Accents */}
      <motion.div
        suppressHydrationWarning
        style={{ y: y1 }}
        className="fixed top-[-10%] right-[-5%] w-[600px] h-[600px] rounded-full border-[40px] border-[#1B36FF]/20 pointer-events-none z-0"
      />
      <motion.div
        suppressHydrationWarning
        style={{ y: y2 }}
        className="fixed bottom-[-20%] left-[-10%] w-[800px] h-[800px] rounded-full border-[80px] border-[#A4BDE5]/5 pointer-events-none z-0"
      />

      {/* Floating Orbs for Dynamism */}
      <motion.div
        suppressHydrationWarning
        animate={{ y: [0, -30, 0], x: [0, 20, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="fixed top-[20%] left-[10%] w-64 h-64 bg-[#1B36FF]/10 rounded-full blur-[60px] pointer-events-none z-0"
      />
      <motion.div
        suppressHydrationWarning
        animate={{ y: [0, 40, 0], x: [0, -30, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="fixed bottom-[30%] right-[15%] w-80 h-80 bg-[#56CFFC]/10 rounded-full blur-[80px] pointer-events-none z-0"
      />

      {/* Dynamic Spotlight */}
      <motion.div
        suppressHydrationWarning
        className="fixed w-[600px] h-[600px] rounded-full bg-[#1B36FF]/20 blur-[100px] pointer-events-none z-0"
        style={{
          x: useTransform(springX, x => x - 300),
          y: useTransform(springY, y => y - 300),
        }}
      />
      
      {/* Subtle Grid Overlay */}
      <div className="fixed inset-0 bg-[url('/noise.svg')] opacity-10 mix-blend-overlay pointer-events-none z-0"></div>
      {/* Navigation — shared component (identical on landing page) */}
      <Navbar ref={navRef} activeNav={activeNav} onScrollToId={scrollToId} />
      {/* Hero Section — memoized to prevent LCP repaint from state updates */}
      <HeroSection openProjectDrawer={setIsProjectDrawerOpen} />
      {/* How We Work — Section 2 */}
      <div ref={whyIndiaSectionRef}>
        <HowWeWorkSection onOpenForm={openPhaseForm} scrollContainerRef={containerRef} />
      </div>
      {/* The Voqals Advantage Section */}
      <WhatWeProducedSection scrollContainerRef={containerRef} />
      {/* About Us + Final CTA — Section 4 */}
      <AboutUsSection openProjectDrawer={() => setIsProjectDrawerOpen(true)} />
      {/* Contact & Scoping Section (includes Footer) */}
      <ContactScoping />
    </div>
    {/* Project Brief — unified overlay for desktop & mobile */}
    <ProjectBriefOverlay isOpen={isProjectDrawerOpen} onClose={() => setIsProjectDrawerOpen(false)} />
    <TalentFormOverlay isOpen={isTalentFormOpen} onClose={() => setIsTalentFormOpen(false)} />
    <StudioFormOverlay isOpen={isStudioFormOpen} onClose={() => setIsStudioFormOpen(false)} />
    {/* Phase-specific overlays */}
    <CastingRequirementsOverlay isOpen={isCastingFormOpen} onClose={() => setIsCastingFormOpen(false)} />
    <LiveSessionOverlay isOpen={isLiveSessionOpen} onClose={() => setIsLiveSessionOpen(false)} />
  </>
  );
}
